import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Plus, X, ChevronLeft, ChevronRight, ChevronDown, Image as ImageIcon, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, Type, Palette, TextCursor, Link as LinkIcon, FileCode, Code, Check, Copy, Info, Quote, MousePointer, Minus, Table, HelpCircle, Share, User, Network, Trash2, FolderDown, Music, Video, Grid3X3, Upload, Calendar, Highlighter } from 'lucide-react';
import { Link } from 'react-router-dom';
import EditorSectionSelector from './EditorSectionSelector';
import VintageLinkManagement from './VintageLinkManagement';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import { databaseService, storageService } from '../../lib/appwrite';
import { useToast } from '../../contexts/ToastContext';
import { GOOGLE_FONTS } from '../../utils/googleFonts';
import { highlightCode } from '../../utils/syntaxHighlighter';

const PROJECTS_COLLECTION = 'projects';
const PROJECTS_BUCKET = 'project_images';
const CATEGORIES_COLLECTION = 'categories';
const BLOG_CATEGORIES_COLLECTION = 'blog_categories'; // Add blog categories collection
const SUBCATEGORIES_COLLECTION = 'subcategories';
const FILE_DOWNLOADS_COLLECTION = 'file_downloads';

export default function RichTextEditorPage() {
  const navigate = useNavigate();
  const { blogId: urlBlogId, projectId: urlProjectId } = useParams(); // Get blog ID or project ID from URL
  const [selectedSection, setSelectedSection] = useState(null);
  const { showToast } = useToast();
  const editorRef = useRef(null);
  
  // Check if we're in blog mode or project mode
  const [isBlogMode, setIsBlogMode] = useState(false);
  const [isProjectMode, setIsProjectMode] = useState(false);
  const [blogEditingId, setBlogEditingId] = useState(null);
  const [projectEditingId, setProjectEditingId] = useState(null);
  const [blogStatus, setBlogStatus] = useState({ published: false, isDraft: true }); // Track blog status
  const [projectStatus, setProjectStatus] = useState({ published: false, isDraft: true }); // Track project status
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // Track dropdown selection
  
  // Categories and subcategories
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  
  // Inline category creation states
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#4C1D95'); // Default purple color
  const [showCategoryColorPicker, setShowCategoryColorPicker] = useState(false); // Toggle color picker dropdown
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [showSubcategoryInput, setShowSubcategoryInput] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [creatingSubcategory, setCreatingSubcategory] = useState(false);
  
  // Gallery scroll
  const [galleryScrollPosition, setGalleryScrollPosition] = useState(0);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    subcategoryId: '',
    description: '',
    fullDescription: '',
    thumbnail: null,
    thumbnailUrl: '',
    gallery: [],
    galleryUrls: [],
    likes: 0,
    featured: false,
    projectDetails: [
      { label: 'Software', value: '' },
      { label: 'Timeframe', value: '' },
      { label: 'Data Source', value: '' },
      { label: 'Study Area', value: '' }
    ],
    projectLink: '',
    tags: [],
    customSlug: '',
    useProjectPrefix: true,
    publishDate: new Date().toISOString().split('T')[0] // Add publishDate with today's date as default
  });
  
  const [tagInput, setTagInput] = useState('');

  // Typography & Fonts states
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);
  const [showWeightPicker, setShowWeightPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [colorInput, setColorInput] = useState('#000000');
  const [sizeInput, setSizeInput] = useState('16');
  const [weightInput, setWeightInput] = useState('400');

  // Insert Link states
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkMode, setLinkMode] = useState('insert'); // 'selection' or 'insert'
  const [savedSelection, setSavedSelection] = useState(null);

  // Embed states
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [embedViewMode, setEmbedViewMode] = useState('code'); // 'code' or 'preview'
  const [selectedEmbed, setSelectedEmbed] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0, handle: '' });

  // Codeblock states
  const [showCodeBlockModal, setShowCodeBlockModal] = useState(false);
  const [codeBlockContent, setCodeBlockContent] = useState('');
  const [codeBlockLanguage, setCodeBlockLanguage] = useState('javascript');
  const [codeBlockFileName, setCodeBlockFileName] = useState('');
  const [codeBlockTheme, setCodeBlockTheme] = useState('dracula');
  const [codeBlockWrapped, setCodeBlockWrapped] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingCodeBlock, setEditingCodeBlock] = useState(null);

  // Tooltip states
  const [showTooltipModal, setShowTooltipModal] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipSelection, setTooltipSelection] = useState(null);
  const [editingTooltip, setEditingTooltip] = useState(null);

  // Quoteblock states
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [quoteAuthor, setQuoteAuthor] = useState('');
  const [quoteTheme, setQuoteTheme] = useState('classic');
  const [editingQuote, setEditingQuote] = useState(null);

  // Button states
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [buttonTheme, setButtonTheme] = useState('primary');
  const [buttonAlignment, setButtonAlignment] = useState('center');
  const [editingButton, setEditingButton] = useState(null);

  // Divider states
  const [showDividerModal, setShowDividerModal] = useState(false);
  const [dividerTheme, setDividerTheme] = useState('simple');
  const [editingDivider, setEditingDivider] = useState(null);

  // Table states
  const [showTableModal, setShowTableModal] = useState(false);
  const [showTableEditor, setShowTableEditor] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  
  // Excel-like selection states
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [tableTheme, setTableTheme] = useState('default');
  const [tableData, setTableData] = useState([]);
  const [editingTable, setEditingTable] = useState(null);

  // FAQ states
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [faqQuestion, setFaqQuestion] = useState('');
  const [faqAnswer, setFaqAnswer] = useState('');
  const [faqTheme, setFaqTheme] = useState('minimal');
  const [editingFaq, setEditingFaq] = useState(null);

  // Social Links states
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [selectedSocialIds, setSelectedSocialIds] = useState([]);
  const [availableSocialMedias, setAvailableSocialMedias] = useState([]);
  const [socialHeading, setSocialHeading] = useState('Connect');
  const [editingSocial, setEditingSocial] = useState(null);
  const [loadingSocialMedias, setLoadingSocialMedias] = useState(false);

  // Project Navigation states
  const [showProjectNavModal, setShowProjectNavModal] = useState(false);
  const [editingProjectNav, setEditingProjectNav] = useState(null);

  // Author states
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [selectedAuthorIds, setSelectedAuthorIds] = useState([]);
  const [availableAuthors, setAvailableAuthors] = useState([]);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [loadingAuthors, setLoadingAuthors] = useState(false);

  // File Download states
  const [showFileDownloadModal, setShowFileDownloadModal] = useState(false);
  const [fileDownloadMessage, setFileDownloadMessage] = useState('');
  const [fileDownloadDate, setFileDownloadDate] = useState('');
  const [fileDownloadFiles, setFileDownloadFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [editingFileDownload, setEditingFileDownload] = useState(null);

  // Image states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imageAltText, setImageAltText] = useState('');
  const [imageAlignment, setImageAlignment] = useState('center');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Audio states
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState('');
  const [audioTitle, setAudioTitle] = useState('');
  const [audioAlignment, setAudioAlignment] = useState('center');
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [editingAudio, setEditingAudio] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);

  // Video states
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoAlignment, setVideoAlignment] = useState('center');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Handle File Download Button Click
  const handleFileDownloadButtonClick = () => {
    saveSelection();
    setFileDownloadMessage('');
    setFileDownloadDate('');
    setFileDownloadFiles([]);
    setEditingFileDownload(null);
    setShowFileDownloadModal(true);
  };

  // Handle Insert Image Button Click
  const handleInsertImageButtonClick = () => {
    saveSelection();
    setSelectedImageFile(null);
    setImagePreviewUrl('');
    setImageAltText('');
    setImageAlignment('center');
    setEditingImage(null);
    setShowImageModal(true);
  };

  // Handle Insert Audio Button Click
  const handleInsertAudioButtonClick = () => {
    saveSelection();
    setSelectedAudioFile(null);
    setAudioPreviewUrl('');
    setAudioTitle('');
    setAudioAlignment('center');
    setEditingAudio(null);
    setShowAudioModal(true);
  };

  // Handle Insert Video Button Click
  const handleInsertVideoButtonClick = () => {
    saveSelection();
    setSelectedVideoFile(null);
    setVideoPreviewUrl('');
    setVideoTitle('');
    setVideoAlignment('center');
    setEditingVideo(null);
    setShowVideoModal(true);
  };

  // Handle Video File Selection
  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedVideoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(previewUrl);
      
      // Set default title from filename (without extension)
      const fileName = file.name.replace(/\.[^/.]+$/, "");
      if (!videoTitle) {
        setVideoTitle(fileName);
      }
    }
  };

  // Handle Save Video
  const handleSaveVideo = async () => {
    if (!selectedVideoFile) {
      showToast('Please select a video file.', 'error');
      return;
    }

    try {
      setUploadingVideo(true);
      
      // Upload video to storage
      const videoId = `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const response = await storageService.uploadFile('file_uploads', videoId, selectedVideoFile);
      
      if (response.success) {
        const videoUrl = storageService.getFileView('file_uploads', response.data.$id);
        
        const videoData = {
          url: videoUrl,
          title: videoTitle || selectedVideoFile.name,
          alignment: videoAlignment,
          fileName: selectedVideoFile.name,
          originalWidth: 640, // Default width
          originalHeight: 360, // Default height
          duration: 0 // Will be set when video loads
        };
        
        if (editingVideo) {
          // Update existing video
          updateVideoElement(editingVideo, videoData);
          setEditingVideo(null);
        } else {
          // Create new video
          restoreSelection();
          createVideoElement(videoData);
        }
        
        // Trigger input event to update preview
        const event = new Event('input', { bubbles: true });
        editorRef.current?.dispatchEvent(event);
        
        setSelectedVideoFile(null);
        setVideoPreviewUrl('');
        setVideoTitle('');
        setVideoAlignment('center');
        setShowVideoModal(false);
        showToast('Video inserted successfully!', 'success');
      } else {
        throw new Error('Failed to upload video');
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      showToast('Failed to upload video. Please try again.', 'error');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Update Video Element
  const updateVideoElement = (element, data) => {
    const video = element.querySelector('.video-element');
    if (video) {
      video.src = data.url;
    }
    
    element.setAttribute('data-video-url', data.url);
    element.setAttribute('data-video-title', data.title);
    element.setAttribute('data-video-alignment', data.alignment);
    
    // Update alignment
    const alignmentWrapper = element.closest('.video-alignment-wrapper');
    if (alignmentWrapper) {
      alignmentWrapper.style.textAlign = data.alignment;
    }
    element.setAttribute('data-video-alignment', data.alignment);
    
    // Re-initialize player
    initializeVideoPlayer(element);
  };

  // Handle Edit Video
  const handleEditVideo = (videoElement) => {
    const url = videoElement.getAttribute('data-video-url') || '';
    const title = videoElement.getAttribute('data-video-title') || '';
    const alignment = videoElement.getAttribute('data-video-alignment') || 'center';
    
    setVideoPreviewUrl(url);
    setVideoTitle(title);
    setVideoAlignment(alignment);
    setEditingVideo(videoElement);
    setSelectedVideoFile(null); // Don't require new file for editing
    setShowVideoModal(true);
  };

  // Handle Audio File Selection
  const handleAudioFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedAudioFile(file);
      const previewUrl = URL.createObjectURL(file);
      setAudioPreviewUrl(previewUrl);
      
      // Set default title from filename (without extension)
      const fileName = file.name.replace(/\.[^/.]+$/, "");
      if (!audioTitle) {
        setAudioTitle(fileName);
      }
    }
  };

  // Handle Save Audio
  const handleSaveAudio = async () => {
    if (!selectedAudioFile) {
      showToast('Please select an audio file.', 'error');
      return;
    }

    try {
      setUploadingAudio(true);
      
      // Upload audio to storage
      const audioId = `audio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const response = await storageService.uploadFile('file_uploads', audioId, selectedAudioFile);
      
      if (response.success) {
        const audioUrl = storageService.getFileView('file_uploads', response.data.$id);
        
        const audioData = {
          url: audioUrl,
          title: audioTitle || selectedAudioFile.name,
          alignment: audioAlignment,
          fileName: selectedAudioFile.name,
          duration: 0 // Will be set when audio loads
        };
        
        if (editingAudio) {
          // Update existing audio
          updateAudioElement(editingAudio, audioData);
          setEditingAudio(null);
        } else {
          // Create new audio
          restoreSelection();
          createAudioElement(audioData);
        }
        
        // Trigger input event to update preview
        const event = new Event('input', { bubbles: true });
        editorRef.current?.dispatchEvent(event);
        
        setSelectedAudioFile(null);
        setAudioPreviewUrl('');
        setAudioTitle('');
        setAudioAlignment('center');
        setShowAudioModal(false);
        showToast('Audio inserted successfully!', 'success');
      } else {
        throw new Error('Failed to upload audio');
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      showToast('Failed to upload audio. Please try again.', 'error');
    } finally {
      setUploadingAudio(false);
    }
  };

  // Handle Image File Selection
  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);
    }
  };

  // Handle Save Image
  const handleSaveImage = async () => {
    if (!selectedImageFile) {
      showToast('Please select an image file.', 'error');
      return;
    }

    try {
      setUploadingImage(true);
      
      // Upload image to storage
      const imageId = `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const response = await storageService.uploadFile('file_uploads', imageId, selectedImageFile);
      
      if (response.success) {
        const imageUrl = storageService.getFileView('file_uploads', response.data.$id);
        
        const imageData = {
          url: imageUrl,
          alt: imageAltText || selectedImageFile.name,
          alignment: imageAlignment,
          originalWidth: 400, // Default width
          originalHeight: 300, // Default height
          fileName: selectedImageFile.name
        };
        
        if (editingImage) {
          // Update existing image
          updateImageElement(editingImage, imageData);
          setEditingImage(null);
        } else {
          // Create new image
          restoreSelection();
          createImageElement(imageData);
        }
        
        // Trigger input event to update preview
        const event = new Event('input', { bubbles: true });
        editorRef.current?.dispatchEvent(event);
        
        setSelectedImageFile(null);
        setImagePreviewUrl('');
        setImageAltText('');
        setImageAlignment('center');
        setShowImageModal(false);
        showToast('Image inserted successfully!', 'success');
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Failed to upload image. Please try again.', 'error');
    } finally {
      setUploadingImage(false);
    }
  };
  // Create Video Element
  const createVideoElement = (data) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'editor-video-wrapper';
    wrapper.contentEditable = 'false';
    wrapper.style.cssText = `
      display: inline-block;
      width: ${data.originalWidth}px;
      height: ${data.originalHeight}px;
      margin: 1rem 0;
      border: 1px solid transparent;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
      position: relative;
      vertical-align: top;
      user-select: none;
      -webkit-user-select: none;
      background: #000;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    `;
    wrapper.setAttribute('data-video-url', data.url);
    wrapper.setAttribute('data-video-title', data.title);
    wrapper.setAttribute('data-video-alignment', data.alignment);
    wrapper.setAttribute('data-video-filename', data.fileName);
    wrapper.setAttribute('data-original-width', data.originalWidth);
    wrapper.setAttribute('data-original-height', data.originalHeight);
    wrapper.setAttribute('tabindex', '-1');
    
    // Create the professional video player HTML
    wrapper.innerHTML = `
      <div class="video-player-container" style="
        position: relative;
        width: 100%;
        height: 100%;
        background: #000;
        border-radius: 12px;
        overflow: hidden;
      ">
        <video class="video-element" style="
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        " preload="metadata">
          <source src="${data.url}" type="video/mp4">
          <source src="${data.url}" type="video/webm">
          <source src="${data.url}" type="video/ogg">
          Your browser does not support the video element.
        </video>
        
        <!-- Video Controls Overlay -->
        <div class="video-controls" style="
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          padding: 20px 16px 16px;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        ">
          <!-- Progress Bar -->
          <div class="progress-container" style="
            width: 100%;
            height: 6px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
            margin-bottom: 12px;
            cursor: pointer;
            pointer-events: auto;
          ">
            <div class="progress-bar" style="
              height: 100%;
              background: #4C1D95;
              border-radius: 3px;
              width: 0%;
              position: relative;
              transition: width 0.1s ease;
            ">
              <div class="progress-thumb" style="
                position: absolute;
                right: -6px;
                top: 50%;
                transform: translateY(-50%);
                width: 12px;
                height: 12px;
                background: #4C1D95;
                border-radius: 50%;
                opacity: 0;
                transition: opacity 0.2s ease;
              "></div>
            </div>
          </div>
          
          <!-- Control Buttons -->
          <div class="control-buttons" style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            pointer-events: auto;
          ">
            <div class="left-controls" style="display: flex; align-items: center; gap: 12px;">
              <!-- Play/Pause Button -->
              <button class="play-pause-btn" style="
                background: none;
                border: none;
                color: white;
                cursor: pointer;
                padding: 4px;
                border-radius: 4px;
                transition: background 0.2s ease;
              " title="Play/Pause">
                <svg class="play-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                <svg class="pause-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style="display: none;">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              </button>
              
              <!-- Time Display -->
              <span class="time-display" style="
                color: white;
                font-size: 14px;
                font-family: monospace;
                min-width: 80px;
              ">0:00 / 0:00</span>
            </div>
            
            <div class="right-controls" style="display: flex; align-items: center; gap: 8px;">
              <!-- Speed Control -->
              <div class="speed-control" style="position: relative;">
                <button class="speed-btn" style="
                  background: none;
                  border: none;
                  color: white;
                  cursor: pointer;
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 12px;
                  transition: background 0.2s ease;
                " title="Playback Speed">1x</button>
                <div class="speed-menu" style="
                  position: absolute;
                  bottom: 100%;
                  right: 0;
                  background: rgba(0, 0, 0, 0.9);
                  border-radius: 6px;
                  padding: 4px;
                  margin-bottom: 8px;
                  display: none;
                  flex-direction: column;
                  min-width: 60px;
                ">
                  ${[0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => `
                    <button class="speed-option" data-speed="${speed}" style="
                      background: none;
                      border: none;
                      color: white;
                      cursor: pointer;
                      padding: 6px 12px;
                      font-size: 12px;
                      text-align: center;
                      border-radius: 4px;
                      transition: background 0.2s ease;
                    ">${speed}x</button>
                  `).join('')}
                </div>
              </div>
              
              <!-- Volume Control -->
              <div class="volume-control" style="position: relative; display: flex; align-items: center;">
                <button class="volume-btn" style="
                  background: none;
                  border: none;
                  color: white;
                  cursor: pointer;
                  padding: 4px;
                  border-radius: 4px;
                  transition: background 0.2s ease;
                " title="Volume">
                  <svg class="volume-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                </button>
                <div class="volume-slider" style="
                  width: 60px;
                  height: 4px;
                  background: rgba(255, 255, 255, 0.3);
                  border-radius: 2px;
                  margin-left: 8px;
                  cursor: pointer;
                  position: relative;
                ">
                  <div class="volume-progress" style="
                    height: 100%;
                    background: white;
                    border-radius: 2px;
                    width: 100%;
                    position: relative;
                  ">
                    <div class="volume-thumb" style="
                      position: absolute;
                      right: -6px;
                      top: 50%;
                      transform: translateY(-50%);
                      width: 12px;
                      height: 12px;
                      background: white;
                      border-radius: 50%;
                      opacity: 0;
                      transition: opacity 0.2s ease;
                    "></div>
                  </div>
                </div>
              </div>
              
              <!-- Fullscreen Button -->
              <button class="fullscreen-btn" style="
                background: none;
                border: none;
                color: white;
                cursor: pointer;
                padding: 4px;
                border-radius: 4px;
                transition: background 0.2s ease;
              " title="Fullscreen">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Play Button Overlay (when paused) -->
        <div class="play-overlay" style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0, 0, 0, 0.7);
          border-radius: 50%;
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          pointer-events: auto;
        ">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
        
        <!-- Loading Spinner -->
        <div class="loading-spinner" style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: none;
        "></div>
      </div>
    `;
    
    // Add click handler for selection (but not on video controls)
    wrapper.addEventListener('click', (e) => {
      if (!e.target.closest('.video-controls, .play-overlay')) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedVideo(wrapper);
      }
    });
    
    // Apply alignment to wrapper's parent container
    const alignmentWrapper = document.createElement('div');
    alignmentWrapper.className = 'video-alignment-wrapper';
    alignmentWrapper.style.cssText = `
      text-align: ${data.alignment};
      margin: 0.5rem 0;
      width: 100%;
    `;
    alignmentWrapper.appendChild(wrapper);
    
    // Initialize video player functionality
    initializeVideoPlayer(wrapper);
    
    // Insert with proper cursor positioning
    insertElementWithCursor(alignmentWrapper);
  };

  // Initialize Video Player
  const initializeVideoPlayer = (wrapper) => {
    const video = wrapper.querySelector('.video-element');
    const playPauseBtn = wrapper.querySelector('.play-pause-btn');
    const playIcon = wrapper.querySelector('.play-icon');
    const pauseIcon = wrapper.querySelector('.pause-icon');
    const playOverlay = wrapper.querySelector('.play-overlay');
    const timeDisplay = wrapper.querySelector('.time-display');
    const progressContainer = wrapper.querySelector('.progress-container');
    const progressBar = wrapper.querySelector('.progress-bar');
    const progressThumb = wrapper.querySelector('.progress-thumb');
    const speedBtn = wrapper.querySelector('.speed-btn');
    const speedMenu = wrapper.querySelector('.speed-menu');
    const volumeBtn = wrapper.querySelector('.volume-btn');
    const volumeSlider = wrapper.querySelector('.volume-slider');
    const volumeProgress = wrapper.querySelector('.volume-progress');
    const volumeThumb = wrapper.querySelector('.volume-thumb');
    const fullscreenBtn = wrapper.querySelector('.fullscreen-btn');
    const controls = wrapper.querySelector('.video-controls');
    const loadingSpinner = wrapper.querySelector('.loading-spinner');
    
    let isPlaying = false;
    let isDragging = false;
    let currentSpeed = 1;
    let hideControlsTimeout;
    
    // Format time helper
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Show/hide controls
    const showControls = () => {
      controls.style.opacity = '1';
      controls.style.pointerEvents = 'auto';
      clearTimeout(hideControlsTimeout);
      hideControlsTimeout = setTimeout(() => {
        if (isPlaying) {
          controls.style.opacity = '0';
          controls.style.pointerEvents = 'none';
        }
      }, 3000);
    };
    
    // Video event listeners
    video.addEventListener('loadedmetadata', () => {
      timeDisplay.textContent = `0:00 / ${formatTime(video.duration)}`;
      loadingSpinner.style.display = 'none';
    });
    
    video.addEventListener('timeupdate', () => {
      if (!isDragging) {
        const progress = (video.currentTime / video.duration) * 100;
        progressBar.style.width = `${progress}%`;
        timeDisplay.textContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
      }
    });
    
    video.addEventListener('waiting', () => {
      loadingSpinner.style.display = 'block';
    });
    
    video.addEventListener('canplay', () => {
      loadingSpinner.style.display = 'none';
    });
    
    // Play/Pause functionality
    const togglePlayPause = () => {
      if (isPlaying) {
        video.pause();
        playIcon.style.display = 'block';
        pauseIcon.style.display = 'none';
        playOverlay.style.display = 'flex';
        isPlaying = false;
      } else {
        video.play();
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'block';
        playOverlay.style.display = 'none';
        isPlaying = true;
        showControls();
      }
    };
    
    playPauseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      togglePlayPause();
    });
    
    playOverlay.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      togglePlayPause();
    });
    
    // Progress bar functionality
    progressContainer.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = progressContainer.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const progress = (clickX / rect.width) * 100;
      const newTime = (progress / 100) * video.duration;
      video.currentTime = newTime;
      progressBar.style.width = `${progress}%`;
    });
    
    // Speed control
    speedBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      speedMenu.style.display = speedMenu.style.display === 'flex' ? 'none' : 'flex';
    });
    
    wrapper.querySelectorAll('.speed-option').forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const speed = parseFloat(option.dataset.speed);
        video.playbackRate = speed;
        speedBtn.textContent = `${speed}x`;
        speedMenu.style.display = 'none';
        currentSpeed = speed;
      });
    });
    
    // Volume control
    volumeSlider.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = volumeSlider.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const volume = clickX / rect.width;
      video.volume = Math.max(0, Math.min(1, volume));
      volumeProgress.style.width = `${volume * 100}%`;
    });
    
    volumeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      video.muted = !video.muted;
      volumeProgress.style.width = video.muted ? '0%' : `${video.volume * 100}%`;
    });
    
    // Fullscreen functionality
    fullscreenBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      } else if (video.msRequestFullscreen) {
        video.msRequestFullscreen();
      }
    });
    
    // Mouse events for controls
    wrapper.addEventListener('mouseenter', showControls);
    wrapper.addEventListener('mousemove', showControls);
    wrapper.addEventListener('mouseleave', () => {
      if (isPlaying) {
        controls.style.opacity = '0';
        controls.style.pointerEvents = 'none';
      }
    });
    
    // Progress thumb hover
    progressContainer.addEventListener('mouseenter', () => {
      progressThumb.style.opacity = '1';
    });
    progressContainer.addEventListener('mouseleave', () => {
      progressThumb.style.opacity = '0';
    });
    
    // Volume thumb hover
    volumeSlider.addEventListener('mouseenter', () => {
      volumeThumb.style.opacity = '1';
    });
    volumeSlider.addEventListener('mouseleave', () => {
      volumeThumb.style.opacity = '0';
    });
    
    // Hide speed menu when clicking elsewhere
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.speed-control')) {
        speedMenu.style.display = 'none';
      }
    });
  };

  // Create Audio Element
  const createAudioElement = (data) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'editor-audio-wrapper';
    wrapper.contentEditable = 'false';
    wrapper.style.cssText = `
      margin: 1rem 0;
      cursor: pointer;
      position: relative;
      user-select: none;
      -webkit-user-select: none;
    `;
    wrapper.setAttribute('data-audio-url', data.url);
    wrapper.setAttribute('data-audio-title', data.title);
    wrapper.setAttribute('data-audio-alignment', data.alignment);
    wrapper.setAttribute('data-audio-filename', data.fileName);
    wrapper.setAttribute('tabindex', '-1');
    
    // Create the audio player HTML using the provided design
    wrapper.innerHTML = `
      <div class="audio green-audio-player" style="
        width: 400px;
        min-width: 300px;
        height: 56px;
        box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.07);
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-left: 24px;
        padding-right: 24px;
        border-radius: 4px;
        user-select: none;
        -webkit-user-select: none;
        background-color: #fff;
        margin: 0 auto;
      ">
        <div class="play-pause-btn" style="cursor: pointer;">
          <svg viewBox="0 0 18 24" height="24" width="18" xmlns="http://www.w3.org/2000/svg">
            <path class="play-pause-icon" d="M18 12L0 24V0" fill-rule="evenodd" fill="#566574" />
          </svg>
        </div>
        <div class="controls" style="
          font-family: 'Roboto', sans-serif;
          font-size: 16px;
          line-height: 18px;
          color: #55606e;
          display: flex;
          flex-grow: 1;
          justify-content: space-between;
          align-items: center;
          margin-left: 24px;
          margin-right: 24px;
        ">
          <span class="current-time" style="cursor: default;">0:00</span>
          <div class="slider" style="
            flex-grow: 1;
            background-color: #d8d8d8;
            cursor: pointer;
            position: relative;
            margin-left: 16px;
            margin-right: 16px;
            border-radius: 2px;
            height: 4px;
          ">
            <div class="progress" style="
              background-color: #44bfa3;
              border-radius: inherit;
              position: absolute;
              pointer-events: none;
              width: 0;
              height: 100%;
            ">
              <div class="pin" style="
                height: 16px;
                width: 16px;
                border-radius: 8px;
                background-color: #44bfa3;
                position: absolute;
                pointer-events: all;
                box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.32);
                right: -8px;
                top: -6px;
              "></div>
            </div>
          </div>
          <span class="total-time" style="cursor: default;">0:00</span>
        </div>
        <div class="volume" style="position: relative;">
          <div class="volume-btn" style="cursor: pointer;">
            <svg viewBox="0 0 24 24" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.667 0v2.747c3.853 1.146 6.666 4.72 6.666 8.946 0 4.227-2.813 7.787-6.666 8.934v2.76C20 22.173 24 17.4 24 11.693 24 5.987 20 1.213 14.667 0zM18 11.693c0-2.36-1.333-4.386-3.333-5.373v10.707c2-.947 3.333-2.987 3.333-5.334zm-18-4v8h5.333L12 22.36V1.027L5.333 7.693H0z" fill-rule="evenodd" fill="#566574" />
            </svg>
          </div>
          <div class="volume-controls hidden" style="
            width: 30px;
            height: 135px;
            background-color: rgba(0, 0, 0, 0.62);
            border-radius: 7px;
            position: absolute;
            left: -3px;
            bottom: 52px;
            flex-direction: column;
            align-items: center;
            display: none;
          ">
            <div class="slider" style="
              margin-top: 12px;
              margin-bottom: 12px;
              width: 6px;
              border-radius: 3px;
              flex-grow: 1;
              background-color: #d8d8d8;
              cursor: pointer;
              position: relative;
            ">
              <div class="progress" style="
                bottom: 0;
                height: 100%;
                width: 6px;
                background-color: #44bfa3;
                border-radius: inherit;
                position: absolute;
                pointer-events: none;
              ">
                <div class="pin" style="
                  height: 16px;
                  width: 16px;
                  border-radius: 8px;
                  background-color: #44bfa3;
                  position: absolute;
                  pointer-events: all;
                  box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.32);
                  left: -5px;
                  top: -8px;
                "></div>
              </div>
            </div>
          </div>
        </div>
        <audio crossorigin style="display: none;">
          <source src="${data.url}" type="audio/mpeg">
          <source src="${data.url}" type="audio/wav">
          <source src="${data.url}" type="audio/ogg">
          Your browser does not support the audio element.
        </audio>
      </div>
    `;
    
    // Add click handler for selection
    wrapper.addEventListener('click', (e) => {
      // Don't select if clicking on audio controls
      if (!e.target.closest('.play-pause-btn, .slider, .volume-btn')) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedAudio(wrapper);
      }
    });
    
    // Apply alignment to wrapper's parent container
    const alignmentWrapper = document.createElement('div');
    alignmentWrapper.className = 'audio-alignment-wrapper';
    alignmentWrapper.style.cssText = `
      text-align: ${data.alignment};
      margin: 0.5rem 0;
      width: 100%;
    `;
    alignmentWrapper.appendChild(wrapper);
    
    // Initialize audio player functionality
    initializeAudioPlayer(wrapper);
    
    // Insert with proper cursor positioning
    insertElementWithCursor(alignmentWrapper);
  };

  // Initialize Audio Player
  const initializeAudioPlayer = (wrapper) => {
    const audio = wrapper.querySelector('audio');
    const playPauseBtn = wrapper.querySelector('.play-pause-btn');
    const playPauseIcon = wrapper.querySelector('.play-pause-icon');
    const currentTimeSpan = wrapper.querySelector('.current-time');
    const totalTimeSpan = wrapper.querySelector('.total-time');
    const progressSlider = wrapper.querySelector('.slider');
    const progress = wrapper.querySelector('.progress');
    const progressPin = wrapper.querySelector('.pin');
    const volumeBtn = wrapper.querySelector('.volume-btn');
    const volumeControls = wrapper.querySelector('.volume-controls');
    
    let isPlaying = false;
    let isDragging = false;
    
    // Format time helper
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Load audio metadata
    audio.addEventListener('loadedmetadata', () => {
      totalTimeSpan.textContent = formatTime(audio.duration);
    });
    
    // Update progress
    audio.addEventListener('timeupdate', () => {
      if (!isDragging) {
        const progressPercent = (audio.currentTime / audio.duration) * 100;
        progress.style.width = `${progressPercent}%`;
        currentTimeSpan.textContent = formatTime(audio.currentTime);
      }
    });
    
    // Play/Pause functionality
    playPauseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (isPlaying) {
        audio.pause();
        playPauseIcon.setAttribute('d', 'M18 12L0 24V0'); // Play icon
        isPlaying = false;
      } else {
        audio.play();
        playPauseIcon.setAttribute('d', 'M0 0h6v24H0zM12 0h6v24h-6z'); // Pause icon
        isPlaying = true;
      }
    });
    
    // Progress slider functionality
    progressSlider.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const rect = progressSlider.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const progressPercent = (clickX / rect.width) * 100;
      const newTime = (progressPercent / 100) * audio.duration;
      
      audio.currentTime = newTime;
      progress.style.width = `${progressPercent}%`;
    });
    
    // Volume controls
    volumeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      volumeControls.classList.toggle('hidden');
    });
    
    // Hide volume controls when clicking elsewhere
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.volume')) {
        volumeControls.classList.add('hidden');
      }
    });
  };

  // Update Audio Element
  const updateAudioElement = (element, data) => {
    const audio = element.querySelector('audio');
    if (audio) {
      audio.src = data.url;
    }
    
    element.setAttribute('data-audio-url', data.url);
    element.setAttribute('data-audio-title', data.title);
    element.setAttribute('data-audio-alignment', data.alignment);
    
    // Update alignment
    const alignmentWrapper = element.closest('.audio-alignment-wrapper');
    if (alignmentWrapper) {
      alignmentWrapper.style.textAlign = data.alignment;
    }
    element.setAttribute('data-audio-alignment', data.alignment);
    
    // Re-initialize player
    initializeAudioPlayer(element);
  };

  // Handle Edit Audio
  const handleEditAudio = (audioElement) => {
    const url = audioElement.getAttribute('data-audio-url') || '';
    const title = audioElement.getAttribute('data-audio-title') || '';
    const alignment = audioElement.getAttribute('data-audio-alignment') || 'center';
    
    setAudioPreviewUrl(url);
    setAudioTitle(title);
    setAudioAlignment(alignment);
    setEditingAudio(audioElement);
    setSelectedAudioFile(null); // Don't require new file for editing
    setShowAudioModal(true);
  };

  // Create Image Element
  const createImageElement = (data) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'editor-image-wrapper';
    wrapper.contentEditable = 'false';
    wrapper.style.cssText = `
      display: inline-block;
      width: ${data.originalWidth}px;
      height: ${data.originalHeight}px;
      margin: 1rem 0;
      border: 2px solid #e5e7eb;
      border-radius: 0.5rem;
      overflow: hidden;
      cursor: pointer;
      position: relative;
      vertical-align: top;
      user-select: none;
      -webkit-user-select: none;
      text-align: ${data.alignment};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    `;
    wrapper.setAttribute('data-image-url', data.url);
    wrapper.setAttribute('data-image-alt', data.alt);
    wrapper.setAttribute('data-image-alignment', data.alignment);
    wrapper.setAttribute('data-original-width', data.originalWidth);
    wrapper.setAttribute('data-original-height', data.originalHeight);
    wrapper.setAttribute('tabindex', '-1');
    
    // Create image element - use contain instead of cover to prevent cropping
    const img = document.createElement('img');
    img.src = data.url;
    img.alt = data.alt;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      pointer-events: none;
      background-color: #f9fafb;
    `;
    
    wrapper.appendChild(img);
    
    // Add click handler for selection
    wrapper.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedImage(wrapper);
    });
    
    // Apply alignment to wrapper's parent container
    const alignmentWrapper = document.createElement('div');
    alignmentWrapper.className = 'image-alignment-wrapper';
    alignmentWrapper.style.cssText = `
      text-align: ${data.alignment};
      margin: 0.5rem 0;
      width: 100%;
    `;
    alignmentWrapper.appendChild(wrapper);
    
    // Insert with proper cursor positioning
    insertElementWithCursor(alignmentWrapper);
  };

  // Update Image Element
  const updateImageElement = (element, data) => {
    const img = element.querySelector('img');
    if (img) {
      img.src = data.url;
      img.alt = data.alt;
    }
    
    element.setAttribute('data-image-url', data.url);
    element.setAttribute('data-image-alt', data.alt);
    element.setAttribute('data-image-alignment', data.alignment);
    
    // Update alignment
    const alignmentWrapper = element.closest('.image-alignment-wrapper');
    if (alignmentWrapper) {
      alignmentWrapper.style.textAlign = data.alignment;
    }
    element.setAttribute('data-image-alignment', data.alignment);
  };

  // Handle Edit Image
  const handleEditImage = (imageElement) => {
    const url = imageElement.getAttribute('data-image-url') || '';
    const alt = imageElement.getAttribute('data-image-alt') || '';
    const alignment = imageElement.getAttribute('data-image-alignment') || 'center';
    
    setImagePreviewUrl(url);
    setImageAltText(alt);
    setImageAlignment(alignment);
    setEditingImage(imageElement);
    setSelectedImageFile(null); // Don't require new file for editing
    setShowImageModal(true);
  };

  const handleFileUpload = async (files) => {
    try {
      setUploadingFiles(true);
      const uploadedFiles = [];
      
      for (const file of files) {
        const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const response = await storageService.uploadFile('file_uploads', fileId, file);
        
        if (response.success) {
          const fileUrl = storageService.getFileView('file_uploads', response.data.$id);
          uploadedFiles.push({
            id: response.data.$id,
            name: file.name,
            size: file.size,
            url: fileUrl,
            type: file.type
          });
        }
      }
      
      setFileDownloadFiles(prev => [...prev, ...uploadedFiles]);
      showToast('Files uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading files:', error);
      showToast('Failed to upload files. Please try again.', 'error');
    } finally {
      setUploadingFiles(false);
    }
  };

  // Handle Save File Download
  const handleSaveFileDownload = async () => {
    if (!fileDownloadMessage.trim() || !fileDownloadDate || fileDownloadFiles.length === 0) {
      showToast('Please fill all fields and upload at least one file.', 'error');
      return;
    }

    try {
      const fileDownloadData = {
        message: fileDownloadMessage,
        date: fileDownloadDate,
        files: fileDownloadFiles,
        createdAt: new Date().toISOString()
      };
      
      if (editingFileDownload) {
        // Update existing file download
        editingFileDownload.setAttribute('data-message', fileDownloadMessage);
        editingFileDownload.setAttribute('data-date', fileDownloadDate);
        editingFileDownload.setAttribute('data-files', JSON.stringify(fileDownloadFiles));
        
        // Update the display
        updateFileDownloadDisplay(editingFileDownload);
        setEditingFileDownload(null);
      } else {
        // Create new file download
        restoreSelection();
        createFileDownloadElement(fileDownloadData);
      }
      
      // Trigger input event to update preview
      const event = new Event('input', { bubbles: true });
      editorRef.current?.dispatchEvent(event);
      
      setFileDownloadMessage('');
      setFileDownloadDate('');
      setFileDownloadFiles([]);
      setShowFileDownloadModal(false);
      showToast('File download created successfully!', 'success');
    } catch (error) {
      console.error('Error saving file download:', error);
      showToast('Failed to save file download. Please try again.', 'error');
    }
  };

  // Create Element with Breathing Room - OPTIMIZED VERSION
  // Reduced whitespace and made it easily deletable
  const createElementWithBreathingRoom = (element) => {
    // Just return the element directly - no wrapper needed
    // The margin on individual elements provides spacing
    return element;
  };
  
  // Helper function to insert element with proper cursor positioning
  const insertElementWithCursor = (element) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.insertNode(element);
      
      // Create a new paragraph after the element for easy cursor placement
      const newParagraph = document.createElement('p');
      newParagraph.innerHTML = '<br>';
      range.setStartAfter(element);
      range.insertNode(newParagraph);
      
      // Move cursor to the new paragraph
      range.setStart(newParagraph, 0);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current?.appendChild(element);
      
      // Add paragraph after
      const newParagraph = document.createElement('p');
      newParagraph.innerHTML = '<br>';
      editorRef.current?.appendChild(newParagraph);
    }
  };

  // Create File Download Element
  const createFileDownloadElement = (data) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'editor-file-download-wrapper';
    wrapper.contentEditable = 'false';
    wrapper.setAttribute('data-message', data.message);
    wrapper.setAttribute('data-date', data.date);
    wrapper.setAttribute('data-files', JSON.stringify(data.files));
    
    // Ensure no background inheritance
    wrapper.style.cssText = `
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
      margin: 1rem 0;
      padding: 0;
      border: none;
      display: block;
      cursor: pointer;
      position: relative;
      z-index: 1;
    `;
    
    // Create the styled card
    wrapper.innerHTML = `
      <div style="
        width: fit-content;
        background-color: #f2f3f7 !important;
        background: #f2f3f7 !important;
        background-image: none !important;
        border-radius: 0.75em;
        cursor: pointer;
        transition: ease 0.2s;
        box-shadow: 1em 1em 1em #d8dae0b1, -0.75em -0.75em 1em #ffffff;
        border: 1.5px solid #f2f3f7;
        display: inline-block;
        position: relative;
        z-index: 2;
      " class="file-download-card">
        <div style="
          margin: 1.25em 1.375em 1.375em 1.375em;
          display: flex;
          flex-direction: row;
          gap: 0.75em;
          background: transparent !important;
          background-color: transparent !important;
          background-image: none !important;
        ">
          <div style="
            width: 0.625em;
            height: 0.625em;
            background-color: #4C1D95 !important;
            background: #4C1D95 !important;
            background-image: none !important;
            margin: 0.375em 0;
            border-radius: 0.5em;
          "></div>
          <div style="
            display: flex;
            flex-direction: column;
            gap: 0.875em;
            background: transparent !important;
            background-color: transparent !important;
            background-image: none !important;
          ">
            <div style="
              display: flex;
              flex-direction: column;
              gap: 0.25em;
              color: #333;
              background: transparent !important;
              background-color: transparent !important;
              background-image: none !important;
            ">
              <p style="margin: 0; font-size: 14px; line-height: 1.4; background: transparent !important; background-color: transparent !important; background-image: none !important;">
                ${data.message}
              </p>
              <p style="margin: 0; font-size: 0.875em; color: #777; background: transparent !important; background-color: transparent !important; background-image: none !important;">
                File Updated on: ${new Date(data.date).toLocaleDateString()}
              </p>
            </div>
            <div style="
              display: flex;
              flex-direction: column;
              gap: 0.5em;
              align-items: flex-start;
              background: transparent !important;
              background-color: transparent !important;
              background-image: none !important;
            ">
              ${data.files.map(file => `
                <button style="
                  font-size: 15px;
                  background-color: transparent !important;
                  background: transparent !important;
                  background-image: none !important;
                  font-weight: 600;
                  color: #1677ff;
                  border: none;
                  border-radius: 1.5em;
                  cursor: pointer;
                  padding: 4px 8px;
                  text-align: left;
                  width: 100%;
                " class="download-btn" data-file-url="${file.url}" data-file-name="${file.name}" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">
                  Download ${file.name}
                </button>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Add click handler for editing (click on card but not on download buttons)
    wrapper.addEventListener('click', (e) => {
      if (!e.target.classList.contains('download-btn') && !e.target.closest('.download-btn')) {
        e.preventDefault();
        e.stopPropagation();
        handleEditFileDownload(wrapper);
      }
    });
    
    // Add download functionality for each button
    const downloadBtns = wrapper.querySelectorAll('.download-btn');
    downloadBtns.forEach(downloadBtn => {
      downloadBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const fileUrl = downloadBtn.getAttribute('data-file-url');
        const fileName = downloadBtn.getAttribute('data-file-name');
        handleDownloadSingleFile(fileUrl, fileName);
      });
    });
    
    // Add hover effect
    const card = wrapper.querySelector('.file-download-card');
    card.addEventListener('mouseenter', () => {
      card.style.backgroundColor = '#d3ddf1';
      card.style.border = '1.5px solid #1677ff';
    });
    card.addEventListener('mouseleave', () => {
      card.style.backgroundColor = '#f2f3f7';
      card.style.border = '1.5px solid #f2f3f7';
    });
    
    wrapper.style.cursor = 'pointer';
    wrapper.title = 'Click to edit file download';
    
    // Wrap with breathing room for better cursor navigation
    const containerWithBreathingRoom = createElementWithBreathingRoom(wrapper);
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.insertNode(containerWithBreathingRoom);
      
      // Move cursor after file download
      range.setStartAfter(containerWithBreathingRoom);
      range.setEndAfter(containerWithBreathingRoom);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current?.appendChild(containerWithBreathingRoom);
    }
  };

  // Handle Download Single File
  const handleDownloadSingleFile = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast(`Downloading ${fileName}...`, 'success');
  };

  // Handle Download Files (keeping for backward compatibility)
  const handleDownloadFiles = (files) => {
    if (files.length > 1) {
      showToast(`Starting download of ${files.length} files...`, 'info');
    }
    
    files.forEach((file, index) => {
      // Add a small delay between downloads to prevent browser blocking
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = file.url;
        link.download = file.name;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Show completion message for the last file
        if (index === files.length - 1 && files.length > 1) {
          setTimeout(() => {
            showToast('All files downloaded successfully!', 'success');
          }, 500);
        }
      }, index * 200); // 200ms delay between each file
    });
  };

  // Handle Edit File Download
  const handleEditFileDownload = (fileDownloadElement) => {
    const message = fileDownloadElement.getAttribute('data-message') || '';
    const date = fileDownloadElement.getAttribute('data-date') || '';
    const filesData = fileDownloadElement.getAttribute('data-files') || '[]';
    
    try {
      const files = JSON.parse(filesData);
      setFileDownloadMessage(message);
      setFileDownloadDate(date);
      setFileDownloadFiles(files);
      setEditingFileDownload(fileDownloadElement);
      setShowFileDownloadModal(true);
    } catch (error) {
      console.error('Error parsing file data:', error);
    }
  };

  // Update File Download Display
  const updateFileDownloadDisplay = (element) => {
    const message = element.getAttribute('data-message');
    const date = element.getAttribute('data-date');
    const filesData = element.getAttribute('data-files');
    
    try {
      const files = JSON.parse(filesData);
      const messageElement = element.querySelector('p');
      const dateElement = element.querySelectorAll('p')[1];
      
      if (messageElement) messageElement.textContent = message;
      if (dateElement) dateElement.textContent = `File Updated on: ${new Date(date).toLocaleDateString()}`;
      
      // Update the buttons container with new files
      const buttonsContainer = element.querySelector('div[style*="flex-direction: column"]');
      if (buttonsContainer) {
        buttonsContainer.innerHTML = files.map(file => `
          <button style="
            font-size: 15px;
            background-color: transparent !important;
            background: transparent !important;
            background-image: none !important;
            font-weight: 600;
            color: #1677ff;
            border: none;
            border-radius: 1.5em;
            cursor: pointer;
            padding: 4px 8px;
            text-align: left;
            width: 100%;
          " class="download-btn" data-file-url="${file.url}" data-file-name="${file.name}" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">
            Download ${file.name}
          </button>
        `).join('');
        
        // Re-add event listeners to new buttons
        const downloadBtns = buttonsContainer.querySelectorAll('.download-btn');
        downloadBtns.forEach(downloadBtn => {
          downloadBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const fileUrl = downloadBtn.getAttribute('data-file-url');
            const fileName = downloadBtn.getAttribute('data-file-name');
            handleDownloadSingleFile(fileUrl, fileName);
          });
        });
      }
    } catch (error) {
      console.error('Error updating file download display:', error);
    }
  };

  useEffect(() => {
    const initializeEditor = async () => {
      // Detect mode from URL path
      const currentPath = window.location.pathname;
      const isProjectPath = currentPath.includes('/project/');
      const isBlogPath = currentPath.includes('/blog/');
      
      // Get ID from URL params
      const idFromUrl = urlBlogId || urlProjectId;
      
      // Clear localStorage for new items
      if (idFromUrl === 'new') {
        console.log('🆕 Creating new item - clearing localStorage');
        localStorage.removeItem('editor_mode');
        localStorage.removeItem('editor_project_id');
        localStorage.removeItem('blog_editor_mode');
        localStorage.removeItem('blog_editor_data');
        localStorage.removeItem('blog_editing_id');
        localStorage.removeItem('blog_editing_id_current');
        localStorage.removeItem('blog_editor_backup');
        localStorage.removeItem('richTextEditorContent');
        localStorage.removeItem('richTextEditorProjectMeta');
      }
      
      // Load subcategories first (not dependent on blog/project mode)
      loadSubcategories();
      
      // PROJECT MODE
      if (isProjectPath || urlProjectId) {
        console.log('🔄 Initializing editor in PROJECT mode with ID:', idFromUrl);
        setIsProjectMode(true);
        setIsBlogMode(false);
        
        // Set localStorage for ProjectEditorPage compatibility
        localStorage.setItem('editor_mode', 'project');
        if (idFromUrl) {
          localStorage.setItem('editor_project_id', idFromUrl);
        }
        
        // Load categories for projects
        await loadCategories();
        
        // Load authors
        await loadAuthors();
        
        if (idFromUrl && idFromUrl !== 'new') {
          // Load existing project
          setProjectEditingId(idFromUrl);
          const loadSuccess = await loadExistingProjectData(idFromUrl);
          
          if (!loadSuccess) {
            console.log('❌ Project not found');
            showToast('Project not found', 'error');
            navigate('/admin/project-management', { replace: true });
            return;
          }
        } else {
          // New project
          setProjectEditingId('new');
          setFormData(prev => ({
            ...prev,
            projectDetails: [
              { label: 'Software', value: '' },
              { label: 'Timeframe', value: '' },
              { label: 'Data Source', value: '' },
              { label: 'Study Area', value: '' }
            ]
          }));
        }
        
        return; // Exit early for project mode
      }
      
      // BLOG MODE
      if (isBlogPath || urlBlogId) {
        console.log('🔄 Initializing editor in BLOG mode with ID:', idFromUrl);
        setIsBlogMode(true);
        setIsProjectMode(false);
        
        // Load categories for blogs
        await loadCategories();
        
        // Load authors for blog mode
        await loadAuthors();
        
        if (idFromUrl && idFromUrl !== 'new') {
          // Load existing blog
          console.log('🔄 Loading blog from URL parameter:', idFromUrl);
          setBlogEditingId(idFromUrl);
          
          const loadSuccess = await loadExistingBlogData(idFromUrl);
          
          if (!loadSuccess) {
            // If URL blog doesn't exist, redirect to new blog
            console.log('❌ Blog not found, redirecting to new blog');
            navigate('/admin/rich-text-editor/blog/new', { replace: true });
            return;
          }
        } else {
          // New blog
          console.log('🔄 Setting up new blog from URL');
          setBlogEditingId('new');
          setBlogStatus({ published: false, isDraft: true });
        }
        
        return; // Exit early for blog mode
      }
      
      // LEGACY FALLBACK (for old URLs without /blog/ or /project/)
      const editorMode = localStorage.getItem('editor_mode');
      const projectIdFromStorage = localStorage.getItem('editor_project_id');
      
      if (editorMode === 'project' && projectIdFromStorage) {
        // Redirect to new project URL format
        navigate(`/admin/rich-text-editor/project/${projectIdFromStorage}`, { replace: true });
        return;
      }
      
      if (urlBlogId) {
        // Redirect to new blog URL format
        navigate(`/admin/rich-text-editor/blog/${urlBlogId}`, { replace: true });
        return;
      }
      
      // Default: load categories
      await loadCategories();
    };
    
    initializeEditor();
  }, [urlBlogId, urlProjectId, navigate]); // Re-run when URL params change

  // Handle page unload and data persistence
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only show warning if we have unsaved changes in blog mode
      if (isBlogMode && (formData.title || formData.description || formData.fullDescription)) {
        // Save current state to localStorage as backup with URL blog ID
        const backupData = {
          ...formData,
          blogStatus,
          blogEditingId: urlBlogId || blogEditingId, // Use URL blog ID as primary
          timestamp: Date.now()
        };
        localStorage.setItem('blog_editor_backup', JSON.stringify(backupData));
        
        // Show browser warning for unsaved changes
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      // Save backup when page becomes hidden (user switches tabs, etc.)
      if (document.hidden && isBlogMode && (formData.title || formData.description || formData.fullDescription)) {
        const backupData = {
          ...formData,
          blogStatus,
          blogEditingId: urlBlogId || blogEditingId, // Use URL blog ID as primary
          timestamp: Date.now()
        };
        localStorage.setItem('blog_editor_backup', JSON.stringify(backupData));
        console.log('💾 Auto-saved backup due to page visibility change');
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isBlogMode, formData, blogStatus, blogEditingId, urlBlogId]);

  // Auto-save backup every 30 seconds when editing
  useEffect(() => {
    if (!isBlogMode) return;

    const autoSaveInterval = setInterval(() => {
      if (formData.title || formData.description || formData.fullDescription) {
        const backupData = {
          ...formData,
          blogStatus,
          blogEditingId: urlBlogId || blogEditingId, // Use URL blog ID as primary
          timestamp: Date.now()
        };
        localStorage.setItem('blog_editor_backup', JSON.stringify(backupData));
        console.log('💾 Auto-saved backup (30s interval)');
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [isBlogMode, formData, blogStatus, blogEditingId, urlBlogId]);

  // Cleanup old backup data on component unmount
  useEffect(() => {
    // Cleanup function that runs when component unmounts
    return () => {
      // Clean up backup data when leaving the editor (but only if we successfully saved)
      const currentBackup = localStorage.getItem('blog_editor_backup');
      if (currentBackup) {
        try {
          const parsedBackup = JSON.parse(currentBackup);
          const backupAge = Date.now() - (parsedBackup.timestamp || 0);
          
          // Remove backup if it's older than 1 hour or if we have a saved blog ID
          if (backupAge > 3600000 || (urlBlogId && urlBlogId !== 'new')) {
            localStorage.removeItem('blog_editor_backup');
            console.log('🧹 Cleaned up old backup data on unmount');
          }
        } catch (error) {
          // Remove corrupted backup data
          localStorage.removeItem('blog_editor_backup');
        }
      }
    };
  }, [urlBlogId]);

  // Reload categories when blog mode changes or URL blog ID changes
  useEffect(() => {
    loadCategories();
  }, [isBlogMode, urlBlogId]);

  // Load existing blog data from database
  const loadExistingBlogData = async (blogId) => {
    try {
      console.log('📖 Loading existing blog data for ID:', blogId);
      const result = await databaseService.getDocument('blogs', blogId);
      
      if (result.success && result.data) {
        const blog = result.data;
        console.log('✅ Blog data loaded successfully:', blog.title);
        
        // Set blog status
        setBlogStatus({
          published: blog.published || false,
          isDraft: blog.isDraft !== undefined ? blog.isDraft : true
        });
        
        // Map blog data to form structure
        const mappedData = {
          title: blog.title || '',
          category: blog.category || '',
          subcategoryId: '',
          description: blog.description || '',
          fullDescription: blog.fullDescription || '',
          thumbnail: null,
          thumbnailUrl: blog.thumbnailUrl || '',
          gallery: [],
          galleryUrls: blog.galleryUrls || [],
          likes: blog.likes || 0,
          featured: blog.featured || false,
          projectDetails: [
            { label: 'Author', value: blog.authorNames?.[0] || '' },
            { label: 'Publish Date', value: blog.publishDate || new Date().toISOString().split('T')[0] },
            { label: 'Category', value: blog.category || '' },
            { label: 'Tags', value: blog.tags?.join(', ') || '' }
          ],
          projectLink: '',
          tags: blog.tags || [],
          customSlug: blog.customSlug || '',
          useProjectPrefix: blog.useProjectPrefix !== undefined ? blog.useProjectPrefix : true,
          // Add author fields for blog mode
          authorIds: blog.authorIds && blog.authorIds.length > 0 && blog.authorIds[0] ? blog.authorIds : [], // Only store author IDs
          publishDate: blog.publishDate || new Date().toISOString().split('T')[0]
        };
        
        setFormData(mappedData);
        
        return true; // Success
      } else {
        console.error('❌ Failed to load blog data:', result.error);
        showToast('Failed to load blog data. Blog may not exist.', 'warning');
        return false; // Failure
      }
    } catch (error) {
      console.error('❌ Error loading existing blog data:', error);
      showToast('Error loading blog data. Please try again.', 'error');
      
      return false; // Failure
    }
  };

  // Load existing project data from database
  const loadExistingProjectData = async (projectId) => {
    try {
      console.log('📖 Loading existing project data for ID:', projectId);
      const result = await databaseService.getDocument(PROJECTS_COLLECTION, projectId);
      
      if (result.success && result.data) {
        const project = result.data;
        console.log('✅ Project data loaded successfully:', project.title);
        
        // Parse projectDetails from database (stored as JSON strings)
        let projectDetails = [];
        if (project.projectDetails && Array.isArray(project.projectDetails)) {
          // New format: array of JSON strings
          projectDetails = project.projectDetails.map(jsonStr => {
            try {
              return JSON.parse(jsonStr);
            } catch (e) {
              console.error('Error parsing project detail:', e);
              return null;
            }
          }).filter(Boolean);
        } else if (project.software || project.timeframe || project.dataSource || project.studyArea) {
          // Old format: individual fields
          projectDetails = [
            { label: 'Software', value: project.software || '' },
            { label: 'Timeframe', value: project.timeframe || '' },
            { label: 'Data Source', value: project.dataSource || '' },
            { label: 'Study Area', value: project.studyArea || '' }
          ];
        }
        
        // Default fields if empty
        if (projectDetails.length === 0) {
          projectDetails = [
            { label: 'Software', value: '' },
            { label: 'Timeframe', value: '' },
            { label: 'Data Source', value: '' },
            { label: 'Study Area', value: '' }
          ];
        }
        
        // Map project data to form structure
        const mappedData = {
          title: project.title || '',
          category: project.category || '',
          subcategoryId: project.subcategoryId || '',
          description: project.description || '',
          fullDescription: project.fullDescription || '',
          thumbnail: null,
          thumbnailUrl: project.thumbnailUrl || '',
          gallery: [],
          galleryUrls: project.galleryUrls || [],
          likes: project.likes || 0,
          featured: project.featured || false,
          projectDetails: projectDetails,
          projectLink: project.projectLink || '',
          tags: project.tags || [],
          customSlug: project.customSlug || '',
          useProjectPrefix: project.useProjectPrefix !== undefined ? project.useProjectPrefix : true,
          publishDate: project.publishDate || new Date().toISOString().split('T')[0]
        };
        
        setFormData(mappedData);
        
        // Set project status
        setProjectStatus({
          published: project.published || false,
          isDraft: project.isDraft !== undefined ? project.isDraft : true
        });
        
        return true; // Success
      } else {
        console.error('❌ Failed to load project data:', result.error);
        showToast('Failed to load project data. Project may not exist.', 'warning');
        return false; // Failure
      }
    } catch (error) {
      console.error('❌ Error loading existing project data:', error);
      showToast('Error loading project data. Please try again.', 'error');
      
      return false; // Failure
    }
  };

  const loadCategories = async () => {
    try {
      // Force blog categories when we have a URL blog ID or are in blog mode
      const shouldUseBlogCategories = urlBlogId || isBlogMode;
      const collectionName = shouldUseBlogCategories ? BLOG_CATEGORIES_COLLECTION : CATEGORIES_COLLECTION;
      
      console.log('🔄 Loading categories:', {
        urlBlogId,
        isBlogMode,
        shouldUseBlogCategories,
        collectionName
      });
      
      const result = await databaseService.listDocuments(collectionName);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => a.order - b.order);
        setCategories(sorted);
        console.log('✅ Categories loaded:', sorted.map(cat => cat.name));
      } else {
        console.error('❌ Failed to load categories:', result.error);
      }
    } catch (error) {
      console.error('❌ Error loading categories:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      const result = await databaseService.listDocuments(SUBCATEGORIES_COLLECTION);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => a.order - b.order);
        setSubcategories(sorted);
      }
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  // Create new category inline
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      showToast('Please enter a category name', 'error');
      return;
    }

    try {
      setCreatingCategory(true);
      const shouldUseBlogCategories = urlBlogId || isBlogMode;
      const collectionName = shouldUseBlogCategories ? BLOG_CATEGORIES_COLLECTION : CATEGORIES_COLLECTION;
      
      // Get the highest order number
      const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.order || 0)) : 0;
      
      const result = await databaseService.createDocument(collectionName, {
        name: newCategoryName.trim(),
        color: newCategoryColor,
        order: maxOrder + 1
      });

      if (result.success) {
        showToast('Category created successfully!', 'success');
        await loadCategories();
        setFormData(prev => ({ ...prev, category: newCategoryName.trim() }));
        setNewCategoryName('');
        setNewCategoryColor('#4C1D95');
        setShowCategoryInput(false);
      } else {
        showToast('Failed to create category', 'error');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      showToast('Failed to create category', 'error');
    } finally {
      setCreatingCategory(false);
    }
  };

  // Create new subcategory inline
  const handleCreateSubcategory = async () => {
    if (!newSubcategoryName.trim()) {
      showToast('Please enter a subcategory name', 'error');
      return;
    }

    const selectedCategory = categories.find(cat => cat.name === formData.category);
    if (!selectedCategory) {
      showToast('Please select a category first', 'error');
      return;
    }

    try {
      setCreatingSubcategory(true);
      
      // Get the highest order number for this category
      const categorySubcategories = subcategories.filter(sub => sub.categoryId === selectedCategory.$id);
      const maxOrder = categorySubcategories.length > 0 ? Math.max(...categorySubcategories.map(s => s.order || 0)) : 0;
      
      const result = await databaseService.createDocument(SUBCATEGORIES_COLLECTION, {
        name: newSubcategoryName.trim(),
        categoryId: selectedCategory.$id,
        order: maxOrder + 1
      });

      if (result.success) {
        showToast('Subcategory created successfully!', 'success');
        await loadSubcategories();
        setFormData(prev => ({ ...prev, subcategoryId: result.data.$id }));
        setNewSubcategoryName('');
        setShowSubcategoryInput(false);
      } else {
        showToast('Failed to create subcategory', 'error');
      }
    } catch (error) {
      console.error('Error creating subcategory:', error);
      showToast('Failed to create subcategory', 'error');
    } finally {
      setCreatingSubcategory(false);
    }
  };

  // Filter subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      const selectedCategory = categories.find(cat => cat.name === formData.category);
      if (selectedCategory) {
        const filtered = subcategories.filter(sub => sub.categoryId === selectedCategory.$id);
        setFilteredSubcategories(filtered);
      } else {
        setFilteredSubcategories([]);
      }
    } else {
      setFilteredSubcategories([]);
    }
  }, [formData.category, categories, subcategories]);

  const handleSelectSection = (sectionId) => {
    setSelectedSection(sectionId);
  };

  const handleBackToSelector = () => {
    setSelectedSection(null);
  };

  // Status management functions
  const handleStatusChange = (newStatus) => {
    console.log('Status change requested:', newStatus);
    setPendingStatusChange(newStatus);
  };

  const handleStatusUpdate = async (statusToUpdate = null) => {
    // Use passed status or fall back to pendingStatusChange
    const targetStatus = statusToUpdate || pendingStatusChange;
    console.log('Status update requested, target status:', targetStatus);
    
    if (!targetStatus || (!isBlogMode && !isProjectMode)) {
      console.log('No target status or not in blog/project mode');
      return;
    }
    
    try {
      const newPublishedStatus = targetStatus === 'Publish';
      console.log('Updating status to:', newPublishedStatus ? 'Published' : 'Draft');
      
      // Validate required fields only when publishing
      if (newPublishedStatus) {
        const missingFields = [];
        if (!formData.title?.trim()) missingFields.push('Title');
        if (!formData.category?.trim()) missingFields.push('Category');
        if (!formData.description?.trim()) missingFields.push('Description');
        
        // Check if fullDescription has actual content (not just empty HTML)
        const fullDescContent = formData.fullDescription?.replace(/<[^>]*>/g, '').trim();
        if (!fullDescContent) missingFields.push('Full Description');
        
        if (missingFields.length > 0) {
          console.log('Missing required fields:', missingFields);
          console.log('Current formData:', {
            title: formData.title,
            category: formData.category,
            description: formData.description,
            fullDescriptionLength: formData.fullDescription?.length || 0,
            fullDescriptionPreview: fullDescContent?.substring(0, 100) || 'Empty'
          });
          
          // Show more specific error message
          if (missingFields.length === 1) {
            showToast(`Please fill in the ${missingFields[0]} field before publishing.`, 'error');
          } else {
            showToast(`Please fill in these required fields: ${missingFields.join(', ')}`, 'error');
          }
          return;
        }
        
        console.log('All required fields are filled, proceeding with publish...');
      }

      // Handle blog or project based on mode
      if (isBlogMode) {
        const blogData = await prepareBlogData(newPublishedStatus);
        const result = await saveBlogToDatabase(blogData);
        
        if (result.success) {
          // Update blogEditingId if this was a new blog creation
          if (!blogEditingId || blogEditingId === 'new') {
            setBlogEditingId(result.data.$id);
            localStorage.setItem('blog_editing_id_current', result.data.$id);
          }
          
          // Update blog status
          setBlogStatus({ 
            published: newPublishedStatus, 
            isDraft: !newPublishedStatus 
          });
          
          // Clear pending change
          setPendingStatusChange(null);
          
          const statusText = newPublishedStatus ? 'published' : 'saved as draft';
          showToast(`Blog ${statusText} successfully!`, 'success');
          
          // DON'T auto-navigate after publish - let user stay in editor
          // They can use the back button if they want to return to blog management
        } else {
          throw new Error(result.error);
        }
      } else if (isProjectMode) {
        const projectData = await prepareProjectData(newPublishedStatus);
        const result = await saveProjectToDatabase(projectData);
        
        if (result.success) {
          // Update projectEditingId if this was a new project creation
          if (!projectEditingId || projectEditingId === 'new') {
            const newProjectId = result.data.$id;
            setProjectEditingId(newProjectId);
            localStorage.setItem('editor_project_id', newProjectId);
            // Update URL with new project ID
            navigate(`/admin/rich-text-editor/project/${newProjectId}`, { replace: true });
          }
          
          // Update project status
          setProjectStatus({ 
            published: newPublishedStatus, 
            isDraft: !newPublishedStatus 
          });
          
          // Clear pending change
          setPendingStatusChange(null);
          
          const statusText = newPublishedStatus ? 'published' : 'saved as draft';
          showToast(`Project ${statusText} successfully!`, 'success');
          
          // DON'T auto-navigate after publish - let user stay in editor
          // They can use the back button if they want to return to project management
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      const contentType = isProjectMode ? 'project' : 'blog';
      showToast(`Failed to update ${contentType} status. Please try again.`, 'error');
      // Clear pending change on error so user can try again
      setPendingStatusChange(null);
    }
  };

  // Master Save Function - Updates everything in database
  const handleMasterSave = async () => {
    // Handle both blog and project modes
    if (isProjectMode) {
      return await handleProjectSave();
    } else if (isBlogMode) {
      return await handleBlogSave();
    }
    return false;
  };

  // Project Save Function
  const handleProjectSave = async () => {
    try {
      console.log('🔄 Project save - current formData:', {
        title: formData.title,
        category: formData.category,
        projectEditingId: projectEditingId
      });
      
      const projectData = await prepareProjectData(projectStatus.published); // Keep current published status
      console.log('📝 Prepared project data for saving:', projectData);
      
      const result = await saveProjectToDatabase(projectData);
      
      if (result.success) {
        console.log('✅ Project saved successfully with data:', result.data);
        
        // Update projectEditingId if this was a new project creation
        if (!projectEditingId || projectEditingId === 'new') {
          const newProjectId = result.data.$id;
          setProjectEditingId(newProjectId);
          localStorage.setItem('editor_project_id', newProjectId);
          
          // Update URL to reflect the new project ID
          navigate(`/admin/rich-text-editor/project/${newProjectId}`, { replace: true });
          console.log('🆔 New project ID set and URL updated:', newProjectId);
        }
        
        showToast('Project saved successfully!', 'success');
        return true;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error saving project:', error);
      showToast('Failed to save project. Please try again.', 'error');
      return false;
    }
  };

  // Blog Save Function
  const handleBlogSave = async () => {
    try {
      console.log('🔄 Blog save - current formData:', {
        title: formData.title,
        authorIds: formData.authorIds,
        blogEditingId: blogEditingId,
        urlBlogId: urlBlogId
      });
      
      const blogData = await prepareBlogData(blogStatus.published); // Keep current published status
      console.log('📝 Prepared blog data for saving:', blogData);
      
      const result = await saveBlogToDatabase(blogData);
      
      if (result.success) {
        console.log('✅ Blog saved successfully with data:', result.data);
        
        // Update blogEditingId and URL if this was a new blog creation
        if (!blogEditingId || blogEditingId === 'new' || urlBlogId === 'new') {
          const newBlogId = result.data.$id;
          setBlogEditingId(newBlogId);
          
          // Update URL to reflect the new blog ID
          navigate(`/admin/rich-text-editor/blog/${newBlogId}`, { replace: true });
          console.log('🆔 New blog ID set and URL updated:', newBlogId);
        }
        
        // Clear backup data since we successfully saved to database
        localStorage.removeItem('blog_editor_backup');
        console.log('🧹 Cleared backup data after successful save');
        
        showToast('Blog saved successfully!', 'success');
        return true;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error saving blog:', error);
      showToast('Failed to save blog. Please try again.', 'error');
      return false;
    }
  };

  // Helper function to prepare blog data
  const prepareBlogData = async (isPublished) => {
    // Upload thumbnail if needed
    let thumbnailUrl = formData.thumbnailUrl;
    if (formData.thumbnail) {
      thumbnailUrl = await uploadImage(formData.thumbnail, 'blog_images');
      if (!thumbnailUrl) {
        throw new Error('Failed to upload thumbnail');
      }
    }

    // Upload gallery images if needed
    const finalGalleryUrls = [];
    if (formData.gallery.length > 0) {
      for (let i = 0; i < formData.gallery.length; i++) {
        const file = formData.gallery[i];
        if (file instanceof File) {
          const url = await uploadImage(file, 'blog_images');
          if (url) finalGalleryUrls.push(url);
        } else {
          if (formData.galleryUrls[i] && !formData.galleryUrls[i].startsWith('blob:')) {
            finalGalleryUrls.push(formData.galleryUrls[i]);
          }
        }
      }
    } else {
      finalGalleryUrls.push(...(formData.galleryUrls || []));
    }

    // Only store author IDs - author data will be fetched dynamically
    const authorIds = formData.authorIds && formData.authorIds.length > 0 && formData.authorIds[0] 
      ? formData.authorIds 
      : [];

    console.log('Final author IDs to save:', authorIds);

    // Use the publishDate from formData (set by the date picker)
    const publishDate = formData.publishDate || new Date().toISOString().split('T')[0];

    return {
      title: formData.title,
      category: formData.category,
      description: formData.description,
      thumbnailUrl: thumbnailUrl,
      galleryUrls: finalGalleryUrls,
      likes: parseInt(formData.likes) || 0,
      featured: formData.featured,
      fullDescription: formData.fullDescription,
      customSlug: formData.customSlug,
      useProjectPrefix: formData.useProjectPrefix,
      authorIds: authorIds, // Only store author IDs
      publishDate: publishDate,
      tags: formData.tags || [],
      published: isPublished,
      isDraft: !isPublished,
    };
  };

  // Helper function to save blog to database
  const saveBlogToDatabase = async (blogData) => {
    try {
      let result;
      if (blogEditingId && blogEditingId !== 'new') {
        // Update existing blog
        result = await databaseService.updateDocument(
          'blogs',
          blogEditingId,
          blogData
        );
      } else {
        // Create new blog
        result = await databaseService.createDocument(
          'blogs',
          blogData
        );
      }
      return result;
    } catch (error) {
      console.error('Database save error:', error);
      return { success: false, error: error.message };
    }
  };

  // Helper function to upload images
  const uploadImage = async (file, bucketId) => {
    try {
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileId = `${timestamp}_${randomStr}.${fileExt}`;
      
      const result = await storageService.uploadFile(bucketId, fileId, file);
      if (result.success) {
        const fileUrl = storageService.getFileView(bucketId, result.data.$id);
        return fileUrl;
      }
      throw new Error(result.error || 'Failed to upload image');
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  // Helper function to prepare project data
  const prepareProjectData = async (isPublished) => {
    // Upload thumbnail if needed
    let thumbnailUrl = formData.thumbnailUrl;
    if (formData.thumbnail) {
      thumbnailUrl = await uploadImage(formData.thumbnail, PROJECTS_BUCKET);
      if (!thumbnailUrl) {
        throw new Error('Failed to upload thumbnail');
      }
    }

    // Upload gallery images if needed
    const finalGalleryUrls = [];
    if (formData.gallery.length > 0) {
      for (let i = 0; i < formData.gallery.length; i++) {
        const file = formData.gallery[i];
        if (file instanceof File) {
          const url = await uploadImage(file, PROJECTS_BUCKET);
          if (url) finalGalleryUrls.push(url);
        } else {
          if (formData.galleryUrls[i] && !formData.galleryUrls[i].startsWith('blob:')) {
            finalGalleryUrls.push(formData.galleryUrls[i]);
          }
        }
      }
    } else {
      finalGalleryUrls.push(...(formData.galleryUrls || []));
    }

    // Convert projectDetails to JSON strings for storage
    const projectDetailsJson = formData.projectDetails.map(detail => 
      JSON.stringify(detail)
    );

    // Use current date if not set
    const publishDate = formData.publishDate || new Date().toISOString().split('T')[0];

    return {
      title: formData.title,
      category: formData.category,
      subcategoryId: formData.subcategoryId || '',
      description: formData.description,
      thumbnailUrl: thumbnailUrl,
      galleryUrls: finalGalleryUrls,
      likes: parseInt(formData.likes) || 0,
      featured: formData.featured,
      fullDescription: formData.fullDescription,
      projectDetails: projectDetailsJson,
      projectLink: formData.projectLink || '',
      customSlug: formData.customSlug,
      useProjectPrefix: formData.useProjectPrefix,
      tags: formData.tags || [],
      publishDate: publishDate,
      published: isPublished !== undefined ? isPublished : projectStatus.published,
      isDraft: isPublished !== undefined ? !isPublished : projectStatus.isDraft,
    };
  };

  // Helper function to save project to database
  const saveProjectToDatabase = async (projectData) => {
    try {
      let result;
      if (projectEditingId && projectEditingId !== 'new') {
        // Update existing project
        result = await databaseService.updateDocument(
          PROJECTS_COLLECTION,
          projectEditingId,
          projectData
        );
      } else {
        // Create new project
        result = await databaseService.createDocument(
          PROJECTS_COLLECTION,
          projectData
        );
      }
      return result;
    } catch (error) {
      console.error('Database save error:', error);
      return { success: false, error: error.message };
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    
    setFormData(prev => ({
      ...prev,
      gallery: [...prev.gallery, ...files],
      galleryUrls: [...prev.galleryUrls, ...newPreviews]
    }));
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => {
      if (prev.galleryUrls[index]?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.galleryUrls[index]);
      }
      return {
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== index),
        galleryUrls: prev.galleryUrls.filter((_, i) => i !== index)
      };
    });
  };

  // Move gallery image left (decrease index)
  const moveGalleryImageLeft = (index) => {
    if (index === 0) return; // Already at the start
    
    setFormData(prev => {
      const newGallery = [...prev.gallery];
      const newGalleryUrls = [...prev.galleryUrls];
      
      // Swap with previous item
      [newGallery[index - 1], newGallery[index]] = [newGallery[index], newGallery[index - 1]];
      [newGalleryUrls[index - 1], newGalleryUrls[index]] = [newGalleryUrls[index], newGalleryUrls[index - 1]];
      
      return {
        ...prev,
        gallery: newGallery,
        galleryUrls: newGalleryUrls
      };
    });
    
    showToast('Image moved left', 'success');
  };

  // Move gallery image right (increase index)
  const moveGalleryImageRight = (index) => {
    if (index === formData.galleryUrls.length - 1) return; // Already at the end
    
    setFormData(prev => {
      const newGallery = [...prev.gallery];
      const newGalleryUrls = [...prev.galleryUrls];
      
      // Swap with next item
      [newGallery[index], newGallery[index + 1]] = [newGallery[index + 1], newGallery[index]];
      [newGalleryUrls[index], newGalleryUrls[index + 1]] = [newGalleryUrls[index], newGalleryUrls[index + 1]];
      
      return {
        ...prev,
        gallery: newGallery,
        galleryUrls: newGalleryUrls
      };
    });
    
    showToast('Image moved right', 'success');
  };

  const addProjectDetail = () => {
    setFormData(prev => ({
      ...prev,
      projectDetails: [...prev.projectDetails, { label: '', value: '' }]
    }));
  };

  const removeProjectDetail = (index) => {
    setFormData(prev => ({
      ...prev,
      projectDetails: prev.projectDetails.filter((_, i) => i !== index)
    }));
  };

  const updateProjectDetail = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      projectDetails: prev.projectDetails.map((detail, i) =>
        i === index ? { ...detail, [field]: value } : detail
      )
    }));
  };

  const handleSave = async () => {
    // If editing an existing project/blog, do a master save to database
    if ((isProjectMode && projectEditingId && projectEditingId !== 'new') || 
        (isBlogMode && blogEditingId && blogEditingId !== 'new')) {
      const success = await handleMasterSave();
      if (success) {
        showToast('All changes saved successfully!', 'success');
      }
    } else {
      // For new projects/blogs, just save to localStorage for preview
      localStorage.setItem('richTextEditorContent', formData.fullDescription || '');
      showToast('Content saved to draft!', 'success');
    }
  };

  // Auto-save to localStorage for preview whenever fullDescription changes
  useEffect(() => {
    if (selectedSection === 'full-description' && formData.fullDescription) {
      localStorage.setItem('richTextEditorContent', formData.fullDescription);
    }
  }, [formData.fullDescription, selectedSection]);

  const handlePreview = () => {
    // Save to localStorage first
    localStorage.setItem('richTextEditorContent', formData.fullDescription || '');
    
    // Save project metadata for preview
    if (isProjectMode) {
      const projectMetadata = {
        title: formData.title || '',
        author: formData.author || '',
        thumbnailUrl: formData.thumbnailUrl || '',
        galleryUrls: formData.galleryUrls || [],
        projectDetails: formData.projectDetails || [],
        projectLink: formData.projectLink || ''
      };
      console.log('📸 Editor - Saving project metadata for preview:', projectMetadata);
      console.log('📸 Editor - Gallery URLs being saved:', formData.galleryUrls);
      localStorage.setItem('richTextEditorProjectMeta', JSON.stringify(projectMetadata));
    } else {
      // Clear project metadata if in blog mode
      localStorage.removeItem('richTextEditorProjectMeta');
    }
    
    // Open preview in new window
    window.open('/admin/rich-text-editor/preview', '_blank');
  };

  // Save and restore selection for link insertion
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0));
    }
  };

  const restoreSelection = () => {
    if (savedSelection && editorRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
      editorRef.current.focus();
    }
  };

  // Validate and fix URL
  const validateUrl = (url) => {
    if (!url) return '';
    let validatedUrl = url.trim();
    if (!/^https?:\/\//i.test(validatedUrl)) {
      validatedUrl = 'https://' + validatedUrl;
    }
    return validatedUrl;
  };

  // Handle Insert Link
  const handleInsertLink = () => {
    if (!linkUrl) return;
    
    const validatedUrl = validateUrl(linkUrl);
    restoreSelection();
    
    if (linkMode === 'selection') {
      // Mode 1: Apply link to selected text
      const selection = window.getSelection();
      const selectedText = selection.toString();
      
      if (selectedText) {
        const link = document.createElement('a');
        link.href = validatedUrl;
        link.textContent = selectedText;
        link.style.color = '#1E8479';
        link.style.textDecoration = 'underline';
        link.style.cursor = 'pointer';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(link);
        
        // Move cursor after link
        range.setStartAfter(link);
        range.setEndAfter(link);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Trigger input event to update preview
        const event = new Event('input', { bubbles: true });
        editorRef.current.dispatchEvent(event);
      }
    } else {
      // Mode 2: Insert new link with title at cursor position
      if (linkTitle && linkUrl) {
        const link = document.createElement('a');
        link.href = validatedUrl;
        link.textContent = linkTitle;
        link.style.color = '#1E8479';
        link.style.textDecoration = 'underline';
        link.style.cursor = 'pointer';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.insertNode(link);
          
          // Add space after link
          const space = document.createTextNode(' ');
          range.setStartAfter(link);
          range.insertNode(space);
          
          // Move cursor after space
          range.setStartAfter(space);
          range.setEndAfter(space);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          editorRef.current?.appendChild(link);
        }
        
        // Trigger input event to update preview
        const event = new Event('input', { bubbles: true });
        editorRef.current.dispatchEvent(event);
      }
    }
    
    setLinkUrl('');
    setLinkTitle('');
    setShowLinkModal(false);
  };

  // Handle Link Button Click
  const handleLinkButtonClick = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    if (selectedText) {
      // Has selection - Mode 1
      saveSelection();
      setLinkMode('selection');
      setLinkUrl('');
      setLinkTitle('');
      setShowLinkModal(true);
    } else {
      // No selection - Mode 2
      saveSelection();
      setLinkMode('insert');
      setLinkUrl('');
      setLinkTitle('');
      setShowLinkModal(true);
    }
  };

  // Extract dimensions from embed code
  const extractDimensions = (code) => {
    const widthMatch = code.match(/width[=:"]\s*["']?(\d+)/i);
    const heightMatch = code.match(/height[=:"]\s*["']?(\d+)/i);
    
    return {
      width: widthMatch ? parseInt(widthMatch[1]) : 560,
      height: heightMatch ? parseInt(heightMatch[1]) : 315
    };
  };

  // Remove width and height attributes from embed code
  const cleanEmbedCode = (code) => {
    return code
      .replace(/\s+width\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\s+height\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\s+style\s*=\s*["'][^"']*width[^"']*["']/gi, '')
      .replace(/\s+style\s*=\s*["'][^"']*height[^"']*["']/gi, '');
  };

  // Handle Insert Embed
  const handleInsertEmbed = () => {
    if (!embedCode.trim()) return;
    
    restoreSelection();
    
    // Extract dimensions before cleaning
    const dimensions = extractDimensions(embedCode);
    
    // Clean the embed code (remove width/height)
    const cleanedCode = cleanEmbedCode(embedCode);
    
    // Create wrapper div for the embed
    const wrapper = document.createElement('div');
    wrapper.className = 'editor-embed-wrapper';
    wrapper.contentEditable = 'false';
    wrapper.style.cssText = `
      display: inline-block;
      width: ${dimensions.width}px;
      height: ${dimensions.height}px;
      margin: 1rem 0;
      border: 1px solid #4C1D95;
      border-radius: 0.5rem;
      overflow: hidden;
      cursor: pointer;
      position: relative;
      vertical-align: top;
      user-select: none;
      -webkit-user-select: none;
    `;
    wrapper.setAttribute('data-embed-code', embedCode);
    wrapper.setAttribute('data-original-width', dimensions.width);
    wrapper.setAttribute('data-original-height', dimensions.height);
    wrapper.setAttribute('tabindex', '-1');
    
    // Create iframe container with proper scaling
    const container = document.createElement('div');
    container.className = 'embed-content';
    container.style.cssText = 'width: 100%; height: 100%; pointer-events: none; position: relative;';
    
    // Parse and update iframe dimensions in the embed code
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanedCode;
    const iframe = tempDiv.querySelector('iframe');
    
    if (iframe) {
      // Set iframe to fill container and DISABLE all pointer events
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';
      iframe.style.pointerEvents = 'none';
      iframe.setAttribute('tabindex', '-1');
      container.appendChild(iframe);
    } else {
      // If not an iframe, just insert the code
      container.innerHTML = cleanedCode;
      // Disable pointer events on all children
      container.querySelectorAll('*').forEach(el => {
        el.style.pointerEvents = 'none';
      });
    }
    
    wrapper.appendChild(container);
    
    // Add a SOLID overlay to completely block ALL video interaction
    const overlay = document.createElement('div');
    overlay.className = 'embed-overlay';
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: transparent;
      cursor: pointer;
      z-index: 1000;
      pointer-events: auto;
    `;
    overlay.title = 'Click to select and resize';
    
    // Add direct click handler to the overlay
    overlay.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedEmbed(wrapper);
    });
    
    wrapper.appendChild(overlay);
    
    // Also add click handler to wrapper itself as backup
    wrapper.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedEmbed(wrapper);
    });
    
    // Wrap with breathing room for better cursor navigation
    const containerWithBreathingRoom = createElementWithBreathingRoom(wrapper);
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.insertNode(containerWithBreathingRoom);
      
      // Move cursor after embed
      range.setStartAfter(containerWithBreathingRoom);
      range.setEndAfter(containerWithBreathingRoom);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current?.appendChild(containerWithBreathingRoom);
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current.dispatchEvent(event);
    
    setEmbedCode('');
    setShowEmbedModal(false);
    setEmbedViewMode('code');
  };

  // Handle Embed Button Click
  const handleEmbedButtonClick = () => {
    saveSelection();
    setEmbedCode('');
    setEmbedViewMode('code');
    setShowEmbedModal(true);
  };

  // Start resizing embed
  const startEmbedResize = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: selectedEmbed.offsetWidth,
      height: selectedEmbed.offsetHeight,
      handle: handle
    };
  };

  // Start resizing image
  const startImageResize = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: selectedImage.offsetWidth,
      height: selectedImage.offsetHeight,
      handle: handle
    };
  };

  // Start resizing video
  const startVideoResize = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: selectedVideo.offsetWidth,
      height: selectedVideo.offsetHeight,
      handle: handle
    };
  };

  // Handle embed selection and resizing
  useEffect(() => {
    const handleEditorClick = (e) => {
      // Check if clicking on a tooltip wrapper
      const tooltipElement = e.target.closest('.editor-tooltip-wrapper');
      if (tooltipElement) {
        e.preventDefault();
        e.stopPropagation();
        handleEditTooltip(tooltipElement);
        return;
      }

      // Check if clicking on a quote wrapper
      const quoteElement = e.target.closest('.editor-quote-wrapper');
      if (quoteElement) {
        e.preventDefault();
        e.stopPropagation();
        handleEditQuote(quoteElement);
        return;
      }

      // Check if clicking on a button wrapper (but not the link itself)
      const buttonElement = e.target.closest('.editor-button-wrapper');
      if (buttonElement && !e.target.matches('a, a *')) {
        e.preventDefault();
        e.stopPropagation();
        handleEditButton(buttonElement);
        return;
      }

      // Check if clicking on a divider wrapper
      const dividerElement = e.target.closest('.editor-divider-wrapper');
      if (dividerElement) {
        e.preventDefault();
        e.stopPropagation();
        handleEditDivider(dividerElement);
        return;
      }

      // Check if clicking on a table wrapper (but not on editable cells)
      const tableElement = e.target.closest('.editor-table-wrapper');
      if (tableElement && !e.target.matches('th[contenteditable], td[contenteditable], th[contenteditable] *, td[contenteditable] *')) {
        e.preventDefault();
        e.stopPropagation();
        const table = tableElement.querySelector('table');
        if (table) handleEditTable(table);
        return;
      }

      // Check if clicking on an FAQ wrapper
      const faqElement = e.target.closest('.editor-faq-wrapper');
      if (faqElement) {
        e.preventDefault();
        e.stopPropagation();
        handleEditFaq(faqElement);
        return;
      }

      // Check if clicking on a social links wrapper (but not on links)
      const socialElement = e.target.closest('.editor-social-wrapper');
      if (socialElement && !e.target.matches('a, a *')) {
        e.preventDefault();
        e.stopPropagation();
        handleEditSocial(socialElement);
        return;
      }

      // Check if clicking on a project navigation wrapper (but not on buttons)
      const projectNavElement = e.target.closest('.editor-project-nav-wrapper');
      if (projectNavElement && !e.target.matches('button, button *')) {
        e.preventDefault();
        e.stopPropagation();
        setEditingProjectNav(projectNavElement);
        setShowProjectNavModal(true);
        return;
      }

      // Check if clicking on an author wrapper
      const authorElement = e.target.closest('.editor-author-wrapper');
      if (authorElement) {
        e.preventDefault();
        e.stopPropagation();
        handleEditAuthor(authorElement);
        return;
      }

      // Check if clicking on a file download wrapper (but not on download button)
      const fileDownloadElement = e.target.closest('.editor-file-download-wrapper');
      if (fileDownloadElement && !e.target.classList.contains('download-btn') && !e.target.closest('.download-btn')) {
        e.preventDefault();
        e.stopPropagation();
        handleEditFileDownload(fileDownloadElement);
        return;
      }

      // Check if clicking on an image wrapper
      const imageElement = e.target.closest('.editor-image-wrapper');
      if (imageElement) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedImage(imageElement);
        return;
      }

      // Check if clicking on an audio wrapper
      const audioElement = e.target.closest('.editor-audio-wrapper');
      if (audioElement) {
        // Don't select if clicking on audio controls
        if (!e.target.closest('.play-pause-btn, .slider, .volume-btn')) {
          e.preventDefault();
          e.stopPropagation();
          setSelectedAudio(audioElement);
        }
        return;
      }

      // Check if clicking on a video wrapper
      const videoElement = e.target.closest('.editor-video-wrapper');
      if (videoElement) {
        // Don't select if clicking on video controls or play overlay
        if (!e.target.closest('.video-controls, .play-overlay')) {
          e.preventDefault();
          e.stopPropagation();
          setSelectedVideo(videoElement);
        }
        return;
      }

      // Check if clicking on a codeblock wrapper
      const codeBlockElement = e.target.closest('.editor-code-block-wrapper');
      if (codeBlockElement) {
        e.preventDefault();
        e.stopPropagation();
        handleEditCodeBlock(codeBlockElement);
        return;
      }

      // Check if clicking on an embed wrapper or overlay
      const embedElement = e.target.closest('.editor-embed-wrapper');
      const isOverlay = e.target.classList.contains('embed-overlay');
      
      if (embedElement || isOverlay) {
        e.preventDefault();
        e.stopPropagation();
        const wrapper = embedElement || e.target.closest('.editor-embed-wrapper');
        setSelectedEmbed(wrapper);
        console.log('Embed selected:', wrapper);
        return;
      }

      // Deselect if clicking elsewhere (and not on resize handles)
      if (!e.target.closest('.resize-handle')) {
        setSelectedEmbed(null);
        setSelectedImage(null);
        setSelectedAudio(null);
        setSelectedVideo(null);
      }
    };

    const handleMouseMove = (e) => {
      if (!isResizing || (!selectedEmbed && !selectedImage && !selectedVideo)) return;
      
      // Throttle using requestAnimationFrame for smooth performance
      if (!resizeStartRef.current.rafId) {
        resizeStartRef.current.rafId = requestAnimationFrame(() => {
          const target = selectedEmbed || selectedImage || selectedVideo;
          if (!target) {
            resizeStartRef.current.rafId = null;
            return;
          }
          
          const deltaX = e.clientX - resizeStartRef.current.x;
          const deltaY = e.clientY - resizeStartRef.current.y;
          const handle = resizeStartRef.current.handle;
          
          let newWidth = resizeStartRef.current.width;
          let newHeight = resizeStartRef.current.height;
          
          // Calculate new dimensions based on handle
          if (handle.includes('e')) newWidth += deltaX;
          if (handle.includes('w')) newWidth -= deltaX;
          if (handle.includes('s')) newHeight += deltaY;
          if (handle.includes('n')) newHeight -= deltaY;
          
          // Maintain aspect ratio for corner handles
          if (handle.length === 2) {
            const aspectRatio = resizeStartRef.current.width / resizeStartRef.current.height;
            if (Math.abs(deltaX) > Math.abs(deltaY)) {
              newHeight = newWidth / aspectRatio;
            } else {
              newWidth = newHeight * aspectRatio;
            }
          }
          
          // Apply minimum size
          newWidth = Math.max(50, newWidth);
          newHeight = Math.max(50, newHeight);
          
          // Use transform for smoother resizing during drag
          const scaleX = newWidth / resizeStartRef.current.width;
          const scaleY = newHeight / resizeStartRef.current.height;
          target.style.transform = `scale(${scaleX}, ${scaleY})`;
          target.style.transformOrigin = 'top left';
          
          // Store final dimensions for mouseup
          resizeStartRef.current.finalWidth = newWidth;
          resizeStartRef.current.finalHeight = newHeight;
          
          resizeStartRef.current.rafId = null;
        });
      }
    };
    
    const handleMouseUp = () => {
      if (isResizing) {
        // Cancel any pending animation frame
        if (resizeStartRef.current.rafId) {
          cancelAnimationFrame(resizeStartRef.current.rafId);
          resizeStartRef.current.rafId = null;
        }
        
        // Apply final dimensions and remove transform
        const target = selectedEmbed || selectedImage || selectedVideo;
        if (target && resizeStartRef.current.finalWidth && resizeStartRef.current.finalHeight) {
          target.style.transform = '';
          target.style.transformOrigin = '';
          target.style.width = `${resizeStartRef.current.finalWidth}px`;
          target.style.height = `${resizeStartRef.current.finalHeight}px`;
          
          // Update child elements
          if (selectedEmbed) {
            const iframe = selectedEmbed.querySelector('iframe');
            if (iframe) {
              iframe.style.width = '100%';
              iframe.style.height = '100%';
            }
            const embedContent = selectedEmbed.querySelector('.embed-content');
            if (embedContent) {
              embedContent.style.width = '100%';
              embedContent.style.height = '100%';
            }
          } else if (selectedImage) {
            const img = selectedImage.querySelector('img');
            if (img) {
              img.style.width = '100%';
              img.style.height = '100%';
            }
          } else if (selectedVideo) {
            const video = selectedVideo.querySelector('.video-element');
            if (video) {
              video.style.width = '100%';
              video.style.height = '100%';
            }
          }
        }
        
        setIsResizing(false);
        // Trigger input event to update preview
        const event = new Event('input', { bubbles: true });
        editorRef.current?.dispatchEvent(event);
      }
    };

    const handleKeyDown = (e) => {
      if ((selectedEmbed || selectedImage || selectedAudio || selectedVideo) && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        if (selectedEmbed) {
          selectedEmbed.remove();
          setSelectedEmbed(null);
        } else if (selectedImage) {
          const alignmentWrapper = selectedImage.closest('.image-alignment-wrapper');
          if (alignmentWrapper) {
            alignmentWrapper.remove();
          } else {
            selectedImage.remove();
          }
          setSelectedImage(null);
        } else if (selectedAudio) {
          const alignmentWrapper = selectedAudio.closest('.audio-alignment-wrapper');
          if (alignmentWrapper) {
            alignmentWrapper.remove();
          } else {
            selectedAudio.remove();
          }
          setSelectedAudio(null);
        } else if (selectedVideo) {
          const alignmentWrapper = selectedVideo.closest('.video-alignment-wrapper');
          if (alignmentWrapper) {
            alignmentWrapper.remove();
          } else {
            selectedVideo.remove();
          }
          setSelectedVideo(null);
        }
        // Trigger input event to update preview
        const event = new Event('input', { bubbles: true });
        editorRef.current?.dispatchEvent(event);
      }
    };
    
    // Add click listener to document to catch all clicks
    document.addEventListener('click', handleEditorClick, true); // Use capture phase
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('click', handleEditorClick, true);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, selectedEmbed]);

  // Codeblock themes
  const CODEBLOCK_THEMES = [
    {
      id: 'dracula',
      name: 'Dracula',
      style: { backgroundColor: '#282a36', color: '#f8f8f2', borderColor: '#6272a4' },
      headerStyle: { backgroundColor: '#21222c', color: '#bd93f9' },
      syntax: {
        '--syntax-comment': '#6272a4', '--syntax-punctuation': '#f8f8f2', '--syntax-number': '#bd93f9',
        '--syntax-string': '#f1fa8c', '--syntax-operator': '#ff79c6', '--syntax-keyword': '#ff79c6',
        '--syntax-function': '#50fa7b', '--syntax-variable': '#8be9fd', '--syntax-tag': '#ff79c6',
        '--syntax-attr-name': '#50fa7b', '--syntax-builtin': '#8be9fd', '--syntax-class-name': '#8be9fd'
      }
    },
    {
      id: 'monokai',
      name: 'Monokai',
      style: { backgroundColor: '#272822', color: '#f8f8f2', borderColor: '#75715e' },
      headerStyle: { backgroundColor: '#1e1f1c', color: '#f92672' },
      syntax: {
        '--syntax-comment': '#75715e', '--syntax-punctuation': '#f8f8f2', '--syntax-number': '#ae81ff',
        '--syntax-string': '#e6db74', '--syntax-operator': '#f92672', '--syntax-keyword': '#f92672',
        '--syntax-function': '#a6e22e', '--syntax-variable': '#fd971f', '--syntax-tag': '#f92672',
        '--syntax-attr-name': '#a6e22e', '--syntax-builtin': '#66d9ef', '--syntax-class-name': '#a6e22e'
      }
    },
    {
      id: 'github-dark',
      name: 'GitHub Dark',
      style: { backgroundColor: '#0d1117', color: '#c9d1d9', borderColor: '#30363d' },
      headerStyle: { backgroundColor: '#161b22', color: '#58a6ff' },
      syntax: {
        '--syntax-comment': '#8b949e', '--syntax-punctuation': '#c9d1d9', '--syntax-number': '#79c0ff',
        '--syntax-string': '#a5d6ff', '--syntax-operator': '#79c0ff', '--syntax-keyword': '#ff7b72',
        '--syntax-function': '#d2a8ff', '--syntax-variable': '#79c0ff', '--syntax-tag': '#7ee787',
        '--syntax-attr-name': '#d2a8ff', '--syntax-builtin': '#79c0ff', '--syntax-class-name': '#ffa657'
      }
    },
    {
      id: 'github-light',
      name: 'GitHub Light',
      style: { backgroundColor: '#ffffff', color: '#24292f', borderColor: '#d0d7de' },
      headerStyle: { backgroundColor: '#f6f8fa', color: '#0969da' },
      syntax: {
        '--syntax-comment': '#6e7781', '--syntax-punctuation': '#24292f', '--syntax-number': '#005cc5',
        '--syntax-string': '#0a3069', '--syntax-operator': '#005cc5', '--syntax-keyword': '#d73a49',
        '--syntax-function': '#6f42c1', '--syntax-variable': '#e36209', '--syntax-tag': '#22863a',
        '--syntax-attr-name': '#6f42c1', '--syntax-builtin': '#005cc5', '--syntax-class-name': '#e36209'
      }
    },
    {
      id: 'solarized-dark',
      name: 'Solarized Dark',
      style: { backgroundColor: '#002b36', color: '#839496', borderColor: '#073642' },
      headerStyle: { backgroundColor: '#073642', color: '#268bd2' },
      syntax: {
        '--syntax-comment': '#586e75', '--syntax-punctuation': '#93a1a1', '--syntax-number': '#2aa198',
        '--syntax-string': '#2aa198', '--syntax-operator': '#859900', '--syntax-keyword': '#859900',
        '--syntax-function': '#268bd2', '--syntax-variable': '#b58900', '--syntax-tag': '#268bd2',
        '--syntax-attr-name': '#93a1a1', '--syntax-builtin': '#b58900', '--syntax-class-name': '#b58900'
      }
    },
    {
      id: 'solarized-light',
      name: 'Solarized Light',
      style: { backgroundColor: '#fdf6e3', color: '#657b83', borderColor: '#eee8d5' },
      headerStyle: { backgroundColor: '#eee8d5', color: '#268bd2' },
      syntax: {
        '--syntax-comment': '#93a1a1', '--syntax-punctuation': '#586e75', '--syntax-number': '#2aa198',
        '--syntax-string': '#2aa198', '--syntax-operator': '#859900', '--syntax-keyword': '#859900',
        '--syntax-function': '#268bd2', '--syntax-variable': '#b58900', '--syntax-tag': '#268bd2',
        '--syntax-attr-name': '#586e75', '--syntax-builtin': '#b58900', '--syntax-class-name': '#b58900'
      }
    },
    {
      id: 'night-owl',
      name: 'Night Owl',
      style: { backgroundColor: '#011627', color: '#d6deeb', borderColor: '#5f7e97' },
      headerStyle: { backgroundColor: '#011627', color: '#82aaff' },
      syntax: {
        '--syntax-comment': '#637777', '--syntax-punctuation': '#c792ea', '--syntax-number': '#f78c6c',
        '--syntax-string': '#ecc48d', '--syntax-operator': '#c792ea', '--syntax-keyword': '#c792ea',
        '--syntax-function': '#82aaff', '--syntax-variable': '#addb67', '--syntax-tag': '#7fdbca',
        '--syntax-attr-name': '#addb67', '--syntax-builtin': '#82aaff', '--syntax-class-name': '#ffcb6b'
      }
    },
    {
      id: 'nord',
      name: 'Nord',
      style: { backgroundColor: '#2e3440', color: '#d8dee9', borderColor: '#4c566a' },
      headerStyle: { backgroundColor: '#3b4252', color: '#88c0d0' },
      syntax: {
        '--syntax-comment': '#4c566a', '--syntax-punctuation': '#eceff4', '--syntax-number': '#b48ead',
        '--syntax-string': '#a3be8c', '--syntax-operator': '#81a1c1', '--syntax-keyword': '#81a1c1',
        '--syntax-function': '#88c0d0', '--syntax-variable': '#d8dee9', '--syntax-tag': '#81a1c1',
        '--syntax-attr-name': '#8fbcbb', '--syntax-builtin': '#88c0d0', '--syntax-class-name': '#8fbcbb'
      }
    },
    {
      id: 'cobalt2',
      name: 'Cobalt2',
      style: { backgroundColor: '#193549', color: '#ffffff', borderColor: '#15232d' },
      headerStyle: { backgroundColor: '#15232d', color: '#ffc600' },
      syntax: {
        '--syntax-comment': '#0088ff', '--syntax-punctuation': '#ffffff', '--syntax-number': '#ff628c',
        '--syntax-string': '#3ad900', '--syntax-operator': '#ff9d00', '--syntax-keyword': '#ff9d00',
        '--syntax-function': '#ffc600', '--syntax-variable': '#ffc600', '--syntax-tag': '#9EFEFF',
        '--syntax-attr-name': '#ffc600', '--syntax-builtin': '#ff9d00', '--syntax-class-name': '#ffc600'
      }
    },
    {
      id: 'shades-of-purple',
      name: 'Shades of Purple',
      style: { backgroundColor: '#2d2b55', color: '#ffffff', borderColor: '#1e1e3f' },
      headerStyle: { backgroundColor: '#1e1e3f', color: '#9EFEFF' },
      syntax: {
        '--syntax-comment': '#b362ff', '--syntax-punctuation': '#ffffff', '--syntax-number': '#ff628c',
        '--syntax-string': '#a5ff90', '--syntax-operator': '#ff9d00', '--syntax-keyword': '#ff9d00',
        '--syntax-function': '#9EFEFF', '--syntax-variable': '#fad000', '--syntax-tag': '#ff9d00',
        '--syntax-attr-name': '#fad000', '--syntax-builtin': '#ff9d00', '--syntax-class-name': '#ffc600'
      }
    },
    {
      id: 'synthwave-84',
      name: 'Synthwave 84',
      style: { backgroundColor: '#2b213a', color: '#ffffff', borderColor: '#241b2f' },
      headerStyle: { backgroundColor: '#241b2f', color: '#ff7edb' },
      syntax: {
        '--syntax-comment': '#6d77b3', '--syntax-punctuation': '#ffffff', '--syntax-number': '#f97e72',
        '--syntax-string': '#ff8b39', '--syntax-operator': '#f97e72', '--syntax-keyword': '#f97e72',
        '--syntax-function': '#36f9f6', '--syntax-variable': '#ff7edb', '--syntax-tag': '#f97e72',
        '--syntax-attr-name': '#ff7edb', '--syntax-builtin': '#36f9f6', '--syntax-class-name': '#ff7edb'
      }
    },
    {
      id: 'atom-one-dark',
      name: 'Atom One Dark',
      style: { backgroundColor: '#282c34', color: '#abb2bf', borderColor: '#21252b' },
      headerStyle: { backgroundColor: '#21252b', color: '#61afef' },
      syntax: {
        '--syntax-comment': '#5c6370', '--syntax-punctuation': '#abb2bf', '--syntax-number': '#d19a66',
        '--syntax-string': '#98c379', '--syntax-operator': '#56b6c2', '--syntax-keyword': '#c678dd',
        '--syntax-function': '#61afef', '--syntax-variable': '#e06c75', '--syntax-tag': '#e06c75',
        '--syntax-attr-name': '#d19a66', '--syntax-builtin': '#56b6c2', '--syntax-class-name': '#e5c07b'
      }
    },
    {
      id: 'material-ocean',
      name: 'Material Ocean',
      style: { backgroundColor: '#0f111a', color: '#8f93a2', borderColor: '#090b10' },
      headerStyle: { backgroundColor: '#090b10', color: '#82aaff' },
      syntax: {
        '--syntax-comment': '#464b5d', '--syntax-punctuation': '#8f93a2', '--syntax-number': '#f78c6c',
        '--syntax-string': '#c3e88d', '--syntax-operator': '#89ddff', '--syntax-keyword': '#c792ea',
        '--syntax-function': '#82aaff', '--syntax-variable': '#f07178', '--syntax-tag': '#f07178',
        '--syntax-attr-name': '#c792ea', '--syntax-builtin': '#82aaff', '--syntax-class-name': '#ffcb6b'
      }
    },
    {
      id: 'andromeda',
      name: 'Andromeda',
      style: { backgroundColor: '#23262e', color: '#d5ced9', borderColor: '#1b1d23' },
      headerStyle: { backgroundColor: '#1b1d23', color: '#00e8c6' },
      syntax: {
        '--syntax-comment': '#a0a1a7', '--syntax-punctuation': '#d5ced9', '--syntax-number': '#f39c12',
        '--syntax-string': '#96e072', '--syntax-operator': '#00e8c6', '--syntax-keyword': '#c74ded',
        '--syntax-function': '#ffe66d', '--syntax-variable': '#00e8c6', '--syntax-tag': '#ff00aa',
        '--syntax-attr-name': '#ffe66d', '--syntax-builtin': '#00e8c6', '--syntax-class-name': '#ffe66d'
      }
    },
    {
      id: 'deep-sea',
      name: 'Deep Sea',
      style: { backgroundColor: '#09141f', color: '#ffffff', borderColor: '#050a10' },
      headerStyle: { backgroundColor: '#050a10', color: '#00fff5' },
      syntax: {
        '--syntax-comment': '#4a5c6a', '--syntax-punctuation': '#ffffff', '--syntax-number': '#ff9f1c',
        '--syntax-string': '#2ec4b6', '--syntax-operator': '#00fff5', '--syntax-keyword': '#ff9f1c',
        '--syntax-function': '#00fff5', '--syntax-variable': '#2ec4b6', '--syntax-tag': '#00fff5',
        '--syntax-attr-name': '#2ec4b6', '--syntax-builtin': '#00fff5', '--syntax-class-name': '#ff9f1c'
      }
    }
  ];

  // Quoteblock themes
  const QUOTE_THEMES = [
    {
      id: 'modern',
      name: 'Modern',
      style: {
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '16px 20px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.5',
        color: '#495057',
        maxWidth: '70%',
        width: '70%',
        position: 'relative',
        whiteSpace: 'pre-wrap'
      },
      authorStyle: {
        marginTop: '8px',
        fontSize: '12px',
        color: '#6c757d',
        fontWeight: '500',
        textAlign: 'right'
      }
    },
    {
      id: 'classic',
      name: 'Classic',
      style: {
        backgroundColor: '#fffbf0',
        border: '1px solid #20b2aa',
        borderRadius: '8px',
        padding: '16px 20px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.5',
        color: '#8b4513',
        fontStyle: 'italic',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap'
      },
      authorStyle: {
        marginTop: '8px',
        fontSize: '12px',
        color: '#8b4513',
        fontWeight: '500',
        textAlign: 'right',
        fontStyle: 'normal'
      }
    },
    {
      id: 'vibrant',
      name: 'Vibrant',
      style: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.5',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap'
      },
      authorStyle: {
        marginTop: '8px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.8)',
        fontWeight: '500',
        textAlign: 'right'
      }
    },
    {
      id: 'minimal',
      name: 'Minimal',
      style: {
        backgroundColor: 'transparent',
        border: 'none',
        borderLeft: '3px solid #dee2e6',
        padding: '12px 0 12px 16px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.5',
        color: '#495057',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap'
      },
      authorStyle: {
        marginTop: '8px',
        fontSize: '12px',
        color: '#6c757d',
        fontWeight: '500',
        textAlign: 'right'
      }
    },
    {
      id: 'code-like',
      name: 'Code-like',
      style: {
        backgroundColor: '#2d3748',
        color: '#4fd1c7',
        borderRadius: '8px',
        padding: '16px 20px',
        margin: '16px auto',
        fontSize: '13px',
        lineHeight: '1.4',
        fontFamily: 'Monaco, Consolas, monospace',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap'
      },
      authorStyle: {
        marginTop: '8px',
        fontSize: '11px',
        color: '#4fd1c7',
        fontWeight: '500',
        textAlign: 'right',
        fontFamily: 'Monaco, Consolas, monospace'
      }
    },
    {
      id: 'radio',
      name: 'Radio',
      style: {
        backgroundColor: '#1a1a1a',
        color: '#00ff41',
        border: '2px solid #00ff41',
        borderRadius: '15px',
        padding: '18px 24px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.6',
        fontFamily: 'Courier New, monospace',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap',
        boxShadow: '0 0 20px rgba(0, 255, 65, 0.3), inset 0 0 20px rgba(0, 255, 65, 0.1)',
        position: 'relative'
      },
      authorStyle: {
        marginTop: '12px',
        fontSize: '12px',
        color: '#00ff41',
        fontWeight: '600',
        textAlign: 'right',
        fontFamily: 'Courier New, monospace',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      beforeContent: '📻',
      beforeStyle: {
        position: 'absolute',
        top: '8px',
        left: '12px',
        fontSize: '16px',
        opacity: '0.7'
      }
    },
    {
      id: 'vintage',
      name: 'Vintage',
      style: {
        backgroundColor: '#f4f1e8',
        color: '#5d4e37',
        border: '3px double #8b7355',
        borderRadius: '0',
        padding: '20px 24px',
        margin: '16px auto',
        fontSize: '15px',
        lineHeight: '1.7',
        fontFamily: 'Georgia, serif',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap',
        position: 'relative',
        boxShadow: 'inset 0 0 0 1px #d4c4a8, 0 2px 8px rgba(139, 115, 85, 0.2)'
      },
      authorStyle: {
        marginTop: '12px',
        fontSize: '13px',
        color: '#8b7355',
        fontWeight: '400',
        textAlign: 'center',
        fontFamily: 'Georgia, serif',
        fontStyle: 'italic',
        borderTop: '1px solid #d4c4a8',
        paddingTop: '8px'
      },
      beforeContent: '❝',
      beforeStyle: {
        position: 'absolute',
        top: '4px',
        left: '8px',
        fontSize: '32px',
        color: '#8b7355',
        opacity: '0.4',
        fontFamily: 'Georgia, serif'
      }
    },
    {
      id: 'neon',
      name: 'Neon',
      style: {
        backgroundColor: '#0a0a0a',
        color: '#ff006e',
        border: '1px solid #ff006e',
        borderRadius: '20px',
        padding: '18px 22px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.5',
        fontFamily: 'Arial, sans-serif',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap',
        boxShadow: '0 0 30px rgba(255, 0, 110, 0.5), inset 0 0 30px rgba(255, 0, 110, 0.1)',
        textShadow: '0 0 10px rgba(255, 0, 110, 0.8)',
        position: 'relative'
      },
      authorStyle: {
        marginTop: '10px',
        fontSize: '12px',
        color: '#00f5ff',
        fontWeight: '600',
        textAlign: 'right',
        fontFamily: 'Arial, sans-serif',
        textShadow: '0 0 8px rgba(0, 245, 255, 0.8)'
      }
    },
    {
      id: 'scifi',
      name: 'Sci-Fi',
      style: {
        background: 'linear-gradient(135deg, #0c1445 0%, #1e3c72 50%, #2a5298 100%)',
        color: '#00d4ff',
        border: '1px solid #00d4ff',
        borderRadius: '0 15px 0 15px',
        padding: '16px 20px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.5',
        fontFamily: 'Orbitron, monospace',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap',
        position: 'relative',
        boxShadow: '0 0 25px rgba(0, 212, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
      },
      authorStyle: {
        marginTop: '10px',
        fontSize: '11px',
        color: '#7dd3fc',
        fontWeight: '500',
        textAlign: 'right',
        fontFamily: 'Orbitron, monospace',
        textTransform: 'uppercase',
        letterSpacing: '2px'
      },
      beforeContent: '▶',
      beforeStyle: {
        position: 'absolute',
        top: '12px',
        left: '12px',
        fontSize: '12px',
        color: '#00d4ff',
        opacity: '0.8'
      }
    },
    {
      id: 'paper',
      name: 'Paper',
      style: {
        backgroundColor: '#fefefe',
        color: '#2c3e50',
        border: '1px solid #bdc3c7',
        borderRadius: '3px',
        padding: '18px 22px',
        margin: '16px auto',
        fontSize: '14px',
        lineHeight: '1.6',
        fontFamily: 'Times New Roman, serif',
        maxWidth: '70%',
        width: '70%',
        whiteSpace: 'pre-wrap',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
        position: 'relative'
      },
      authorStyle: {
        marginTop: '12px',
        fontSize: '13px',
        color: '#7f8c8d',
        fontWeight: '400',
        textAlign: 'right',
        fontFamily: 'Times New Roman, serif',
        borderTop: '1px solid #ecf0f1',
        paddingTop: '8px'
      }
    }
  ];

  // Button themes
  const BUTTON_THEMES = [
    {
      id: 'primary',
      name: 'Primary',
      style: {
        backgroundColor: '#105652',
        color: 'white',
        padding: '12px 24px',
        borderRadius: '8px',
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-block',
        transition: 'all 0.3s ease',
        margin: '0.5rem 0'
      },
      hoverStyle: {
        backgroundColor: '#0d4a47',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(16, 86, 82, 0.3)'
      }
    },
    {
      id: 'outline',
      name: 'Outline',
      style: {
        backgroundColor: 'transparent',
        color: '#105652',
        padding: '12px 24px',
        borderRadius: '8px',
        border: '2px solid #105652',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-block',
        transition: 'all 0.3s ease',
        margin: '0.5rem 0'
      },
      hoverStyle: {
        backgroundColor: '#105652',
        color: 'white'
      }
    },
    {
      id: 'gradient',
      name: 'Gradient',
      style: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '14px 28px',
        borderRadius: '25px',
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-block',
        transition: 'all 0.3s ease',
        margin: '0.5rem 0',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
      },
      hoverStyle: {
        transform: 'translateY(-3px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
      }
    },
    {
      id: 'minimal',
      name: 'Minimal',
      style: {
        backgroundColor: 'transparent',
        color: '#333',
        padding: '8px 16px',
        borderRadius: '4px',
        border: '1px solid #ddd',
        fontWeight: '500',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-block',
        transition: 'all 0.3s ease',
        margin: '0.5rem 0'
      },
      hoverStyle: {
        backgroundColor: '#f8f9fa',
        borderColor: '#adb5bd'
      }
    },
    {
      id: 'rounded',
      name: 'Rounded',
      style: {
        backgroundColor: '#1E8479',
        color: 'white',
        padding: '12px 24px',
        borderRadius: '50px',
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-block',
        transition: 'all 0.3s ease',
        margin: '0.5rem 0',
        boxShadow: '0 2px 10px rgba(30, 132, 121, 0.3)'
      },
      hoverStyle: {
        backgroundColor: '#196b61',
        transform: 'scale(1.05)'
      }
    }
  ];

  // Divider themes
  const DIVIDER_THEMES = [
    {
      id: 'simple',
      name: 'Simple Line',
      style: {
        height: '1px',
        backgroundColor: '#dee2e6',
        border: 'none',
        margin: '2rem 0'
      }
    },
    {
      id: 'thick',
      name: 'Thick Line',
      style: {
        height: '3px',
        backgroundColor: '#105652',
        border: 'none',
        margin: '2rem 0',
        borderRadius: '2px'
      }
    },
    {
      id: 'dashed',
      name: 'Dashed',
      style: {
        height: '0',
        border: 'none',
        borderTop: '2px dashed #adb5bd',
        margin: '2rem 0'
      }
    },
    {
      id: 'dotted',
      name: 'Dotted',
      style: {
        height: '0',
        border: 'none',
        borderTop: '3px dotted #105652',
        margin: '2rem 0'
      }
    },
    {
      id: 'gradient',
      name: 'Gradient',
      style: {
        height: '2px',
        background: 'linear-gradient(90deg, transparent, #105652, transparent)',
        border: 'none',
        margin: '2rem 0'
      }
    },
    {
      id: 'decorative',
      name: 'Decorative',
      style: {
        height: '0',
        border: 'none',
        textAlign: 'center',
        margin: '2rem 0',
        position: 'relative'
      },
      content: '◆ ◆ ◆',
      contentStyle: {
        color: '#105652',
        fontSize: '1.2rem',
        backgroundColor: 'white',
        padding: '0 1rem'
      }
    }
  ];

  // Table themes
  const TABLE_THEMES = [
    {
      id: 'default',
      name: 'Default',
      icon: '📋',
      tableStyle: {
        width: '100%',
        borderCollapse: 'collapse',
        margin: '1rem 0',
        fontSize: '0.9rem'
      },
      thStyle: {
        backgroundColor: '#f8f9fa',
        color: '#495057',
        fontWeight: '700',
        padding: '12px',
        textAlign: 'left',
        borderBottom: '2px solid #dee2e6'
      },
      tdStyle: {
        padding: '12px',
        borderBottom: '1px solid #dee2e6'
      }
    },
    {
      id: 'striped',
      name: 'Striped',
      icon: '📊',
      tableStyle: {
        width: '100%',
        borderCollapse: 'collapse',
        margin: '1rem 0',
        fontSize: '0.9rem'
      },
      thStyle: {
        backgroundColor: '#105652',
        color: 'white',
        fontWeight: '700',
        padding: '12px',
        textAlign: 'left'
      },
      tdStyle: {
        padding: '12px',
        borderBottom: '1px solid #dee2e6'
      },
      evenRowStyle: {
        backgroundColor: '#f8f9fa'
      }
    },
    {
      id: 'bordered',
      name: 'Bordered',
      icon: '🔲',
      tableStyle: {
        width: '100%',
        borderCollapse: 'collapse',
        margin: '1rem 0',
        fontSize: '0.9rem',
        border: '1px solid #dee2e6'
      },
      thStyle: {
        backgroundColor: '#e9ecef',
        color: '#495057',
        fontWeight: '700',
        padding: '12px',
        textAlign: 'left',
        border: '1px solid #dee2e6'
      },
      tdStyle: {
        padding: '12px',
        border: '1px solid #dee2e6'
      }
    },
    {
      id: 'minimal',
      name: 'Minimal',
      icon: '📝',
      tableStyle: {
        width: '100%',
        borderCollapse: 'collapse',
        margin: '1rem 0',
        fontSize: '0.9rem'
      },
      thStyle: {
        color: '#495057',
        fontWeight: '700',
        padding: '12px 8px',
        textAlign: 'left',
        borderBottom: '1px solid #105652'
      },
      tdStyle: {
        padding: '12px 8px'
      }
    },
    {
      id: 'modern',
      name: 'Modern',
      icon: '✨',
      tableStyle: {
        width: '100%',
        borderCollapse: 'collapse',
        margin: '1rem 0',
        fontSize: '0.9rem',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      },
      thStyle: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontWeight: '700',
        padding: '16px',
        textAlign: 'left'
      },
      tdStyle: {
        padding: '16px',
        backgroundColor: 'white'
      },
      evenRowStyle: {
        backgroundColor: '#f8f9fa'
      }
    }
  ];

  // FAQ themes
  const FAQ_THEMES = [
    {
      id: 'minimal',
      name: 'Minimal',
      containerStyle: {
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        margin: '1rem 0',
        backgroundColor: 'white',
        overflow: 'hidden'
      },
      questionStyle: {
        padding: '16px 20px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#374151',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s',
        position: 'relative'
      },
      questionHoverStyle: {
        backgroundColor: '#f9fafb'
      },
      answerStyle: {
        padding: '0 20px 16px 20px',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#6b7280',
        borderTop: '1px solid #f3f4f6'
      },
      iconStyle: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#105652',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
        transition: 'transform 0.2s'
      }
    },
    {
      id: 'clean',
      name: 'Clean',
      containerStyle: {
        border: 'none',
        borderBottom: '1px solid #e5e7eb',
        margin: '0',
        backgroundColor: 'transparent',
        overflow: 'hidden'
      },
      questionStyle: {
        padding: '14px 0',
        fontSize: '15px',
        fontWeight: '600',
        color: '#1f2937',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'color 0.2s'
      },
      questionHoverStyle: {
        color: '#4C1D95'
      },
      answerStyle: {
        padding: '0 0 14px 0',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#6b7280'
      },
      iconStyle: {
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        border: '2px solid #d1d5db',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
        transition: 'all 0.2s',
        backgroundColor: 'transparent'
      }
    },
    {
      id: 'simple',
      name: 'Simple',
      containerStyle: {
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        margin: '0.5rem 0',
        backgroundColor: '#fafafa',
        overflow: 'hidden'
      },
      questionStyle: {
        padding: '14px 18px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#374151',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'background-color 0.2s',
        backgroundColor: 'white'
      },
      questionHoverStyle: {
        backgroundColor: '#f9fafb'
      },
      answerStyle: {
        padding: '0 18px 14px 18px',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#6b7280',
        backgroundColor: 'white'
      },
      iconStyle: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        border: '2px solid #d1d5db',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        fontWeight: 'bold',
        transition: 'all 0.2s',
        backgroundColor: 'white'
      }
    },
    {
      id: 'modern',
      name: 'Modern',
      containerStyle: {
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        margin: '0.75rem 0',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden'
      },
      questionStyle: {
        padding: '16px 20px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#1f2937',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s'
      },
      questionHoverStyle: {
        backgroundColor: '#f9fafb'
      },
      answerStyle: {
        padding: '0 20px 16px 20px',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#6b7280',
        borderTop: '1px solid #f3f4f6'
      },
      iconStyle: {
        width: '22px',
        height: '22px',
        borderRadius: '6px',
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
        transition: 'all 0.2s'
      }
    },
    {
      id: 'card',
      name: 'Card',
      containerStyle: {
        backgroundColor: 'white',
        borderRadius: '8px',
        margin: '0.75rem 0',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden'
      },
      questionStyle: {
        padding: '16px 20px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#1f2937',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
      },
      questionHoverStyle: {
        backgroundColor: '#f1f5f9',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)'
      },
      answerStyle: {
        padding: '0 24px 24px 24px',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#6b7280'
      },
      iconStyle: {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: '#4C1D95',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: 'bold',
        transition: 'all 0.3s',
        boxShadow: '0 2px 8px rgba(76, 29, 149, 0.3)'
      }
    },
    {
      id: 'gradient',
      name: 'Gradient',
      containerStyle: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        margin: '1rem 0',
        padding: '2px',
        overflow: 'hidden'
      },
      questionStyle: {
        padding: '18px 24px',
        fontSize: '16px',
        fontWeight: '600',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s',
        backgroundColor: 'transparent',
        borderRadius: '14px'
      },
      questionHoverStyle: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        transform: 'scale(1.02)'
      },
      answerStyle: {
        padding: '0 24px 20px 24px',
        fontSize: '14px',
        lineHeight: '1.7',
        color: 'rgba(255, 255, 255, 0.9)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        margin: '0 2px 2px 2px',
        borderRadius: '0 0 14px 14px'
      },
      iconStyle: {
        width: '26px',
        height: '26px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'all 0.3s',
        border: '1px solid rgba(255, 255, 255, 0.3)'
      }
    },
    {
      id: 'outlined',
      name: 'Outlined',
      containerStyle: {
        border: '2px solid #e5e7eb',
        borderRadius: '10px',
        margin: '1rem 0',
        backgroundColor: 'transparent',
        overflow: 'hidden'
      },
      questionStyle: {
        padding: '16px 20px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#374151',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s',
        borderLeft: '4px solid #105652'
      },
      questionHoverStyle: {
        backgroundColor: '#f9fafb',
        borderLeftColor: '#1E8479',
        paddingLeft: '24px'
      },
      answerStyle: {
        padding: '0 20px 16px 24px',
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#6b7280',
        borderTop: '1px solid #f3f4f6',
        backgroundColor: '#fafafa'
      },
      iconStyle: {
        width: '22px',
        height: '22px',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: '#105652',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        fontWeight: 'bold',
        transition: 'all 0.2s',
        border: '2px solid #105652'
      }
    }
  ];

  // Load social media from database
  const loadSocialMedias = async () => {
    try {
      setLoadingSocialMedias(true);
      const result = await databaseService.listDocuments('social_media');
      if (result.success) {
        // Only get active social media
        const activeSocialMedias = result.data.documents.filter(sm => sm.isActive !== false);
        setAvailableSocialMedias(activeSocialMedias);
      }
    } catch (error) {
      console.error('Error loading social media:', error);
      setAvailableSocialMedias([]);
    } finally {
      setLoadingSocialMedias(false);
    }
  };

  // Load authors from database
  const loadAuthors = async () => {
    try {
      setLoadingAuthors(true);
      const result = await databaseService.listDocuments('authors');
      if (result.success) {
        setAvailableAuthors(result.data.documents);
      }
    } catch (error) {
      console.error('Error loading authors:', error);
      setAvailableAuthors([]);
    } finally {
      setLoadingAuthors(false);
    }
  };

  // Handle Edit Tooltip
  const handleEditTooltip = (tooltipElement) => {
    const tooltipText = tooltipElement.getAttribute('data-tooltip') || '';
    const selectedText = tooltipElement.textContent || '';
    
    setTooltipText(tooltipText);
    setTooltipSelection(selectedText);
    setEditingTooltip(tooltipElement);
    setShowTooltipModal(true);
  };

  // Handle Tooltip Button Click
  const handleTooltipButtonClick = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    
    if (!selectedText) {
      alert('Please select some text first to add a tooltip.');
      return;
    }
    
    saveSelection();
    setTooltipSelection(selectedText);
    setTooltipText('');
    setEditingTooltip(null);
    setShowTooltipModal(true);
  };

  // Handle Save Tooltip
  const handleSaveTooltip = () => {
    if (!tooltipText.trim() || !tooltipSelection) return;
    
    if (editingTooltip) {
      // Update existing tooltip
      editingTooltip.setAttribute('data-tooltip', tooltipText);
      
      // Update the tooltip element inside
      const tooltipElement = editingTooltip.querySelector('.editor-tooltip');
      if (tooltipElement) {
        tooltipElement.textContent = tooltipText;
      } else if (editingTooltip._tooltipElement) {
        // Handle case where tooltip is stored as reference
        editingTooltip._tooltipElement.textContent = tooltipText;
      }
      
      // Trigger input event to update preview
      const event = new Event('input', { bubbles: true });
      editorRef.current?.dispatchEvent(event);
      
      setEditingTooltip(null);
    } else {
      // Create new tooltip
      restoreSelection();
      
      // Create tooltip wrapper
      const tooltipWrapper = document.createElement('span');
      tooltipWrapper.className = 'editor-tooltip-wrapper';
      tooltipWrapper.style.cssText = `
        position: relative;
        cursor: pointer;
        display: inline;
        color: #105652;
        border-bottom: 2px solid #105652;
        text-decoration: none;
        transition: all 0.2s;
      `;
      tooltipWrapper.setAttribute('data-tooltip', tooltipText);
      
      // Create the tooltip element
      const tooltip = document.createElement('span');
      tooltip.className = 'editor-tooltip';
      tooltip.textContent = tooltipText;
      tooltip.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        padding: 0.5em 0.8em;
        opacity: 0;
        pointer-events: none;
        transition: all 0.3s;
        color: #fff;
        background: #105652;
        border-radius: 0.375em;
        font-size: 14px;
        z-index: 1000;
        visibility: hidden;
        max-width: 280px;
        width: max-content;
        white-space: normal;
        word-wrap: break-word;
        text-align: center;
        line-height: 1.4;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      `;
      
      // Create tooltip arrow
      const arrow = document.createElement('span');
      arrow.className = 'editor-tooltip-arrow';
      arrow.style.cssText = `
        position: absolute;
        content: "";
        height: 0.6em;
        width: 0.6em;
        bottom: -0.2em;
        left: 50%;
        transform: translate(-50%, 0) rotate(45deg);
        background: #105652;
      `;
      tooltip.appendChild(arrow);
      
      // Position tooltip function
      const positionTooltip = () => {
        const wrapperRect = tooltipWrapper.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate ideal position (centered above the text)
        let left = wrapperRect.left + (wrapperRect.width / 2) - (tooltipRect.width / 2);
        let top = wrapperRect.top - tooltipRect.height - 10;
        
        // Adjust horizontal position to prevent overflow
        const padding = 10;
        if (left < padding) {
          left = padding;
        } else if (left + tooltipRect.width > viewportWidth - padding) {
          left = viewportWidth - tooltipRect.width - padding;
        }
        
        // Adjust vertical position if tooltip would go above viewport
        if (top < padding) {
          top = wrapperRect.bottom + 10;
          // Move arrow to top for bottom positioning
          arrow.style.top = '-0.2em';
          arrow.style.bottom = 'auto';
          arrow.style.transform = 'translate(-50%, 0) rotate(45deg)';
        } else {
          // Reset arrow to bottom for top positioning
          arrow.style.top = 'auto';
          arrow.style.bottom = '-0.2em';
          arrow.style.transform = 'translate(-50%, 0) rotate(45deg)';
        }
        
        // Calculate arrow position relative to tooltip
        const arrowLeft = ((wrapperRect.left + wrapperRect.width / 2) - left) / tooltipRect.width * 100;
        arrow.style.left = Math.max(10, Math.min(90, arrowLeft)) + '%';
        
        tooltip.style.left = left + 'px';
        tooltip.style.top = top + 'px';
      };
      
      // Add hover functionality with positioning
      tooltipWrapper.addEventListener('mouseenter', () => {
        // Temporarily show tooltip to measure it
        tooltip.style.visibility = 'visible';
        tooltip.style.opacity = '0';
        document.body.appendChild(tooltip);
        
        // Position the tooltip
        positionTooltip();
        
        // Show tooltip with animation
        tooltip.style.opacity = '1';
        tooltip.style.pointerEvents = 'auto';
      });
      
      tooltipWrapper.addEventListener('mouseleave', () => {
        tooltip.style.opacity = '0';
        tooltip.style.visibility = 'hidden';
        tooltip.style.pointerEvents = 'none';
        // Remove from body after animation
        setTimeout(() => {
          if (tooltip.parentNode === document.body) {
            document.body.removeChild(tooltip);
          }
        }, 300);
      });
      
      // Add click functionality for editing
      tooltipWrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleEditTooltip(tooltipWrapper);
      });
      
      // Set the selected text as content
      tooltipWrapper.textContent = tooltipSelection;
      
      // Store tooltip element reference on wrapper for cleanup
      tooltipWrapper._tooltipElement = tooltip;
      
      // Replace the selected text with the tooltip wrapper
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(tooltipWrapper);
        
        // Move cursor after tooltip
        range.setStartAfter(tooltipWrapper);
        range.setEndAfter(tooltipWrapper);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Trigger input event to update preview
      const event = new Event('input', { bubbles: true });
      editorRef.current?.dispatchEvent(event);
    }
    
    setTooltipText('');
    setTooltipSelection(null);
    setShowTooltipModal(false);
  };

  // Handle Edit Codeblock
  const handleEditCodeBlock = (codeBlockElement) => {
    const codeContent = codeBlockElement.getAttribute('data-code') || '';
    const themeId = codeBlockElement.getAttribute('data-theme-id') || 'dracula';
    const language = codeBlockElement.getAttribute('data-language') || 'javascript';
    const fileName = codeBlockElement.getAttribute('data-filename') || '';
    const isWrapped = codeBlockElement.getAttribute('data-is-wrapped') === 'true';
    
    setCodeBlockContent(codeContent);
    setCodeBlockTheme(themeId);
    setCodeBlockLanguage(language);
    setCodeBlockFileName(fileName);
    setCodeBlockWrapped(isWrapped);
    setEditingCodeBlock(codeBlockElement);
    setShowCodeBlockModal(true);
  };

  // Handle Quoteblock Button Click
  const handleQuoteButtonClick = () => {
    saveSelection();
    setQuoteText('');
    setQuoteAuthor('');
    setQuoteTheme('classic');
    setEditingQuote(null);
    setShowQuoteModal(true);
  };

  // Handle Edit Quote
  const handleEditQuote = (quoteElement) => {
    const quoteText = quoteElement.getAttribute('data-quote') || '';
    const quoteAuthor = quoteElement.getAttribute('data-author') || '';
    const quoteTheme = quoteElement.getAttribute('data-theme') || 'classic';
    
    setQuoteText(quoteText);
    setQuoteAuthor(quoteAuthor);
    setQuoteTheme(quoteTheme);
    setEditingQuote(quoteElement);
    setShowQuoteModal(true);
  };

  // Handle Save Quote
  const handleSaveQuote = () => {
    if (!quoteText.trim()) return;
    
    const selectedTheme = QUOTE_THEMES.find(t => t.id === quoteTheme) || QUOTE_THEMES[0];
    
    if (editingQuote) {
      // Update existing quote
      editingQuote.setAttribute('data-quote', quoteText);
      editingQuote.setAttribute('data-author', quoteAuthor);
      editingQuote.setAttribute('data-theme', quoteTheme);
      
      // Update styles - clear all background properties first
      editingQuote.style.background = '';
      editingQuote.style.backgroundColor = '';
      editingQuote.style.border = '';
      editingQuote.style.borderLeft = '';
      editingQuote.style.borderRadius = '';
      editingQuote.style.padding = '';
      editingQuote.style.margin = '';
      editingQuote.style.fontSize = '';
      editingQuote.style.lineHeight = '';
      editingQuote.style.color = '';
      editingQuote.style.fontStyle = '';
      editingQuote.style.fontFamily = '';
      editingQuote.style.maxWidth = '';
      editingQuote.style.width = '';
      editingQuote.style.whiteSpace = '';
      editingQuote.style.boxShadow = '';
      editingQuote.style.textShadow = '';
      editingQuote.style.position = '';
      editingQuote.style.textTransform = '';
      editingQuote.style.letterSpacing = '';
      
      // Apply new theme styles
      Object.assign(editingQuote.style, selectedTheme.style);
      
      // Update content
      const quoteTextElement = editingQuote.querySelector('.quote-text');
      const quoteAuthorElement = editingQuote.querySelector('.quote-author');
      
      if (quoteTextElement) {
        quoteTextElement.textContent = quoteText;
        quoteTextElement.style.whiteSpace = 'pre-wrap';
      }
      
      if (quoteAuthorElement) {
        if (quoteAuthor.trim()) {
          quoteAuthorElement.textContent = `— ${quoteAuthor}`;
          quoteAuthorElement.style.display = 'block';
          
          // Clear existing author styles first
          quoteAuthorElement.style.marginTop = '';
          quoteAuthorElement.style.fontSize = '';
          quoteAuthorElement.style.color = '';
          quoteAuthorElement.style.fontWeight = '';
          quoteAuthorElement.style.textAlign = '';
          quoteAuthorElement.style.fontStyle = '';
          quoteAuthorElement.style.fontFamily = '';
          quoteAuthorElement.style.borderTop = '';
          quoteAuthorElement.style.paddingTop = '';
          
          // Apply new author styles
          Object.assign(quoteAuthorElement.style, selectedTheme.authorStyle);
        } else {
          quoteAuthorElement.style.display = 'none';
        }
      }
      
      // Handle before content for themes that have it
      if (selectedTheme.beforeContent) {
        let beforeElement = editingQuote.querySelector('.quote-before');
        if (!beforeElement) {
          beforeElement = document.createElement('span');
          beforeElement.className = 'quote-before';
          editingQuote.insertBefore(beforeElement, editingQuote.firstChild);
        }
        beforeElement.textContent = selectedTheme.beforeContent;
        Object.assign(beforeElement.style, selectedTheme.beforeStyle);
      } else {
        const beforeElement = editingQuote.querySelector('.quote-before');
        if (beforeElement) beforeElement.remove();
      }
      
      setEditingQuote(null);
    } else {
      // Create new quote
      restoreSelection();
      
      const quoteWrapper = document.createElement('blockquote');
      quoteWrapper.className = 'editor-quote-wrapper';
      quoteWrapper.contentEditable = 'false';
      quoteWrapper.setAttribute('data-quote', quoteText);
      quoteWrapper.setAttribute('data-author', quoteAuthor);
      quoteWrapper.setAttribute('data-theme', quoteTheme);
      
      // Apply theme styles
      Object.assign(quoteWrapper.style, selectedTheme.style);
      
      // Add before content for themes that have it (radio, vintage, sci-fi)
      if (selectedTheme.beforeContent) {
        const beforeElement = document.createElement('span');
        beforeElement.className = 'quote-before';
        beforeElement.textContent = selectedTheme.beforeContent;
        Object.assign(beforeElement.style, selectedTheme.beforeStyle);
        quoteWrapper.appendChild(beforeElement);
      }
      
      // Add quote text
      const quoteTextElement = document.createElement('div');
      quoteTextElement.className = 'quote-text';
      quoteTextElement.style.whiteSpace = 'pre-wrap';
      quoteTextElement.textContent = quoteText;
      quoteWrapper.appendChild(quoteTextElement);
      
      // Add author if provided
      if (quoteAuthor.trim()) {
        const quoteAuthorElement = document.createElement('div');
        quoteAuthorElement.className = 'quote-author';
        quoteAuthorElement.textContent = `— ${quoteAuthor}`;
        Object.assign(quoteAuthorElement.style, selectedTheme.authorStyle);
        quoteWrapper.appendChild(quoteAuthorElement);
      }
      
      // Add click handler for editing
      quoteWrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleEditQuote(quoteWrapper);
      });
      
      // Make sure the wrapper is clickable
      quoteWrapper.style.cursor = 'pointer';
      quoteWrapper.title = 'Click to edit quote';
      
      // Wrap with breathing room for better cursor navigation
      const containerWithBreathingRoom = createElementWithBreathingRoom(quoteWrapper);
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(containerWithBreathingRoom);
        
        // Move cursor after quote
        range.setStartAfter(containerWithBreathingRoom);
        range.setEndAfter(containerWithBreathingRoom);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current?.appendChild(containerWithBreathingRoom);
      }
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setQuoteText('');
    setQuoteAuthor('');
    setShowQuoteModal(false);
  };

  // Handle Button Click
  const handleButtonButtonClick = () => {
    saveSelection();
    setButtonText('');
    setButtonUrl('');
    setButtonTheme('primary');
    setButtonAlignment('center');
    setEditingButton(null);
    setShowButtonModal(true);
  };

  // Handle Edit Button
  const handleEditButton = (buttonElement) => {
    const buttonText = buttonElement.getAttribute('data-text') || '';
    const buttonUrl = buttonElement.getAttribute('data-url') || '';
    const buttonTheme = buttonElement.getAttribute('data-theme') || 'primary';
    const buttonAlignment = buttonElement.getAttribute('data-alignment') || 'center';
    
    setButtonText(buttonText);
    setButtonUrl(buttonUrl);
    setButtonTheme(buttonTheme);
    setButtonAlignment(buttonAlignment);
    setEditingButton(buttonElement);
    setShowButtonModal(true);
  };

  // Handle Save Button
  const handleSaveButton = () => {
    if (!buttonText.trim() || !buttonUrl.trim()) return;
    
    const selectedTheme = BUTTON_THEMES.find(t => t.id === buttonTheme) || BUTTON_THEMES[0];
    
    // Get alignment style
    const getAlignmentStyle = (alignment) => {
      switch (alignment) {
        case 'left': return 'text-align: left;';
        case 'right': return 'text-align: right;';
        case 'center':
        default: return 'text-align: center;';
      }
    };
    
    if (editingButton) {
      // Update existing button
      editingButton.setAttribute('data-text', buttonText);
      editingButton.setAttribute('data-url', buttonUrl);
      editingButton.setAttribute('data-theme', buttonTheme);
      editingButton.setAttribute('data-alignment', buttonAlignment);
      
      // Update wrapper alignment
      editingButton.style.cssText = `${getAlignmentStyle(buttonAlignment)} margin: 1rem 0; cursor: pointer;`;
      
      const buttonLink = editingButton.querySelector('a');
      if (buttonLink) {
        buttonLink.textContent = buttonText;
        buttonLink.href = buttonUrl;
        Object.assign(buttonLink.style, selectedTheme.style);
      }
      
      setEditingButton(null);
    } else {
      // Create new button
      restoreSelection();
      
      const buttonWrapper = document.createElement('div');
      buttonWrapper.className = 'editor-button-wrapper';
      buttonWrapper.contentEditable = 'false';
      buttonWrapper.setAttribute('data-text', buttonText);
      buttonWrapper.setAttribute('data-url', buttonUrl);
      buttonWrapper.setAttribute('data-theme', buttonTheme);
      buttonWrapper.setAttribute('data-alignment', buttonAlignment);
      buttonWrapper.style.cssText = `${getAlignmentStyle(buttonAlignment)} margin: 1rem 0; cursor: pointer;`;
      
      const buttonLink = document.createElement('a');
      buttonLink.href = buttonUrl;
      buttonLink.textContent = buttonText;
      buttonLink.target = '_blank';
      buttonLink.rel = 'noopener noreferrer';
      Object.assign(buttonLink.style, selectedTheme.style);
      
      // Add hover effects
      buttonLink.addEventListener('mouseenter', () => {
        Object.assign(buttonLink.style, selectedTheme.hoverStyle);
      });
      
      buttonLink.addEventListener('mouseleave', () => {
        Object.assign(buttonLink.style, selectedTheme.style);
      });
      
      buttonWrapper.appendChild(buttonLink);
      
      // Add click handler for editing
      buttonWrapper.addEventListener('click', (e) => {
        // Only handle clicks on the wrapper itself, not the link
        if (e.target === buttonWrapper || e.target.closest('.editor-button-wrapper') === buttonWrapper) {
          e.preventDefault();
          e.stopPropagation();
          handleEditButton(buttonWrapper);
        }
      });
      
      // Make sure the wrapper is clickable
      buttonWrapper.style.cursor = 'pointer';
      buttonWrapper.title = 'Click to edit button';
      
      // Wrap with breathing room for better cursor navigation
      const containerWithBreathingRoom = createElementWithBreathingRoom(buttonWrapper);
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(containerWithBreathingRoom);
        
        // Move cursor after button
        range.setStartAfter(containerWithBreathingRoom);
        range.setEndAfter(containerWithBreathingRoom);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current?.appendChild(containerWithBreathingRoom);
      }
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setButtonText('');
    setButtonUrl('');
    setButtonAlignment('center');
    setShowButtonModal(false);
  };

  // Handle Divider Click
  const handleDividerButtonClick = () => {
    setDividerTheme('simple');
    setEditingDivider(null);
    setShowDividerModal(true);
  };

  // Handle Edit Divider
  const handleEditDivider = (dividerElement) => {
    const theme = dividerElement.getAttribute('data-theme') || 'simple';
    setDividerTheme(theme);
    setEditingDivider(dividerElement);
    setShowDividerModal(true);
  };

  // Handle Save Divider
  const handleSaveDivider = () => {
    const selectedTheme = DIVIDER_THEMES.find(t => t.id === dividerTheme) || DIVIDER_THEMES[0];
    
    if (editingDivider) {
      // Update existing divider
      editingDivider.setAttribute('data-theme', dividerTheme);
      editingDivider.innerHTML = '';
      
      if (selectedTheme.content) {
        Object.assign(editingDivider.style, selectedTheme.style);
        const contentSpan = document.createElement('span');
        contentSpan.textContent = selectedTheme.content;
        Object.assign(contentSpan.style, selectedTheme.contentStyle);
        editingDivider.appendChild(contentSpan);
      } else {
        const hr = document.createElement('hr');
        Object.assign(hr.style, selectedTheme.style);
        editingDivider.appendChild(hr);
      }
      
      setEditingDivider(null);
    } else {
      // Create new divider
      restoreSelection();
      
      const dividerWrapper = document.createElement('div');
      dividerWrapper.className = 'editor-divider-wrapper';
      dividerWrapper.contentEditable = 'false';
      dividerWrapper.setAttribute('data-theme', dividerTheme);
      dividerWrapper.style.cssText = 'margin: 1rem 0; cursor: pointer;';
      
      if (selectedTheme.content) {
        // Decorative divider with content
        Object.assign(dividerWrapper.style, selectedTheme.style);
        const contentSpan = document.createElement('span');
        contentSpan.textContent = selectedTheme.content;
        Object.assign(contentSpan.style, selectedTheme.contentStyle);
        dividerWrapper.appendChild(contentSpan);
      } else {
        // Simple line divider
        const hr = document.createElement('hr');
        Object.assign(hr.style, selectedTheme.style);
        dividerWrapper.appendChild(hr);
      }
      
      // Add click handler for editing (will open modal)
      dividerWrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // This will be handled by the global click handler
      });
      
      // Make sure the wrapper is clickable
      dividerWrapper.style.cursor = 'pointer';
      dividerWrapper.title = 'Click to change divider style';
      
      // Wrap with breathing room for better cursor navigation
      const containerWithBreathingRoom = createElementWithBreathingRoom(dividerWrapper);
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(containerWithBreathingRoom);
        
        // Move cursor after divider
        range.setStartAfter(containerWithBreathingRoom);
        range.setEndAfter(containerWithBreathingRoom);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current?.appendChild(containerWithBreathingRoom);
      }
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setShowDividerModal(false);
  };

  // Handle Table Click
  const handleTableButtonClick = () => {
    saveSelection();
    setTableRows(3);
    setTableCols(3);
    setTableTheme('default');
    setEditingTable(null);
    
    // Initialize default table data
    const defaultData = [];
    for (let i = 0; i < 3; i++) {
      const row = [];
      for (let j = 0; j < 3; j++) {
        if (i === 0) {
          row.push(`Header ${j + 1}`);
        } else {
          row.push(`Cell ${i}-${j + 1}`);
        }
      }
      defaultData.push(row);
    }
    setTableData(defaultData);
    setShowTableModal(true);
  };

  // Handle Edit Table
  const handleEditTable = (tableElement) => {
    const rows = tableElement.querySelectorAll('tr').length;
    const cols = tableElement.querySelector('tr')?.querySelectorAll('th, td').length || 3;
    const theme = tableElement.getAttribute('data-theme') || 'default';
    
    // Extract existing table data
    const existingData = [];
    const tableRows = tableElement.querySelectorAll('tr');
    tableRows.forEach(row => {
      const rowData = [];
      const cells = row.querySelectorAll('th, td');
      cells.forEach(cell => {
        rowData.push(cell.textContent || '');
      });
      existingData.push(rowData);
    });
    
    setTableRows(rows);
    setTableCols(cols);
    setTableTheme(theme);
    setTableData(existingData);
    setEditingTable(tableElement);
    setShowTableEditor(true);
  };

  // Handle opening full table editor
  const handleOpenTableEditor = () => {
    setShowTableModal(false);
    setShowTableEditor(true);
  };

  // Update table data when rows/cols change
  const updateTableDimensions = (newRows, newCols) => {
    const newData = [];
    
    for (let i = 0; i < newRows; i++) {
      const row = [];
      for (let j = 0; j < newCols; j++) {
        if (tableData[i] && tableData[i][j] !== undefined) {
          // Keep existing data
          row.push(tableData[i][j]);
        } else {
          // Add new cells
          if (i === 0) {
            row.push(`Header ${j + 1}`);
          } else {
            row.push(`Cell ${i}-${j + 1}`);
          }
        }
      }
      newData.push(row);
    }
    
    setTableData(newData);
    setTableRows(newRows);
    setTableCols(newCols);
  };

  // Update individual cell data
  const updateCellData = (rowIndex, colIndex, value) => {
    const newData = [...tableData];
    newData[rowIndex][colIndex] = value;
    setTableData(newData);
  };

  // Excel-like selection functions
  const getCellKey = (row, col) => `${row}-${col}`;
  
  const handleCellMouseDown = (rowIndex, colIndex, e) => {
    if (e.shiftKey && selectionStart) {
      // Extend selection with Shift key
      handleCellMouseEnter(rowIndex, colIndex, e);
    } else {
      // Start new selection
      setSelectionStart({ row: rowIndex, col: colIndex });
      setSelectionEnd({ row: rowIndex, col: colIndex });
      setIsSelecting(true);
      
      if (!e.ctrlKey && !e.metaKey) {
        // Clear previous selection if not holding Ctrl/Cmd
        setSelectedCells(new Set([getCellKey(rowIndex, colIndex)]));
      } else {
        // Add to selection with Ctrl/Cmd
        const newSelected = new Set(selectedCells);
        const key = getCellKey(rowIndex, colIndex);
        if (newSelected.has(key)) {
          newSelected.delete(key);
        } else {
          newSelected.add(key);
        }
        setSelectedCells(newSelected);
      }
    }
  };
  
  const handleCellMouseEnter = (rowIndex, colIndex, e) => {
    if (isSelecting && selectionStart) {
      setSelectionEnd({ row: rowIndex, col: colIndex });
      
      // Calculate selection range
      const minRow = Math.min(selectionStart.row, rowIndex);
      const maxRow = Math.max(selectionStart.row, rowIndex);
      const minCol = Math.min(selectionStart.col, colIndex);
      const maxCol = Math.max(selectionStart.col, colIndex);
      
      // Create set of selected cells
      const newSelected = new Set();
      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          newSelected.add(getCellKey(r, c));
        }
      }
      setSelectedCells(newSelected);
    }
  };
  
  const handleCellMouseUp = () => {
    setIsSelecting(false);
  };
  
  const isCellSelected = (rowIndex, colIndex) => {
    return selectedCells.has(getCellKey(rowIndex, colIndex));
  };
  
  // Handle paste from Excel/spreadsheet
  const handleTablePaste = (e) => {
    e.preventDefault();
    
    const pastedText = e.clipboardData.getData('text/plain');
    if (!pastedText) return;
    
    // Parse pasted data (tab-separated for columns, newline for rows)
    const rows = pastedText.split('\n').map(row => 
      row.split('\t').map(cell => cell.trim())
    ).filter(row => row.some(cell => cell)); // Remove empty rows
    
    if (rows.length === 0) return;
    
    // Find the starting position (first selected cell or 0,0)
    let startRow = 0;
    let startCol = 0;
    
    if (selectedCells.size > 0) {
      // Get the top-left cell of selection
      const selectedArray = Array.from(selectedCells).map(key => {
        const [row, col] = key.split('-').map(Number);
        return { row, col };
      });
      startRow = Math.min(...selectedArray.map(c => c.row));
      startCol = Math.min(...selectedArray.map(c => c.col));
    }
    
    // Calculate required dimensions
    const requiredRows = startRow + rows.length;
    const requiredCols = startCol + Math.max(...rows.map(r => r.length));
    
    // Expand table if needed
    if (requiredRows > tableRows || requiredCols > tableCols) {
      const newRows = Math.max(requiredRows, tableRows);
      const newCols = Math.max(requiredCols, tableCols);
      updateTableDimensions(newRows, newCols);
    }
    
    // Paste data into table
    const newData = [...tableData];
    rows.forEach((row, rowOffset) => {
      row.forEach((cellValue, colOffset) => {
        const targetRow = startRow + rowOffset;
        const targetCol = startCol + colOffset;
        
        if (targetRow < newData.length && targetCol < newData[targetRow].length) {
          newData[targetRow][targetCol] = cellValue;
        }
      });
    });
    
    setTableData(newData);
    
    // Select the pasted range
    const newSelected = new Set();
    for (let r = startRow; r < startRow + rows.length; r++) {
      for (let c = startCol; c < startCol + rows[0].length; c++) {
        if (r < tableRows && c < tableCols) {
          newSelected.add(getCellKey(r, c));
        }
      }
    }
    setSelectedCells(newSelected);
    
    showToast('Data pasted successfully!', 'success');
  };
  
  // Select entire row
  const selectRow = (rowIndex) => {
    const newSelected = new Set();
    for (let c = 0; c < tableCols; c++) {
      newSelected.add(getCellKey(rowIndex, c));
    }
    setSelectedCells(newSelected);
    setSelectionStart({ row: rowIndex, col: 0 });
    setSelectionEnd({ row: rowIndex, col: tableCols - 1 });
  };
  
  // Select entire column
  const selectColumn = (colIndex) => {
    const newSelected = new Set();
    for (let r = 0; r < tableRows; r++) {
      newSelected.add(getCellKey(r, colIndex));
    }
    setSelectedCells(newSelected);
    setSelectionStart({ row: 0, col: colIndex });
    setSelectionEnd({ row: tableRows - 1, col: colIndex });
  };
  
  // Select all cells
  const selectAllCells = () => {
    const newSelected = new Set();
    for (let r = 0; r < tableRows; r++) {
      for (let c = 0; c < tableCols; c++) {
        newSelected.add(getCellKey(r, c));
      }
    }
    setSelectedCells(newSelected);
    setSelectionStart({ row: 0, col: 0 });
    setSelectionEnd({ row: tableRows - 1, col: tableCols - 1 });
  };
  
  // Handle keyboard delete/backspace
  const handleTableKeyDown = (e) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedCells.size > 0) {
      // Check if we're not focused on an input field
      if (e.target.tagName !== 'INPUT') {
        e.preventDefault();
        
        // Clear all selected cells
        const newData = [...tableData];
        selectedCells.forEach(cellKey => {
          const [row, col] = cellKey.split('-').map(Number);
          if (newData[row] && newData[row][col] !== undefined) {
            newData[row][col] = '';
          }
        });
        
        setTableData(newData);
        showToast(`Cleared ${selectedCells.size} cell${selectedCells.size > 1 ? 's' : ''}`, 'success');
      }
    }
    
    // Handle Ctrl+A to select all
    if ((e.ctrlKey || e.metaKey) && e.key === 'a' && e.target.tagName !== 'INPUT') {
      e.preventDefault();
      selectAllCells();
    }
  };

  // Handle Save Table
  const handleSaveTable = () => {
    const selectedTheme = TABLE_THEMES.find(t => t.id === tableTheme) || TABLE_THEMES[0];
    
    if (editingTable) {
      // Update existing table
      const wrapper = editingTable.closest('.editor-table-wrapper');
      if (wrapper) {
        wrapper.innerHTML = '';
        const newTable = createTableElement(selectedTheme);
        wrapper.appendChild(newTable);
      }
      setEditingTable(null);
    } else {
      // Create new table
      restoreSelection();
      
      const tableWrapper = document.createElement('div');
      tableWrapper.className = 'editor-table-wrapper';
      tableWrapper.contentEditable = 'false';
      tableWrapper.style.cssText = 'margin: 1rem 0; overflow-x: auto; cursor: pointer;';
      
      const table = createTableElement(selectedTheme);
      tableWrapper.appendChild(table);
      
      // Add click handler for editing
      tableWrapper.addEventListener('click', (e) => {
        // Only handle clicks on the wrapper or table itself, not on editable cells
        if (e.target === tableWrapper || e.target.tagName === 'TABLE' || 
            (e.target.closest('.editor-table-wrapper') === tableWrapper && 
             !e.target.matches('th[contenteditable], td[contenteditable], th[contenteditable] *, td[contenteditable] *'))) {
          e.preventDefault();
          e.stopPropagation();
          const table = tableWrapper.querySelector('table');
          if (table) handleEditTable(table);
        }
      });
      
      // Make sure the wrapper is clickable
      tableWrapper.style.cursor = 'pointer';
      tableWrapper.title = 'Click to edit table';
      
      // Wrap with breathing room for better cursor navigation
      const containerWithBreathingRoom = createElementWithBreathingRoom(tableWrapper);
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(containerWithBreathingRoom);
        
        // Move cursor after table
        range.setStartAfter(containerWithBreathingRoom);
        range.setEndAfter(containerWithBreathingRoom);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current?.appendChild(containerWithBreathingRoom);
      }
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setShowTableModal(false);
    setShowTableEditor(false);
  };

  // Create table element with current data and theme
  const createTableElement = (selectedTheme) => {
    const table = document.createElement('table');
    table.setAttribute('data-theme', tableTheme);
    Object.assign(table.style, selectedTheme.tableStyle);
    
    // Create header row
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    
    for (let i = 0; i < tableCols; i++) {
      const th = document.createElement('th');
      th.textContent = tableData[0] ? tableData[0][i] : `Header ${i + 1}`;
      th.contentEditable = 'true';
      Object.assign(th.style, selectedTheme.thStyle);
      th.style.fontWeight = '700'; // Ensure headers are always bold
      headerRow.appendChild(th);
    }
    
    thead.appendChild(headerRow);
    table.appendChild(thead);
    
    // Create body rows
    const tbody = document.createElement('tbody');
    
    for (let i = 1; i < tableRows; i++) {
      const row = document.createElement('tr');
      
      for (let j = 0; j < tableCols; j++) {
        const td = document.createElement('td');
        td.textContent = tableData[i] ? tableData[i][j] : `Cell ${i}-${j + 1}`;
        td.contentEditable = 'true';
        Object.assign(td.style, selectedTheme.tdStyle);
        if (selectedTheme.evenRowStyle && (i - 1) % 2 === 1) {
          Object.assign(td.style, selectedTheme.evenRowStyle);
        }
        row.appendChild(td);
      }
      
      tbody.appendChild(row);
    }
    
    table.appendChild(tbody);
    return table;
  };

  // Handle FAQ Button Click
  const handleFaqButtonClick = () => {
    saveSelection();
    setFaqQuestion('');
    setFaqAnswer('');
    setFaqTheme('minimal');
    setEditingFaq(null);
    setShowFaqModal(true);
  };

  // Handle Edit FAQ
  const handleEditFaq = (faqElement) => {
    const question = faqElement.getAttribute('data-question') || '';
    const answer = faqElement.getAttribute('data-answer') || '';
    const theme = faqElement.getAttribute('data-theme') || 'minimal';
    
    setFaqQuestion(question);
    setFaqAnswer(answer);
    setFaqTheme(theme);
    setEditingFaq(faqElement);
    setShowFaqModal(true);
  };

  // Handle Save FAQ
  const handleSaveFaq = () => {
    if (!faqQuestion.trim() || !faqAnswer.trim()) return;
    
    const selectedTheme = FAQ_THEMES.find(t => t.id === faqTheme) || FAQ_THEMES[0];
    
    if (editingFaq) {
      // Update existing FAQ
      editingFaq.setAttribute('data-question', faqQuestion);
      editingFaq.setAttribute('data-answer', faqAnswer);
      editingFaq.setAttribute('data-theme', faqTheme);
      
      // COMPLETELY CLEAR ALL EXISTING STYLES
      editingFaq.style.cssText = '';
      editingFaq.removeAttribute('style');
      
      const questionElement = editingFaq.querySelector('.faq-question');
      const answerElement = editingFaq.querySelector('.faq-answer');
      const chevronIcon = editingFaq.querySelector('.faq-chevron');
      
      if (questionElement) {
        // Clear all existing styles
        questionElement.style.cssText = '';
        questionElement.removeAttribute('style');
        questionElement.textContent = faqQuestion;
        
        // Apply new theme styles
        Object.assign(questionElement.style, selectedTheme.questionStyle);
        
        // Re-create chevron icon with proper styling
        if (chevronIcon) {
          chevronIcon.remove();
        }
        
        const newChevronIcon = document.createElement('div');
        newChevronIcon.className = 'faq-chevron';
        newChevronIcon.innerHTML = '▼';
        Object.assign(newChevronIcon.style, selectedTheme.iconStyle);
        questionElement.appendChild(newChevronIcon);
      }
      
      if (answerElement) {
        // Clear all existing styles
        answerElement.style.cssText = '';
        answerElement.removeAttribute('style');
        answerElement.textContent = faqAnswer;
        
        // Apply new theme styles
        Object.assign(answerElement.style, selectedTheme.answerStyle);
        answerElement.style.display = 'none'; // Start collapsed
      }
      
      // Apply new container styles
      Object.assign(editingFaq.style, selectedTheme.containerStyle);
      
      setEditingFaq(null);
    } else {
      // Create new FAQ
      restoreSelection();
      
      const faqWrapper = document.createElement('div');
      faqWrapper.className = 'editor-faq-wrapper';
      faqWrapper.contentEditable = 'false';
      faqWrapper.setAttribute('data-question', faqQuestion);
      faqWrapper.setAttribute('data-answer', faqAnswer);
      faqWrapper.setAttribute('data-theme', faqTheme);
      Object.assign(faqWrapper.style, selectedTheme.containerStyle);
      
      // Create question element
      const questionElement = document.createElement('div');
      questionElement.className = 'faq-question';
      questionElement.textContent = faqQuestion;
      Object.assign(questionElement.style, selectedTheme.questionStyle);
      
      // Add chevron icon
      const chevronIcon = document.createElement('div');
      chevronIcon.className = 'faq-chevron';
      chevronIcon.innerHTML = '▼';
      Object.assign(chevronIcon.style, selectedTheme.iconStyle);
      questionElement.appendChild(chevronIcon);
      
      // Create answer element
      const answerElement = document.createElement('div');
      answerElement.className = 'faq-answer';
      answerElement.textContent = faqAnswer;
      Object.assign(answerElement.style, selectedTheme.answerStyle);
      answerElement.style.display = 'none'; // Start collapsed
      
      faqWrapper.appendChild(questionElement);
      faqWrapper.appendChild(answerElement);
      
      // Add click handler for editing
      faqWrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleEditFaq(faqWrapper);
      });
      
      faqWrapper.style.cursor = 'pointer';
      faqWrapper.title = 'Click to edit FAQ';
      
      // Wrap with breathing room for better cursor navigation
      const containerWithBreathingRoom = createElementWithBreathingRoom(faqWrapper);
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(containerWithBreathingRoom);
        
        // Move cursor after FAQ
        range.setStartAfter(containerWithBreathingRoom);
        range.setEndAfter(containerWithBreathingRoom);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current?.appendChild(containerWithBreathingRoom);
      }
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setFaqQuestion('');
    setFaqAnswer('');
    setShowFaqModal(false);
  };

  // Handle Social Links Button Click
  const handleSocialButtonClick = async () => {
    saveSelection();
    await loadSocialMedias(); // Load available social media
    setSelectedSocialIds([]);
    setSocialHeading('Connect');
    setEditingSocial(null);
    setShowSocialModal(true);
  };

  // Handle Edit Social Links
  const handleEditSocial = async (socialElement) => {
    await loadSocialMedias(); // Load available social media
    const selectedIds = socialElement.getAttribute('data-selected-ids');
    const heading = socialElement.getAttribute('data-heading') || 'Connect';
    
    if (selectedIds) {
      try {
        const ids = JSON.parse(selectedIds);
        setSelectedSocialIds(ids);
      } catch (e) {
        setSelectedSocialIds([]);
      }
    }
    
    setSocialHeading(heading);
    setEditingSocial(socialElement);
    setShowSocialModal(true);
  };

  // Toggle social media selection
  const toggleSocialSelection = (socialMediaId) => {
    setSelectedSocialIds(prev => 
      prev.includes(socialMediaId) 
        ? prev.filter(id => id !== socialMediaId)
        : [...prev, socialMediaId]
    );
  };

  // Handle Save Social Links
  const handleSaveSocial = () => {
    if (selectedSocialIds.length === 0) return;
    
    const selectedSocialMedias = availableSocialMedias.filter(sm => selectedSocialIds.includes(sm.$id));
    
    if (editingSocial) {
      // Update existing social links
      editingSocial.setAttribute('data-selected-ids', JSON.stringify(selectedSocialIds));
      editingSocial.setAttribute('data-heading', socialHeading);
      
      // Update content
      const headingElement = editingSocial.querySelector('.social-heading');
      const linksContainer = editingSocial.querySelector('.social-links');
      
      if (headingElement) {
        headingElement.textContent = socialHeading;
      }
      
      if (linksContainer) {
        linksContainer.innerHTML = '';
        selectedSocialMedias.forEach(socialMedia => {
          const linkElement = document.createElement('a');
          linkElement.href = socialMedia.link;
          linkElement.target = '_blank';
          linkElement.rel = 'noopener noreferrer';
          linkElement.title = socialMedia.name;
          linkElement.style.cssText = 'margin: 0 8px; text-decoration: none; transition: transform 0.2s; display: inline-block;';
          
          if (socialMedia.iconUrl) {
            const imgElement = document.createElement('img');
            imgElement.src = socialMedia.iconUrl;
            imgElement.alt = socialMedia.name;
            imgElement.style.cssText = 'width: 24px; height: 24px; object-fit: cover; border-radius: 4px;';
            linkElement.appendChild(imgElement);
          } else {
            linkElement.textContent = socialMedia.name.charAt(0).toUpperCase();
            linkElement.style.cssText += 'width: 24px; height: 24px; background: #105652; color: white; border-radius: 4px; display: inline-flex; align-items: center; justify-content: center; font-weight: 600; font-size: 12px;';
          }
          
          linkElement.addEventListener('mouseenter', () => linkElement.style.transform = 'scale(1.1)');
          linkElement.addEventListener('mouseleave', () => linkElement.style.transform = 'scale(1)');
          linksContainer.appendChild(linkElement);
        });
      }
      
      setEditingSocial(null);
    } else {
      // Create new social links
      restoreSelection();
      
      const socialWrapper = document.createElement('div');
      socialWrapper.className = 'editor-social-wrapper';
      socialWrapper.contentEditable = 'false';
      socialWrapper.setAttribute('data-selected-ids', JSON.stringify(selectedSocialIds));
      socialWrapper.setAttribute('data-heading', socialHeading);
      socialWrapper.style.cssText = 'display: flex; align-items: center; gap: 16px; margin: 2rem 0; padding: 16px; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb;';
      
      // Create heading
      const headingElement = document.createElement('span');
      headingElement.className = 'social-heading';
      headingElement.textContent = socialHeading;
      headingElement.style.cssText = 'font-weight: 600; color: #374151; font-size: 16px;';
      
      // Create links container
      const linksContainer = document.createElement('div');
      linksContainer.className = 'social-links';
      linksContainer.style.cssText = 'display: flex; align-items: center; gap: 8px;';
      
      selectedSocialMedias.forEach(socialMedia => {
        const linkElement = document.createElement('a');
        linkElement.href = socialMedia.link;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.title = socialMedia.name;
        linkElement.style.cssText = 'margin: 0 8px; text-decoration: none; transition: transform 0.2s; display: inline-block;';
        
        if (socialMedia.iconUrl) {
          const imgElement = document.createElement('img');
          imgElement.src = socialMedia.iconUrl;
          imgElement.alt = socialMedia.name;
          imgElement.style.cssText = 'width: 24px; height: 24px; object-fit: cover; border-radius: 4px;';
          linkElement.appendChild(imgElement);
        } else {
          linkElement.textContent = socialMedia.name.charAt(0).toUpperCase();
          linkElement.style.cssText += 'width: 24px; height: 24px; background: #105652; color: white; border-radius: 4px; display: inline-flex; align-items: center; justify-content: center; font-weight: 600; font-size: 12px;';
        }
        
        linkElement.addEventListener('mouseenter', () => linkElement.style.transform = 'scale(1.1)');
        linkElement.addEventListener('mouseleave', () => linkElement.style.transform = 'scale(1)');
        linksContainer.appendChild(linkElement);
      });
      
      socialWrapper.appendChild(headingElement);
      socialWrapper.appendChild(linksContainer);
      
      // Add click handler for editing
      socialWrapper.addEventListener('click', (e) => {
        if (!e.target.matches('a, a *, img')) {
          e.preventDefault();
          e.stopPropagation();
          handleEditSocial(socialWrapper);
        }
      });
      
      socialWrapper.style.cursor = 'pointer';
      socialWrapper.title = 'Click to edit social links';
      
      // Wrap with breathing room for better cursor navigation
      const containerWithBreathingRoom = createElementWithBreathingRoom(socialWrapper);
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(containerWithBreathingRoom);
        
        // Move cursor after social links
        range.setStartAfter(containerWithBreathingRoom);
        range.setEndAfter(containerWithBreathingRoom);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current?.appendChild(containerWithBreathingRoom);
      }
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setSelectedSocialIds([]);
    setSocialHeading('Connect');
    setShowSocialModal(false);
  };

  // Handle Project Navigation Button Click
  const handleProjectNavButtonClick = () => {
    saveSelection();
    setEditingProjectNav(null);
    setShowProjectNavModal(true);
  };

  // Handle Save Project Navigation
  const handleSaveProjectNav = () => {
    restoreSelection();
    
    const navWrapper = document.createElement('div');
    navWrapper.className = 'editor-project-nav-wrapper';
    navWrapper.contentEditable = 'false';
    navWrapper.style.cssText = 'display: flex; justify-content: space-between; align-items: center; margin: 2rem 0; padding: 20px; background: #f8f9fa; border-radius: 12px; border: 1px solid #e9ecef;';
    
    // Previous button
    const prevButton = document.createElement('button');
    prevButton.className = 'project-nav-prev';
    prevButton.innerHTML = '← Previous';
    prevButton.style.cssText = 'display: flex; align-items: center; gap: 8px; padding: 12px 20px; background: #105652; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.3s;';
    prevButton.addEventListener('mouseenter', () => prevButton.style.backgroundColor = '#0d4a47');
    prevButton.addEventListener('mouseleave', () => prevButton.style.backgroundColor = '#105652');
    
    // Next button
    const nextButton = document.createElement('button');
    nextButton.className = 'project-nav-next';
    nextButton.innerHTML = 'Next →';
    nextButton.style.cssText = 'display: flex; align-items: center; gap: 8px; padding: 12px 20px; background: #105652; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.3s;';
    nextButton.addEventListener('mouseenter', () => nextButton.style.backgroundColor = '#0d4a47');
    nextButton.addEventListener('mouseleave', () => nextButton.style.backgroundColor = '#105652');
    
    navWrapper.appendChild(prevButton);
    navWrapper.appendChild(nextButton);
    
    // Add click handler for editing
    navWrapper.addEventListener('click', (e) => {
      if (!e.target.matches('button, button *')) {
        e.preventDefault();
        e.stopPropagation();
        setEditingProjectNav(navWrapper);
        setShowProjectNavModal(true);
      }
    });
    
    navWrapper.style.cursor = 'pointer';
    navWrapper.title = 'Click to edit navigation';
    
    // Wrap with breathing room for better cursor navigation
    const containerWithBreathingRoom = createElementWithBreathingRoom(navWrapper);
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.insertNode(containerWithBreathingRoom);
      
      // Move cursor after navigation
      range.setStartAfter(containerWithBreathingRoom);
      range.setEndAfter(containerWithBreathingRoom);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current?.appendChild(containerWithBreathingRoom);
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setShowProjectNavModal(false);
  };

  // Handle Author Button Click
  const handleAuthorButtonClick = async () => {
    saveSelection();
    await loadAuthors(); // Load available authors
    setSelectedAuthorIds([]);
    setEditingAuthor(null);
    setShowAuthorModal(true);
  };

  // Handle Edit Author
  const handleEditAuthor = async (authorElement) => {
    await loadAuthors(); // Load available authors
    const selectedIds = authorElement.getAttribute('data-selected-ids');
    
    if (selectedIds) {
      try {
        const ids = JSON.parse(selectedIds);
        setSelectedAuthorIds(ids);
      } catch (e) {
        setSelectedAuthorIds([]);
      }
    }
    
    setEditingAuthor(authorElement);
    setShowAuthorModal(true);
  };

  // Toggle author selection
  const toggleAuthorSelection = (authorId) => {
    setSelectedAuthorIds(prev => 
      prev.includes(authorId) 
        ? prev.filter(id => id !== authorId)
        : [...prev, authorId]
    );
  };

  // Handle Save Author
  const handleSaveAuthor = () => {
    if (selectedAuthorIds.length === 0) return;
    
    const selectedAuthors = availableAuthors.filter(author => selectedAuthorIds.includes(author.$id));
    
    if (editingAuthor) {
      // Update existing author
      editingAuthor.setAttribute('data-selected-ids', JSON.stringify(selectedAuthorIds));
      
      // Update content
      const authorsContainer = editingAuthor.querySelector('.authors-container');
      
      if (authorsContainer) {
        authorsContainer.innerHTML = '';
        
        // Add "Authors:" label
        const label = document.createElement('span');
        label.textContent = 'Authors: ';
        label.style.cssText = 'font-weight: 600; color: #374151; margin-right: 12px;';
        authorsContainer.appendChild(label);
        
        // Add authors
        selectedAuthors.forEach((author, index) => {
          if (index > 0) {
            const comma = document.createElement('span');
            comma.textContent = ', ';
            comma.style.cssText = 'color: #6b7280; margin: 0 4px;';
            authorsContainer.appendChild(comma);
          }
          
          // Author image
          const imgElement = document.createElement('img');
          imgElement.src = author.imageUrl || '';
          imgElement.alt = author.name;
          imgElement.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; object-fit: cover; margin-right: 8px; display: inline-block; vertical-align: middle;';
          
          // Fallback if no image
          if (!author.imageUrl) {
            const fallback = document.createElement('div');
            fallback.textContent = author.name.charAt(0).toUpperCase();
            fallback.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; background: #105652; color: white; display: inline-flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; margin-right: 8px; vertical-align: middle;';
            authorsContainer.appendChild(fallback);
          } else {
            authorsContainer.appendChild(imgElement);
          }
          
          // Author name
          const nameElement = document.createElement('span');
          nameElement.textContent = author.name;
          nameElement.style.cssText = 'font-weight: 500; color: #1f2937; margin-right: 4px;';
          authorsContainer.appendChild(nameElement);
          
          // Author designation
          if (author.designation) {
            const designationElement = document.createElement('span');
            designationElement.textContent = author.designation;
            designationElement.style.cssText = 'color: #6b7280; font-size: 14px;';
            authorsContainer.appendChild(designationElement);
          }
        });
      }
      
      setEditingAuthor(null);
    } else {
      // Create new author
      restoreSelection();
      
      const authorWrapper = document.createElement('div');
      authorWrapper.className = 'editor-author-wrapper';
      authorWrapper.contentEditable = 'false';
      authorWrapper.setAttribute('data-selected-ids', JSON.stringify(selectedAuthorIds));
      authorWrapper.style.cssText = 'display: flex; align-items: center; margin: 2rem 0; padding: 16px; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb; flex-wrap: wrap;';
      
      // Create authors container
      const authorsContainer = document.createElement('div');
      authorsContainer.className = 'authors-container';
      authorsContainer.style.cssText = 'display: flex; align-items: center; flex-wrap: wrap;';
      
      // Add "Authors:" label
      const label = document.createElement('span');
      label.textContent = 'Authors: ';
      label.style.cssText = 'font-weight: 600; color: #374151; margin-right: 12px;';
      authorsContainer.appendChild(label);
      
      // Add authors
      selectedAuthors.forEach((author, index) => {
        if (index > 0) {
          const comma = document.createElement('span');
          comma.textContent = ', ';
          comma.style.cssText = 'color: #6b7280; margin: 0 4px;';
          authorsContainer.appendChild(comma);
        }
        
        // Author image
        if (author.imageUrl) {
          const imgElement = document.createElement('img');
          imgElement.src = author.imageUrl;
          imgElement.alt = author.name;
          imgElement.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; object-fit: cover; margin-right: 8px; display: inline-block; vertical-align: middle;';
          authorsContainer.appendChild(imgElement);
        } else {
          // Fallback if no image
          const fallback = document.createElement('div');
          fallback.textContent = author.name.charAt(0).toUpperCase();
          fallback.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; background: #105652; color: white; display: inline-flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; margin-right: 8px; vertical-align: middle;';
          authorsContainer.appendChild(fallback);
        }
        
        // Author name
        const nameElement = document.createElement('span');
        nameElement.textContent = author.name;
        nameElement.style.cssText = 'font-weight: 500; color: #1f2937; margin-right: 4px;';
        authorsContainer.appendChild(nameElement);
        
        // Author designation
        if (author.designation) {
          const designationElement = document.createElement('span');
          designationElement.textContent = author.designation;
          designationElement.style.cssText = 'color: #6b7280; font-size: 14px;';
          authorsContainer.appendChild(designationElement);
        }
      });
      
      authorWrapper.appendChild(authorsContainer);
      
      // Add click handler for editing
      authorWrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleEditAuthor(authorWrapper);
      });
      
      authorWrapper.style.cursor = 'pointer';
      authorWrapper.title = 'Click to edit authors';
      
      // Wrap with breathing room for better cursor navigation
      const containerWithBreathingRoom = createElementWithBreathingRoom(authorWrapper);
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(containerWithBreathingRoom);
        
        // Move cursor after author
        range.setStartAfter(containerWithBreathingRoom);
        range.setEndAfter(containerWithBreathingRoom);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current?.appendChild(containerWithBreathingRoom);
      }
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current?.dispatchEvent(event);
    
    setSelectedAuthorIds([]);
    setShowAuthorModal(false);
  };

  // Handle Codeblock Button Click
  const handleCodeBlockButtonClick = () => {
    saveSelection();
    setCodeBlockContent('');
    setCodeBlockLanguage('javascript');
    setCodeBlockFileName('');
    setCodeBlockTheme('dracula');
    setCodeBlockWrapped(false);
    setEditingCodeBlock(null);
    setShowCodeBlockModal(true);
  };

  // Handle Save Codeblock
  const handleSaveCodeBlock = () => {
    if (!codeBlockContent.trim()) return;
    
    const selectedTheme = CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme) || CODEBLOCK_THEMES[0];
    
    if (editingCodeBlock) {
      // Update existing codeblock
      const wrapper = editingCodeBlock;
      
      // Update attributes
      wrapper.setAttribute('data-theme-id', codeBlockTheme);
      wrapper.setAttribute('data-language', codeBlockLanguage);
      wrapper.setAttribute('data-code', codeBlockContent);
      if (codeBlockFileName) wrapper.setAttribute('data-filename', codeBlockFileName);
      else wrapper.removeAttribute('data-filename');
      if (codeBlockWrapped) wrapper.setAttribute('data-is-wrapped', 'true');
      else wrapper.removeAttribute('data-is-wrapped');
      
      // Update styles
      Object.assign(wrapper.style, {
        backgroundColor: selectedTheme.style.backgroundColor,
        borderColor: selectedTheme.style.borderColor || 'transparent',
        color: selectedTheme.style.color
      });

      // Apply syntax highlighting variables
      if (selectedTheme.syntax) {
        Object.entries(selectedTheme.syntax).forEach(([key, value]) => {
          wrapper.style.setProperty(key, value);
        });
      }

      // Update header
      const header = wrapper.querySelector('.editor-code-header');
      if (header) {
        header.style.backgroundColor = selectedTheme.headerStyle.backgroundColor;
        header.style.color = selectedTheme.headerStyle.color;
        
        // Update title
        const title = header.querySelector('.code-header-title');
        if (title) {
          title.textContent = codeBlockFileName || codeBlockLanguage || 'code';
          title.style.cssText = `
            color: ${selectedTheme.headerStyle.color};
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            font-size: 12px;
            opacity: 0.8;
          `;
        }
        
        // Update copy button
        const copyBtn = header.querySelector('button');
        if (copyBtn) {
          copyBtn.style.color = selectedTheme.headerStyle.color;
          copyBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            navigator.clipboard.writeText(codeBlockContent).then(() => {
              const originalHTML = copyBtn.innerHTML;
              copyBtn.innerHTML = 'Copied!';
              copyBtn.style.fontSize = '11px';
              setTimeout(() => {
                copyBtn.innerHTML = originalHTML;
                copyBtn.style.fontSize = '';
              }, 2000);
            }).catch(err => console.error('Failed to copy:', err));
          };
        }
      }

      // Update content
      const contentPre = wrapper.querySelector('pre');
      if (contentPre) {
        contentPre.style.backgroundColor = selectedTheme.style.backgroundColor;
        contentPre.style.color = selectedTheme.style.color;
        
        const code = contentPre.querySelector('code');
        if (code) {
          code.className = `language-${codeBlockLanguage}`;
          code.innerHTML = highlightCode(codeBlockContent, codeBlockLanguage);
        }
      }

      // Handle wrapped code
      if (codeBlockWrapped) {
        wrapper.style.maxHeight = '360px';
        
        // Update or create shade
        let shade = wrapper.querySelector('div[style*="linear-gradient"]');
        if (!shade) {
          shade = document.createElement('div');
          wrapper.appendChild(shade);
        }
        shade.style.cssText = `
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 80px;
          background: linear-gradient(to bottom, transparent, ${selectedTheme.style.backgroundColor});
          pointer-events: none;
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        `;

        // Update or create expand button
        let expandBtnContainer = wrapper.querySelector('div[style*="bottom: 10px"]');
        if (!expandBtnContainer) {
          expandBtnContainer = document.createElement('div');
          expandBtnContainer.style.cssText = `
            position: absolute;
            bottom: 10px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 10;
          `;

          const expandBtn = document.createElement('button');
          expandBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>';
          expandBtn.title = "Click to show full code";
          expandBtn.style.cssText = `
            background: ${selectedTheme.headerStyle.backgroundColor};
            color: ${selectedTheme.headerStyle.color};
            border: 1px solid ${selectedTheme.style.borderColor || 'rgba(255,255,255,0.1)'};
            border-radius: 50%;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 4px 6px rgba(0,0,0,0.3);
            transition: transform 0.2s;
          `;
          expandBtn.onmouseenter = () => expandBtn.style.transform = 'scale(1.1)';
          expandBtn.onmouseleave = () => expandBtn.style.transform = 'scale(1)';
          expandBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            wrapper.style.maxHeight = wrapper.style.maxHeight === '360px' ? 'none' : '360px';
            shade.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
            expandBtnContainer.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
          };

          expandBtnContainer.appendChild(expandBtn);
          wrapper.appendChild(expandBtnContainer);
        }
      } else {
        // Remove wrapped code elements if not wrapped
        wrapper.style.maxHeight = 'none';
        const shade = wrapper.querySelector('div[style*="linear-gradient"]');
        const expandBtnContainer = wrapper.querySelector('div[style*="bottom: 10px"]');
        if (shade) shade.remove();
        if (expandBtnContainer) expandBtnContainer.remove();
      }
      
      // Trigger input event to update preview
      const event = new Event('input', { bubbles: true });
      editorRef.current.dispatchEvent(event);
      
      setEditingCodeBlock(null);
      setCodeBlockContent('');
      setShowCodeBlockModal(false);
      return;
    }
    
    // Create new codeblock (existing logic)
    restoreSelection();
    
    // Create wrapper div for the codeblock
    const wrapper = document.createElement('div');
    wrapper.className = 'editor-code-block-wrapper';
    wrapper.contentEditable = 'false';
    wrapper.setAttribute('data-theme-id', codeBlockTheme);
    wrapper.setAttribute('data-language', codeBlockLanguage);
    wrapper.setAttribute('data-code', codeBlockContent);
    if (codeBlockFileName) wrapper.setAttribute('data-filename', codeBlockFileName);
    if (codeBlockWrapped) wrapper.setAttribute('data-is-wrapped', 'true');
    
    // Apply styles
    Object.assign(wrapper.style, {
      backgroundColor: selectedTheme.style.backgroundColor,
      borderColor: selectedTheme.style.borderColor || 'transparent',
      color: selectedTheme.style.color,
      borderRadius: '0.5rem',
      overflow: 'hidden',
      border: `1px solid ${selectedTheme.style.borderColor || 'transparent'}`,
      margin: '1em 0',
      position: 'relative'
    });

    // Apply syntax highlighting variables
    if (selectedTheme.syntax) {
      Object.entries(selectedTheme.syntax).forEach(([key, value]) => {
        wrapper.style.setProperty(key, value);
      });
    }

    // Build Header
    const header = document.createElement('div');
    header.className = 'editor-code-header';
    header.style.cssText = `
      background-color: ${selectedTheme.headerStyle.backgroundColor};
      color: ${selectedTheme.headerStyle.color};
      padding: 8px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 12px;
      position: relative;
    `;

    // Dots
    const dots = document.createElement('div');
    dots.style.cssText = 'display: flex; gap: 6px;';
    ['#ff5f56', '#ffbd2e', '#27c93f'].forEach(color => {
      const dot = document.createElement('div');
      dot.style.cssText = `width: 12px; height: 12px; border-radius: 50%; background-color: ${color};`;
      dots.appendChild(dot);
    });
    header.appendChild(dots);

    // Title/Filename (centered)
    const title = document.createElement('div');
    title.className = 'code-header-title';
    title.textContent = codeBlockFileName || codeBlockLanguage || 'code';
    title.style.cssText = `
      color: ${selectedTheme.headerStyle.color};
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      font-size: 12px;
      opacity: 0.8;
    `;
    header.appendChild(title);

    // Copy Button
    const copyBtn = document.createElement('button');
    copyBtn.className = 'editor-code-copy';
    copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
    copyBtn.style.cssText = `
      background: none;
      border: none;
      color: ${selectedTheme.headerStyle.color};
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      opacity: 0.7;
      transition: opacity 0.2s;
    `;
    copyBtn.title = "Copy code";
    copyBtn.onmouseenter = () => copyBtn.style.opacity = '1';
    copyBtn.onmouseleave = () => copyBtn.style.opacity = '0.7';
    copyBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigator.clipboard.writeText(codeBlockContent).then(() => {
        const originalHTML = copyBtn.innerHTML;
        copyBtn.innerHTML = 'Copied!';
        copyBtn.style.fontSize = '11px';
        setTimeout(() => {
          copyBtn.innerHTML = originalHTML;
          copyBtn.style.fontSize = '';
        }, 2000);
      }).catch(err => console.error('Failed to copy:', err));
    };
    header.appendChild(copyBtn);

    wrapper.appendChild(header);

    // Content
    const contentPre = document.createElement('pre');
    contentPre.style.cssText = `
      background-color: ${selectedTheme.style.backgroundColor};
      color: ${selectedTheme.style.color};
      margin: 0;
      padding: 16px;
      overflow-x: auto;
      font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
      font-size: 14px;
      line-height: 1.5;
    `;
    
    const code = document.createElement('code');
    code.className = `language-${codeBlockLanguage}`;
    code.innerHTML = highlightCode(codeBlockContent, codeBlockLanguage);
    contentPre.appendChild(code);
    
    wrapper.appendChild(contentPre);

    // Wrapped Code UI (Shade + Expand Button)
    if (codeBlockWrapped) {
      wrapper.style.maxHeight = '360px';
      
      // Shade
      const shade = document.createElement('div');
      shade.style.cssText = `
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 80px;
        background: linear-gradient(to bottom, transparent, ${selectedTheme.style.backgroundColor});
        pointer-events: none;
        border-bottom-left-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
      `;
      wrapper.appendChild(shade);

      // Expand Button
      const expandBtnContainer = document.createElement('div');
      expandBtnContainer.style.cssText = `
        position: absolute;
        bottom: 10px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10;
      `;

      const expandBtn = document.createElement('button');
      expandBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>';
      expandBtn.title = "Click to show full code";
      expandBtn.style.cssText = `
        background: ${selectedTheme.headerStyle.backgroundColor};
        color: ${selectedTheme.headerStyle.color};
        border: 1px solid ${selectedTheme.style.borderColor || 'rgba(255,255,255,0.1)'};
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 4px 6px rgba(0,0,0,0.3);
        transition: transform 0.2s;
      `;
      
      expandBtn.onmouseenter = () => expandBtn.style.transform = 'scale(1.1)';
      expandBtn.onmouseleave = () => expandBtn.style.transform = 'scale(1)';
      expandBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        wrapper.style.maxHeight = wrapper.style.maxHeight === '360px' ? 'none' : '360px';
        shade.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
        expandBtnContainer.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
      };

      expandBtnContainer.appendChild(expandBtn);
      wrapper.appendChild(expandBtnContainer);
    }
    
    // Wrap with breathing room for better cursor navigation
    const containerWithBreathingRoom = createElementWithBreathingRoom(wrapper);
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.insertNode(containerWithBreathingRoom);
      
      // Move cursor after codeblock
      range.setStartAfter(containerWithBreathingRoom);
      range.setEndAfter(containerWithBreathingRoom);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current?.appendChild(containerWithBreathingRoom);
    }
    
    // Trigger input event to update preview
    const event = new Event('input', { bubbles: true });
    editorRef.current.dispatchEvent(event);
    
    setCodeBlockContent('');
    setShowCodeBlockModal(false);
  };

  // Handle Copy Code
  const handleCopyCode = () => {
    if (codeBlockContent) {
      navigator.clipboard.writeText(codeBlockContent).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(err => console.error('Failed to copy:', err));
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[99999]"
      style={{
        background: '#FFFAEB',
        backgroundImage: `
          radial-gradient(circle, rgba(16, 86, 82, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px'
      }}
    >
      {/* Back Button */}
      <button
        onClick={() => {
          if (selectedSection) {
            handleBackToSelector();
          } else if (isProjectMode) {
            navigate('/admin/project-management');
          } else if (isBlogMode) {
            navigate('/admin/blogs');
          } else {
            navigate('/admin');
          }
        }}
        className="fixed top-6 left-6 flex items-center gap-2 px-4 py-2 bg-[#4C1D95] text-white rounded-lg hover:bg-[#3d1777] transition-colors shadow-lg z-20"
      >
        <ArrowLeft size={20} />
        <span className="font-medium">
          {selectedSection 
            ? 'Back to Selection' 
            : isBlogMode 
              ? 'Back to Blog Management' 
              : 'Back to Dashboard'
          }
        </span>
      </button>

      {/* Show Selector or Editor based on selection */}
      {!selectedSection ? (
        <EditorSectionSelector 
          onSelectSection={handleSelectSection}
          onMasterSave={(isBlogMode || isProjectMode) ? handleMasterSave : undefined}
          onPreview={handlePreview}
          onStatusChange={(isBlogMode || isProjectMode) ? handleStatusChange : undefined}
          onStatusUpdate={(isBlogMode || isProjectMode) ? handleStatusUpdate : undefined}
          isBlogMode={isBlogMode}
          isProjectMode={isProjectMode}
          blogStatus={blogStatus}
          projectStatus={projectStatus}
          formData={formData}
        />
      ) : selectedSection === 'link-management' ? (
        <VintageLinkManagement 
          formData={formData}
          setFormData={setFormData}
          handleSave={handleSave}
          showToast={showToast}
        />
      ) : selectedSection === 'basic-info' ? (
        <>
        {/* Basic Project Information Form */}
        <div className="w-full h-full pt-20 pb-6 px-6 flex gap-4">
          {/* Left Section - 20% */}
          <div 
            className="w-[20%] h-full bg-white rounded-2xl overflow-auto p-6"
            style={{
              boxShadow: `
                0 10px 25px -5px rgba(0, 0, 0, 0.1),
                0 8px 10px -6px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 0 rgba(255, 255, 255, 0.8),
                0 1px 3px 0 rgba(0, 0, 0, 0.08)
              `,
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            <h2 className="text-lg font-bold text-gray-800 mb-6">Select the Field</h2>
            
            <div className="space-y-4">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                {categories.length === 0 ? (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      No categories found. Please{' '}
                      <Link to="/admin/categories" className="underline font-semibold">
                        create categories
                      </Link>{' '}
                      first.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={(e) => {
                        if (e.target.value === '__ADD_NEW__') {
                          setShowCategoryInput(true);
                          setNewCategoryName('');
                        } else {
                          handleInputChange(e);
                          setFormData(prev => ({ ...prev, subcategoryId: '' }));
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.$id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                      <option value="__ADD_NEW__" className="font-semibold text-[#4C1D95]">
                        + Add New Category
                      </option>
                    </select>
                    
                    {/* Inline Category Creation */}
                    {showCategoryInput && (
                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category Name</label>
                          <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleCreateCategory();
                              } else if (e.key === 'Escape') {
                                setShowCategoryInput(false);
                                setNewCategoryName('');
                                setNewCategoryColor('#4C1D95');
                                setShowCategoryColorPicker(false);
                              }
                            }}
                            placeholder="Enter category name"
                            className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                            autoFocus
                          />
                        </div>
                        
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category Color</label>
                          <button
                            type="button"
                            onClick={() => setShowCategoryColorPicker(!showCategoryColorPicker)}
                            className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95] bg-white flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-6 h-6 rounded border-2 border-gray-300"
                                style={{ backgroundColor: newCategoryColor }}
                              />
                              <span className="font-mono text-xs">{newCategoryColor}</span>
                            </div>
                            <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryColorPicker ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {/* Color Picker Dropdown */}
                          {showCategoryColorPicker && (
                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                              <div className="grid grid-cols-5 gap-2 mb-2">
                                {['#4C1D95', '#DC2626', '#059669', '#2563EB', '#EA580C', '#7C3AED', '#DB2777', '#0891B2', '#65A30D', '#CA8A04'].map(color => (
                                  <button
                                    key={color}
                                    type="button"
                                    onClick={() => {
                                      setNewCategoryColor(color);
                                      setShowCategoryColorPicker(false);
                                    }}
                                    className={`w-full h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                                      newCategoryColor === color ? 'border-gray-800 ring-2 ring-gray-400' : 'border-gray-300'
                                    }`}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                  />
                                ))}
                              </div>
                              
                              <div className="pt-2 border-t border-gray-200">
                                <label className="block text-xs text-gray-600 mb-1">Custom Color</label>
                                <input
                                  type="color"
                                  value={newCategoryColor}
                                  onChange={(e) => setNewCategoryColor(e.target.value)}
                                  className="w-full h-10 rounded-lg border-2 border-gray-300 cursor-pointer"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                          <button
                            type="button"
                            onClick={handleCreateCategory}
                            disabled={creatingCategory || !newCategoryName.trim()}
                            className="flex-1 px-4 py-2 bg-[#4C1D95] text-white text-sm font-medium rounded-lg hover:bg-[#3C1575] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {creatingCategory ? 'Creating...' : 'Create Category'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowCategoryInput(false);
                              setNewCategoryName('');
                              setNewCategoryColor('#4C1D95');
                              setShowCategoryColorPicker(false);
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Subcategory */}
              {formData.category && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subcategory
                  </label>
                  <div className="space-y-2">
                    <select
                      name="subcategoryId"
                      value={formData.subcategoryId}
                      onChange={(e) => {
                        if (e.target.value === '__ADD_NEW__') {
                          setShowSubcategoryInput(true);
                          setNewSubcategoryName('');
                        } else {
                          handleInputChange(e);
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                    >
                      <option value="">No subcategory</option>
                      {filteredSubcategories.map((sub) => (
                        <option key={sub.$id} value={sub.$id}>
                          {sub.name}
                        </option>
                      ))}
                      <option value="__ADD_NEW__" className="font-semibold text-[#4C1D95]">
                        + Add New Subcategory
                      </option>
                    </select>
                    
                    {/* Inline Subcategory Creation */}
                    {showSubcategoryInput && (
                      <div className="flex gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <input
                          type="text"
                          value={newSubcategoryName}
                          onChange={(e) => setNewSubcategoryName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleCreateSubcategory();
                            } else if (e.key === 'Escape') {
                              setShowSubcategoryInput(false);
                              setNewSubcategoryName('');
                            }
                          }}
                          placeholder="Enter new subcategory name"
                          className="flex-1 px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={handleCreateSubcategory}
                          disabled={creatingSubcategory || !newSubcategoryName.trim()}
                          className="px-4 py-2 bg-[#4C1D95] text-white text-sm font-medium rounded-lg hover:bg-[#3C1575] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {creatingSubcategory ? 'Creating...' : 'Create'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowSubcategoryInput(false);
                            setNewSubcategoryName('');
                          }}
                          className="px-3 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Initial Likes */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Initial Likes
                </label>
                <input 
                  type="number" 
                  name="likes"
                  value={formData.likes}
                  onChange={handleInputChange}
                  min="0"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95] max-w-24"
                />
              </div>

              {/* Featured Checkbox */}
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="featured"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-[#4C1D95] border-gray-300 rounded focus:ring-[#4C1D95]"
                />
                <label htmlFor="featured" className="text-sm font-medium text-gray-700">
                  Featured to homepage
                </label>
              </div>

              {/* Author Selection - Only for Blog Mode */}
              {isBlogMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Author *</label>
                  
                  {availableAuthors.length === 0 ? (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800">
                        {loadingAuthors ? 'Loading authors...' : 'No authors found. Please add authors in Settings → Authors first.'}
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        value={(formData.authorIds && formData.authorIds[0]) || ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            setFormData(prev => ({
                              ...prev,
                              authorIds: [e.target.value] // Only store author ID
                            }));
                          } else {
                            // Clear selection
                            setFormData(prev => ({
                              ...prev,
                              authorIds: [] // Clear author ID
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95] appearance-none bg-white pr-8"
                      >
                        <option value="">Select author...</option>
                        {availableAuthors.map((author) => (
                          <option key={author.$id} value={author.$id}>
                            {author.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  )}
                </div>
              )}

              {/* Publish Date Selection - Only for Blog Mode */}
              {isBlogMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Publish Date *</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={formData.publishDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          publishDate: e.target.value
                        }));
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95] bg-white pr-8"
                    />
                    <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    This date will be displayed on the blog page
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Section - 80% */}
          <div 
            className="w-[80%] h-full bg-white rounded-2xl overflow-auto"
            style={{
              boxShadow: `
                0 10px 25px -5px rgba(0, 0, 0, 0.1),
                0 8px 10px -6px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 0 rgba(255, 255, 255, 0.8),
                0 1px 3px 0 rgba(0, 0, 0, 0.08)
              `,
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            <div className="w-full h-full flex justify-center py-8">
              <div className="w-full max-w-7xl px-8 pb-12">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-gray-800">Add Basic Information</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 px-4 py-2 bg-[#4C1D95] text-white rounded-lg hover:bg-[#3d1777] transition-colors"
                    >
                      <Save size={18} />
                      <span className="font-medium">
                        {(isProjectMode && projectEditingId && projectEditingId !== 'new') || 
                         (isBlogMode && blogEditingId && blogEditingId !== 'new') 
                          ? 'Save All Changes' 
                          : 'Save Draft'}
                      </span>
                    </button>
                  </div>
                </div>
                
                {/* Form Fields */}
                <div className="space-y-6 pb-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input 
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter project title"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#4C1D95] transition-colors"
                    />
                  </div>

                  {/* Short Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Description (for card) *
                    </label>
                    <textarea 
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="2"
                      maxLength="150"
                      placeholder="Brief description (max 150 characters)"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#4C1D95] transition-colors resize-none"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.description.length}/150 characters
                    </p>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = tagInput.trim();
                            if (!input) return;
                            
                            // Parse tags from different formats
                            let parsedTags = [];
                            
                            // Check if input contains commas
                            if (input.includes(',')) {
                              // Split by comma, trim whitespace, remove empty strings, and remove extra spaces
                              parsedTags = input
                                .split(',')
                                .map(tag => tag.trim().replace(/\s+/g, ' '))
                                .filter(tag => tag.length > 0);
                            } else {
                              // Single tag - trim and normalize spaces
                              parsedTags = [input.trim().replace(/\s+/g, ' ')];
                            }
                            
                            // Add only new tags
                            const newTags = parsedTags.filter(tag => !formData.tags.includes(tag));
                            if (newTags.length > 0) {
                              setFormData(prev => ({ ...prev, tags: [...prev.tags, ...newTags] }));
                            }
                            setTagInput('');
                          }
                        }}
                        className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#4C1D95]"
                        placeholder="Enter tag and press Enter (e.g., ArcGIS, Python, GIS)"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = tagInput.trim();
                          if (!input) return;
                          
                          // Parse tags from different formats
                          let parsedTags = [];
                          
                          // Check if input contains commas
                          if (input.includes(',')) {
                            // Split by comma, trim whitespace, remove empty strings, and remove extra spaces
                            parsedTags = input
                              .split(',')
                              .map(tag => tag.trim().replace(/\s+/g, ' '))
                              .filter(tag => tag.length > 0);
                          } else {
                            // Single tag - trim and normalize spaces
                            parsedTags = [input.trim().replace(/\s+/g, ' ')];
                          }
                          
                          // Add only new tags
                          const newTags = parsedTags.filter(tag => !formData.tags.includes(tag));
                          if (newTags.length > 0) {
                            setFormData(prev => ({ ...prev, tags: [...prev.tags, ...newTags] }));
                          }
                          setTagInput('');
                        }}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-[#4C1D95]/10 text-[#4C1D95] text-sm rounded-full"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                tags: prev.tags.filter((_, i) => i !== index)
                              }))}
                              className="hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Add tags to help categorize and filter. Supports: "tag1,tag2" or "tag1, tag2" or "tag1 , tag2"
                    </p>
                  </div>

                  {/* Project Details */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">Project Details</label>
                      <button
                        type="button"
                        onClick={addProjectDetail}
                        className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg text-white bg-[#4C1D95] hover:bg-[#3d1777] transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Field
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {formData.projectDetails.map((detail, index) => (
                        <div key={index} className="grid grid-cols-[200px_1fr_auto] gap-2 items-start">
                          <input
                            type="text"
                            value={detail.label}
                            onChange={(e) => updateProjectDetail(index, 'label', e.target.value)}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#4C1D95] text-sm"
                            placeholder="Label (e.g., Software)"
                          />
                          <input
                            type="text"
                            value={detail.value}
                            onChange={(e) => updateProjectDetail(index, 'value', e.target.value)}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#4C1D95] text-sm"
                            placeholder="Value (e.g., ArcGIS Pro)"
                          />
                          <button
                            type="button"
                            onClick={() => removeProjectDetail(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove field"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Add custom fields to display project information. Empty fields will not be shown.
                    </p>
                  </div>

                  {/* Project Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Link
                    </label>
                    <input 
                      type="url"
                      name="projectLink"
                      value={formData.projectLink}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#4C1D95] transition-colors"
                    />
                  </div>

                  {/* Image Uploads - Two Column Layout */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Thumbnail Image - Left Column */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Thumbnail Image * <span className="text-xs text-gray-500">(w-350px & h-256px)</span>
                      </label>
                      
                      {!formData.thumbnailUrl ? (
                        <div className="flex items-center justify-center">
                          <div className="relative inline-flex flex-col items-center justify-end p-2 bg-gradient-to-br from-[#6dd5ed] to-[#2193b0] rounded-xl shadow-md" style={{ height: '100px' }}>
                            <div className="absolute top-[-15px] left-1/2 transform -translate-x-1/2 animate-float">
                              <div className="relative">
                                <div className="absolute top-[-8px] w-16 h-4 bg-gradient-to-br from-[#ff9a56] to-[#ff6f56] rounded-t-lg shadow-md z-10" />
                                <div className="w-[90px] h-[60px] bg-gradient-to-br from-[#ffe563] to-[#ffc663] rounded-lg shadow-lg" />
                              </div>
                            </div>
                            <label className="cursor-pointer text-white text-sm text-center bg-white/20 hover:bg-white/40 rounded-lg px-6 py-1.5 transition-all duration-300 relative z-10">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    setFormData(prev => ({ ...prev, thumbnail: file, thumbnailUrl: URL.createObjectURL(file) }));
                                  }
                                }}
                                className="hidden"
                              />
                              Choose a file
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <div className="relative inline-block group">
                            <div className="relative w-[140px] transition-transform duration-300 hover:rotate-[5deg]">
                              <div className="relative aspect-[3/2] border-3 border-black bg-white p-[5%_5%_15%_5%] shadow-md">
                                <div className="w-full aspect-square border-3 border-black bg-gray-100 overflow-hidden">
                                  <img
                                    src={formData.thumbnailUrl}
                                    alt="Thumbnail preview"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="absolute inset-0 border-3 border-black bg-white -z-10 transform translate-y-[-2%] rotate-[-6deg]" />
                                <div className="absolute inset-0 border-3 border-black bg-white -z-20 transform translate-y-[2%] rotate-[6deg]" />
                              </div>
                            </div>
                            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <label className="cursor-pointer p-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600 shadow-md">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      if (formData.thumbnailUrl?.startsWith('blob:')) {
                                        URL.revokeObjectURL(formData.thumbnailUrl);
                                      }
                                      setFormData(prev => ({ ...prev, thumbnail: file, thumbnailUrl: URL.createObjectURL(file) }));
                                    }
                                  }}
                                  className="hidden"
                                />
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  if (formData.thumbnailUrl?.startsWith('blob:')) {
                                    URL.revokeObjectURL(formData.thumbnailUrl);
                                  }
                                  setFormData(prev => ({ ...prev, thumbnail: null, thumbnailUrl: '' }));
                                }}
                                className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Gallery Images - Right Column */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gallery Images (Multiple)
                      </label>
                      
                      <div className="flex items-center justify-center">
                        <div className="relative inline-flex flex-col items-center justify-end p-2 bg-gradient-to-br from-[#6dd5ed] to-[#2193b0] rounded-xl shadow-md" style={{ height: '100px' }}>
                          <div className="absolute top-[-15px] left-1/2 transform -translate-x-1/2 animate-float">
                            <div className="relative">
                              <div className="absolute top-[-8px] w-16 h-4 bg-gradient-to-br from-[#ff9a56] to-[#ff6f56] rounded-t-lg shadow-md z-10" />
                              <div className="w-[90px] h-[60px] bg-gradient-to-br from-[#ffe563] to-[#ffc663] rounded-lg shadow-lg" />
                            </div>
                          </div>
                          <label className="cursor-pointer text-white text-sm text-center bg-white/20 hover:bg-white/40 rounded-lg px-6 py-1.5 transition-all duration-300 relative z-10">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleGalleryChange}
                              className="hidden"
                            />
                            Choose files
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gallery Preview - Below in full width */}
                  {formData.galleryUrls.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gallery Preview ({formData.galleryUrls.length} images)
                      </label>
                      <div className="mb-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-sm text-purple-800">
                          💡 <strong>Tip:</strong> Use the arrow buttons on each image to reorder them
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {formData.galleryUrls.map((url, index) => (
                          <div key={index} className="relative inline-block group">
                            <div className="relative w-[140px] transition-transform duration-300 hover:rotate-[5deg]">
                              <div className="relative aspect-[3/2] border-3 border-black bg-white p-[5%_5%_15%_5%] shadow-md">
                                <div className="w-full aspect-square border-3 border-black bg-gray-100 overflow-hidden">
                                  <img
                                    src={url}
                                    alt={`Gallery ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="absolute inset-0 border-3 border-black bg-white -z-10 transform translate-y-[-2%] rotate-[-6deg]" />
                                <div className="absolute inset-0 border-3 border-black bg-white -z-20 transform translate-y-[2%] rotate-[6deg]" />
                              </div>
                              
                              {/* Position Badge */}
                              <div className="absolute top-1 left-1 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full z-20">
                                {index + 1}
                              </div>
                              
                              {/* Arrow Buttons - Always visible at top */}
                              <div className="absolute top-1 right-1 flex gap-1 z-20">
                                {/* Move Left Arrow */}
                                <button
                                  type="button"
                                  onClick={() => moveGalleryImageLeft(index)}
                                  disabled={index === 0}
                                  className="p-1.5 bg-white/90 text-purple-600 rounded-full hover:bg-purple-600 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg backdrop-blur-sm"
                                  title="Move left"
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </button>
                                
                                {/* Move Right Arrow */}
                                <button
                                  type="button"
                                  onClick={() => moveGalleryImageRight(index)}
                                  disabled={index === formData.galleryUrls.length - 1}
                                  className="p-1.5 bg-white/90 text-purple-600 rounded-full hover:bg-purple-600 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg backdrop-blur-sm"
                                  title="Move right"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Delete Button - Shows on hover at bottom */}
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="absolute bottom-1 right-1 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md opacity-0 group-hover:opacity-100 transition-opacity z-20"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <style>{`
                    @keyframes float {
                      0%, 100% { transform: translateY(0px); }
                      50% { transform: translateY(-15px); }
                    }
                    .animate-float {
                      animation: float 2.5s infinite ease-in-out;
                    }
                  `}</style>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      ) : selectedSection === 'full-description' ? (
        /* Full Description Editor with 20/80 Layout */
        <div className="w-full h-full pt-20 pb-6 px-6 flex gap-4">
          {/* Left Section - 20% Toolbar */}
          <div 
            className="w-[20%] h-full bg-white rounded-2xl overflow-auto p-4"
            style={{
              boxShadow: `
                0 10px 25px -5px rgba(0, 0, 0, 0.1),
                0 8px 10px -6px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 0 rgba(255, 255, 255, 0.8),
                0 1px 3px 0 rgba(0, 0, 0, 0.08)
              `,
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Title */}
            <div className="mb-4">
              <h2 className="text-lg font-bold text-center" style={{ color: '#4C1D95' }}>
                Tools for Crafting
              </h2>
            </div>

            {/* Text Formatting Section */}
            <div className="mb-4">
              <div className="flex gap-1">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('bold', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Bold"
                >
                  <Bold className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('italic', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Italic"
                >
                  <Italic className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('underline', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Underline"
                >
                  <Underline className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      const selectedText = window.getSelection().toString() || 'Normal text';
                      document.execCommand('insertHTML', false, `<p style="color: #000000; font-size: 1rem; font-weight: normal; margin: 0.5rem 0; line-height: 1.6;">${selectedText}</p>`);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                  title="Normal Paragraph"
                >
                  P
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('removeFormat', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Remove Formatting"
                >
                  <X className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 my-3"></div>

            {/* Heading Section */}
            <div className="mb-4">
              <div className="flex gap-1">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      const selectedText = window.getSelection().toString() || 'Heading 1';
                      document.execCommand('insertHTML', false, `<h1 style="color: #000000; font-size: 2rem; font-weight: bold; margin: 2rem 0 1rem; line-height: 1.2;">${selectedText}</h1>`);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                  title="Heading 1"
                >
                  H1
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      const selectedText = window.getSelection().toString() || 'Heading 2';
                      document.execCommand('insertHTML', false, `<h2 style="color: #000000; font-size: 1.5rem; font-weight: bold; margin: 2rem 0 1rem; line-height: 1.2;">${selectedText}</h2>`);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      const selectedText = window.getSelection().toString() || 'Heading 3';
                      document.execCommand('insertHTML', false, `<h3 style="color: #000000; font-size: 1.25rem; font-weight: bold; margin: 2rem 0 1rem; line-height: 1.2;">${selectedText}</h3>`);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                  title="Heading 3"
                >
                  H3
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      const selectedText = window.getSelection().toString() || 'Heading 4';
                      document.execCommand('insertHTML', false, `<h4 style="color: #000000; font-size: 1.125rem; font-weight: bold; margin: 1.5rem 0 0.75rem; line-height: 1.2;">${selectedText}</h4>`);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                  title="Heading 4"
                >
                  H4
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      const selectedText = window.getSelection().toString() || 'Heading 5';
                      document.execCommand('insertHTML', false, `<h5 style="color: #1E8479; font-size: 1rem; font-weight: bold; margin: 1.5rem 0 0.75rem; line-height: 1.2;">${selectedText}</h5>`);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                  title="Heading 5"
                >
                  H5
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      const selectedText = window.getSelection().toString() || 'Heading 6';
                      document.execCommand('insertHTML', false, `<h6 style="color: #000000; font-size: 0.875rem; font-weight: bold; margin: 1.5rem 0 0.75rem; line-height: 1.2;">${selectedText}</h6>`);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                  title="Heading 6"
                >
                  H6
                </button>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 my-3"></div>

            {/* Text Alignment Section */}
            <div className="mb-4">
              <div className="flex gap-1">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (selectedEmbed) {
                      // Align selected embed
                      selectedEmbed.style.display = 'block';
                      selectedEmbed.style.textAlign = 'left';
                      selectedEmbed.style.marginLeft = '0';
                      selectedEmbed.style.marginRight = 'auto';
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    } else if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('justifyLeft', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Align Left"
                >
                  <AlignLeft className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (selectedEmbed) {
                      // Center selected embed
                      selectedEmbed.style.display = 'block';
                      selectedEmbed.style.textAlign = 'center';
                      selectedEmbed.style.marginLeft = 'auto';
                      selectedEmbed.style.marginRight = 'auto';
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    } else if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('justifyCenter', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Align Center"
                >
                  <AlignCenter className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (selectedEmbed) {
                      // Right align selected embed
                      selectedEmbed.style.display = 'block';
                      selectedEmbed.style.textAlign = 'right';
                      selectedEmbed.style.marginLeft = 'auto';
                      selectedEmbed.style.marginRight = '0';
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    } else if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('justifyRight', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Align Right"
                >
                  <AlignRight className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (selectedEmbed) {
                      // Justify doesn't really apply to embeds, so we'll center them
                      selectedEmbed.style.display = 'block';
                      selectedEmbed.style.textAlign = 'center';
                      selectedEmbed.style.marginLeft = 'auto';
                      selectedEmbed.style.marginRight = 'auto';
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    } else if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('justifyFull', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Justify"
                >
                  <AlignJustify className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 my-3"></div>

            {/* List Section */}
            <div className="mb-4">
              <div className="flex gap-1">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('insertUnorderedList', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Bullet List"
                >
                  <List className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('insertOrderedList', false, null);
                    }
                  }}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Numbered List (1, 2, 3)"
                >
                  <ListOrdered className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('insertOrderedList', false, null);
                      // Apply Roman numeral style
                      setTimeout(() => {
                        const selection = window.getSelection();
                        if (selection.rangeCount > 0) {
                          let node = selection.focusNode;
                          while (node && node !== editorRef.current) {
                            if (node.tagName === 'OL') {
                              node.style.listStyleType = 'upper-roman';
                              break;
                            }
                            node = node.parentNode;
                          }
                        }
                      }, 0);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center font-semibold"
                  title="Roman Numeral List (I, II, III)"
                  style={{ color: '#4C1D95' }}
                >
                  I, II
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (editorRef.current) {
                      editorRef.current.focus();
                      document.execCommand('insertOrderedList', false, null);
                      // Apply lowercase letter style
                      setTimeout(() => {
                        const selection = window.getSelection();
                        if (selection.rangeCount > 0) {
                          let node = selection.focusNode;
                          while (node && node !== editorRef.current) {
                            if (node.tagName === 'OL') {
                              node.style.listStyleType = 'lower-alpha';
                              break;
                            }
                            node = node.parentNode;
                          }
                        }
                      }, 0);
                    }
                  }}
                  className="flex-1 px-2 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center font-semibold"
                  title="Alphabetical List (a, b, c)"
                  style={{ color: '#4C1D95' }}
                >
                  a, b
                </button>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 my-3"></div>

            {/* Typography & Fonts Section */}
            <div className="mb-4">
              <div className="flex gap-1">
                {/* Font Family */}
                <div className="relative flex-1">
                  <button
                    onClick={() => {
                      setShowFontPicker(!showFontPicker);
                      setShowColorPicker(false);
                      setShowSizePicker(false);
                      setShowWeightPicker(false);
                      setShowHighlightPicker(false);
                    }}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Font Family"
                  >
                    <Type className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                  </button>
                  
                  {showFontPicker && (
                    <div className="absolute top-full left-[-10px] mt-1 bg-white border rounded-lg shadow-lg w-64 max-h-64 overflow-y-auto z-50">
                      <div className="p-2">
                        {GOOGLE_FONTS.map(font => (
                          <button
                            key={font.name}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (editorRef.current) {
                                editorRef.current.focus();
                                document.execCommand('fontName', false, font.value);
                                // Trigger input event to update preview
                                const event = new Event('input', { bubbles: true });
                                editorRef.current.dispatchEvent(event);
                              }
                              setShowFontPicker(false);
                            }}
                            className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded transition-colors"
                            style={{ fontFamily: font.value }}
                          >
                            {font.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Font Color */}
                <div className="relative flex-1">
                  <button
                    onClick={() => {
                      setShowColorPicker(!showColorPicker);
                      setShowFontPicker(false);
                      setShowSizePicker(false);
                      setShowWeightPicker(false);
                      setShowHighlightPicker(false);
                    }}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Font Color"
                  >
                    <Palette className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                  </button>
                  
                  {showColorPicker && (
                    <div className="absolute top-full left-[-70px] mt-1 bg-white border rounded-lg shadow-lg p-3 z-50 w-64">
                      <div className="text-xs font-semibold text-gray-500 mb-2">FONT COLOR</div>
                      <input
                        type="color"
                        value={colorInput}
                        onChange={(e) => setColorInput(e.target.value)}
                        className="w-full h-10 rounded cursor-pointer mb-2"
                      />
                      <input
                        type="text"
                        value={colorInput}
                        onChange={(e) => setColorInput(e.target.value)}
                        placeholder="#000000 or rgb(0,0,0)"
                        className="w-full px-2 py-1 text-sm border rounded mb-2"
                      />
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (editorRef.current) {
                            editorRef.current.focus();
                            document.execCommand('foreColor', false, colorInput);
                            // Trigger input event to update preview
                            const event = new Event('input', { bubbles: true });
                            editorRef.current.dispatchEvent(event);
                          }
                          setShowColorPicker(false);
                        }}
                        className="w-full px-3 py-1.5 bg-[#4C1D95] text-white rounded text-sm hover:bg-[#3d1777] transition-colors"
                      >
                        Apply Color
                      </button>
                    </div>
                  )}
                </div>

                {/* Font Size */}
                <div className="relative flex-1">
                  <button
                    onClick={() => {
                      // Detect current font size when opening
                      if (!showSizePicker && editorRef.current) {
                        const selection = window.getSelection();
                        console.log('Selection:', selection.toString(), 'Range count:', selection.rangeCount);
                        if (selection.rangeCount > 0 && selection.toString().length > 0) {
                          let node = selection.anchorNode;
                          // If text node, get parent element
                          if (node.nodeType === 3) {
                            node = node.parentElement;
                          }
                          // Traverse up to find element with font size
                          while (node && node !== editorRef.current) {
                            const computedStyle = window.getComputedStyle(node);
                            const fontSize = parseInt(computedStyle.fontSize);
                            console.log('Node:', node.tagName, 'Font size:', fontSize);
                            if (fontSize && fontSize > 0) {
                              setSizeInput(fontSize.toString());
                              console.log('Set size to:', fontSize);
                              break;
                            }
                            node = node.parentElement;
                          }
                        }
                      }
                      setShowSizePicker(!showSizePicker);
                      setShowFontPicker(false);
                      setShowColorPicker(false);
                      setShowWeightPicker(false);
                      setShowHighlightPicker(false);
                    }}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Font Size"
                  >
                    <TextCursor className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                  </button>
                  
                  {showSizePicker && (
                    <div className="absolute top-full left-[-100px] mt-1 bg-white border rounded-lg shadow-lg p-3 z-50 w-48">
                      <div className="text-xs font-semibold text-gray-500 mb-2">FONT SIZE (px)</div>
                      <input
                        type="range"
                        min="8"
                        max="72"
                        value={sizeInput}
                        onChange={(e) => setSizeInput(e.target.value)}
                        className="w-full mb-2"
                      />
                      <input
                        type="number"
                        value={sizeInput}
                        onChange={(e) => setSizeInput(e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded mb-2"
                        min="8"
                        max="200"
                      />
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (editorRef.current) {
                            editorRef.current.focus();
                            document.execCommand('fontSize', false, '7');
                            const selection = window.getSelection();
                            if (selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              const span = range.commonAncestorContainer.parentElement;
                              if (span && span.tagName === 'FONT') {
                                span.removeAttribute('size');
                                span.style.fontSize = sizeInput + 'px';
                              }
                            }
                            // Trigger input event to update preview
                            const event = new Event('input', { bubbles: true });
                            editorRef.current.dispatchEvent(event);
                          }
                          setShowSizePicker(false);
                        }}
                        className="w-full px-3 py-1.5 bg-[#4C1D95] text-white rounded text-sm hover:bg-[#3d1777] transition-colors"
                      >
                        Apply Size
                      </button>
                    </div>
                  )}
                </div>

                {/* Font Weight */}
                <div className="relative flex-1">
                  <button
                    onClick={() => {
                      // Detect current font weight when opening
                      if (!showWeightPicker && editorRef.current) {
                        const selection = window.getSelection();
                        if (selection.rangeCount > 0 && selection.toString().length > 0) {
                          let node = selection.anchorNode;
                          // If text node, get parent element
                          if (node.nodeType === 3) {
                            node = node.parentElement;
                          }
                          // Traverse up to find element with font weight
                          while (node && node !== editorRef.current) {
                            const computedStyle = window.getComputedStyle(node);
                            let fontWeight = computedStyle.fontWeight;
                            if (fontWeight) {
                              // Convert weight names to numbers
                              const weightMap = { 'normal': '400', 'bold': '700', 'lighter': '300', 'bolder': '700' };
                              fontWeight = weightMap[fontWeight] || fontWeight;
                              // Round to nearest 100
                              const numWeight = Math.round(parseInt(fontWeight) / 100) * 100;
                              if (numWeight >= 100 && numWeight <= 900) {
                                setWeightInput(numWeight.toString());
                                break;
                              }
                            }
                            node = node.parentElement;
                          }
                        }
                      }
                      setShowWeightPicker(!showWeightPicker);
                      setShowFontPicker(false);
                      setShowColorPicker(false);
                      setShowSizePicker(false);
                      setShowHighlightPicker(false);
                    }}
                    className="w-full px-2 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center font-bold"
                    title="Font Weight"
                    style={{ color: '#4C1D95' }}
                  >
                    W
                  </button>
                  
                  {showWeightPicker && (
                    <div className="absolute top-full left-[-150px] mt-1 bg-white border rounded-lg shadow-lg p-3 z-50 w-48">
                      <div className="text-xs font-semibold text-gray-500 mb-2">FONT WEIGHT</div>
                      <input
                        type="range"
                        min="100"
                        max="900"
                        step="100"
                        value={weightInput}
                        onChange={(e) => setWeightInput(e.target.value)}
                        className="w-full mb-2"
                      />
                      <input
                        type="number"
                        value={weightInput}
                        onChange={(e) => setWeightInput(e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded mb-2"
                        min="100"
                        max="900"
                        step="100"
                      />
                      <div className="text-xs text-gray-500 mb-2">
                        {weightInput === '100' && 'Thin'}
                        {weightInput === '200' && 'Extra Light'}
                        {weightInput === '300' && 'Light'}
                        {weightInput === '400' && 'Normal'}
                        {weightInput === '500' && 'Medium'}
                        {weightInput === '600' && 'Semi Bold'}
                        {weightInput === '700' && 'Bold'}
                        {weightInput === '800' && 'Extra Bold'}
                        {weightInput === '900' && 'Black'}
                      </div>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (editorRef.current) {
                            editorRef.current.focus();
                            const selection = window.getSelection();
                            if (selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              const span = document.createElement('span');
                              span.style.fontWeight = weightInput;
                              range.surroundContents(span);
                            }
                            // Trigger input event to update preview
                            const event = new Event('input', { bubbles: true });
                            editorRef.current.dispatchEvent(event);
                          }
                          setShowWeightPicker(false);
                        }}
                        className="w-full px-3 py-1.5 bg-[#4C1D95] text-white rounded text-sm hover:bg-[#3d1777] transition-colors"
                      >
                        Apply Weight
                      </button>
                    </div>
                  )}
                </div>

                {/* Highlighter */}
                <div className="relative flex-1">
                  <button
                    onClick={() => {
                      setShowHighlightPicker(!showHighlightPicker);
                      setShowFontPicker(false);
                      setShowColorPicker(false);
                      setShowSizePicker(false);
                      setShowWeightPicker(false);
                    }}
                    className="w-full px-2 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-center"
                    title="Highlight Text"
                    style={{ color: '#4C1D95' }}
                  >
                    <Highlighter className="w-4 h-4 mx-auto" />
                  </button>
                  
                  {showHighlightPicker && (
                    <div className="absolute top-full left-[-100px] mt-1 bg-white border rounded-lg shadow-lg p-3 z-50 w-40">
                      <div className="text-xs font-semibold text-gray-500 mb-2">HIGHLIGHT</div>
                      <div className="grid grid-cols-5 gap-1">
                        {[
                          '#FFEB3B', '#FFC107', '#FF9800', '#FF5722', '#F44336',
                          '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3',
                          '#105652', '#1E8479', '#4CAF50', '#8BC34A', '#CDDC39'
                        ].map(color => (
                          <button
                            key={color}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (editorRef.current) {
                                editorRef.current.focus();
                                const selection = window.getSelection();
                                if (selection.rangeCount > 0 && selection.toString().length > 0) {
                                  const range = selection.getRangeAt(0);
                                  const span = document.createElement('span');
                                  span.style.backgroundColor = color;
                                  span.style.padding = '2px 4px';
                                  span.style.borderRadius = '3px';
                                  try {
                                    range.surroundContents(span);
                                  } catch (error) {
                                    // If surroundContents fails, use insertHTML
                                    const selectedText = selection.toString();
                                    const highlightHtml = `<span style="background-color: ${color}; padding: 2px 4px; border-radius: 3px;">${selectedText}</span>`;
                                    document.execCommand('insertHTML', false, highlightHtml);
                                  }
                                  
                                  // Clear selection
                                  selection.removeAllRanges();
                                  
                                  // Trigger input event to update preview
                                  const event = new Event('input', { bubbles: true });
                                  editorRef.current.dispatchEvent(event);
                                }
                              }
                              setShowHighlightPicker(false);
                            }}
                            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (editorRef.current) {
                            editorRef.current.focus();
                            const selection = window.getSelection();
                            if (selection.rangeCount > 0 && selection.toString().length > 0) {
                              // Remove highlight by removing background color
                              document.execCommand('backColor', false, 'transparent');
                              
                              // Trigger input event to update preview
                              const event = new Event('input', { bubbles: true });
                              editorRef.current.dispatchEvent(event);
                            }
                          }
                          setShowHighlightPicker(false);
                        }}
                        className="w-full px-2 py-1 mt-2 text-xs border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                      >
                        Remove Highlight
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 my-3"></div>

            {/* Embeds Section */}
            <div className="mb-4">
              <div className="flex gap-1">
                {/* Insert Link */}
                <button
                  onClick={handleLinkButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Link"
                >
                  <LinkIcon className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                
                {/* Insert Embed/Code */}
                <button
                  onClick={handleEmbedButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Embed/Code"
                >
                  <FileCode className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                
                {/* Code/Inline Code */}
                <button
                  onClick={handleCodeBlockButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Code Block"
                >
                  <Code className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
                
                {/* Insert Tooltip */}
                <button
                  onClick={handleTooltipButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Add Tooltip"
                >
                  <Info className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-3"></div>

              {/* Content Elements */}
              <div className="grid grid-cols-4 gap-1">
                {/* Insert Quoteblock */}
                <button
                  onClick={handleQuoteButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Quote Block"
                >
                  <Quote className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Button */}
                <button
                  onClick={handleButtonButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Button"
                >
                  <MousePointer className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Divider */}
                <button
                  onClick={handleDividerButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Divider"
                >
                  <Minus className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Table */}
                <button
                  onClick={handleTableButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Table"
                >
                  <Table className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-3"></div>

              {/* Additional Tools Row */}
              <div className="grid grid-cols-4 gap-1 mb-4">
                {/* Insert FAQ/Accordion */}
                <button
                  onClick={handleFaqButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert FAQ/Accordion"
                >
                  <HelpCircle className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Social Links */}
                <button
                  onClick={handleSocialButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Social Links"
                >
                  <Network className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Project Navigation */}
                <button
                  onClick={handleProjectNavButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Project Navigation"
                >
                  <div className="flex items-center justify-center gap-1">
                    <ChevronLeft className="w-3 h-3" style={{ color: '#4C1D95' }} />
                    <ChevronRight className="w-3 h-3" style={{ color: '#4C1D95' }} />
                  </div>
                </button>

                {/* Insert Author */}
                <button
                  onClick={handleAuthorButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Author"
                >
                  <User className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-3"></div>

              {/* Media Tools Row */}
              <div className="grid grid-cols-5 gap-1 mb-4">
                {/* Insert File Download */}
                <button
                  onClick={handleFileDownloadButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert File Download"
                >
                  <FolderDown className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Image */}
                <button
                  onClick={handleInsertImageButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Image"
                >
                  <ImageIcon className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Audio */}
                <button
                  onClick={handleInsertAudioButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Audio"
                >
                  <Music className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Video */}
                <button
                  onClick={handleInsertVideoButtonClick}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Video"
                >
                  <Video className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>

                {/* Insert Photo Grid */}
                <button
                  onClick={() => {/* TODO: Implement handlePhotoGridButtonClick */}}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Insert Photo Grid"
                >
                  <Grid3X3 className="w-4 h-4 mx-auto" style={{ color: '#4C1D95' }} />
                </button>
              </div>
            </div>
          </div>

          {/* Right Section - 80% Editor Canvas */}
          <div 
            className="w-[80%] h-full bg-white rounded-2xl overflow-auto"
            style={{
              boxShadow: `
                0 10px 25px -5px rgba(0, 0, 0, 0.1),
                0 8px 10px -6px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 0 rgba(255, 255, 255, 0.8),
                0 1px 3px 0 rgba(0, 0, 0, 0.08)
              `,
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            <div className="w-full h-full flex flex-col p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Editor Canvas</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 px-4 py-2 bg-[#4C1D95] text-white rounded-lg hover:bg-[#3d1777] transition-colors"
                  >
                    <Save size={18} />
                    <span className="font-medium">
                      {(isProjectMode && projectEditingId && projectEditingId !== 'new') || 
                       (isBlogMode && blogEditingId && blogEditingId !== 'new') 
                        ? 'Save All Changes' 
                        : 'Save Draft'}
                    </span>
                  </button>
                </div>
              </div>
              
              {/* Rich Text Editor */}
              <div className="flex-1">
                <RichTextEditor
                  ref={editorRef}
                  value={formData.fullDescription || ''}
                  onChange={(content) => setFormData(prev => ({ ...prev, fullDescription: content }))}
                  placeholder="Write your full project description here..."
                  hideToolbar={true}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Placeholder for other sections */
        <div className="w-full h-full pt-20 pb-6 px-6 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {selectedSection === 'full-description' ? 'Full Description Editor' : 'Link Management'}
            </h2>
            <p className="text-gray-600">This section will be implemented next</p>
          </div>
        </div>
      )}

      {/* Insert Link Modal */}
      {showLinkModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowLinkModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {linkMode === 'selection' ? 'Add Link to Selected Text' : 'Insert New Link'}
              </h3>
              <p className="text-xs text-gray-600">
                {linkMode === 'selection' 
                  ? 'The link will be applied to your selected text' 
                  : 'Enter both title and URL to insert a new link at cursor position'}
              </p>
            </div>
            
            {/* Show title field only for insert mode */}
            {linkMode === 'insert' && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Title *
                </label>
                <input
                  type="text"
                  value={linkTitle}
                  onChange={(e) => setLinkTitle(e.target.value)}
                  placeholder="e.g., Click here"
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-[#4C1D95] transition-colors"
                  style={{ borderColor: '#e5e7eb' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleInsertLink();
                    }
                  }}
                  autoFocus
                />
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL *
              </label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-[#4C1D95] transition-colors"
                style={{ borderColor: '#e5e7eb' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleInsertLink();
                  }
                }}
                autoFocus={linkMode === 'selection'}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl('');
                  setLinkTitle('');
                }}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                disabled={!linkUrl || (linkMode === 'insert' && !linkTitle)}
                className="px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: '#4C1D95' }}
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Embed Modal */}
      {showEmbedModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => {
            setShowEmbedModal(false);
            setEmbedCode('');
            setEmbedViewMode('code');
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                Insert Embed Code
              </h3>
              <p className="text-xs text-gray-600">
                Paste your embed code (iframe, video, map, etc.)
              </p>
            </div>
            
            {/* View Mode Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setEmbedViewMode('code')}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                style={{
                  backgroundColor: embedViewMode === 'code' ? '#4C1D95' : 'white',
                  color: embedViewMode === 'code' ? 'white' : '#4C1D95',
                  border: `2px solid #4C1D95`,
                }}
              >
                <FileCode className="w-4 h-4" />
                Code View
              </button>
              <button
                type="button"
                onClick={() => setEmbedViewMode('preview')}
                disabled={!embedCode.trim()}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: embedViewMode === 'preview' ? '#4C1D95' : 'white',
                  color: embedViewMode === 'preview' ? 'white' : '#4C1D95',
                  border: `2px solid #4C1D95`,
                }}
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            </div>
            
            {/* Code View */}
            {embedViewMode === 'code' && (
              <div className="space-y-4">
                <textarea
                  value={embedCode}
                  onChange={(e) => setEmbedCode(e.target.value)}
                  placeholder='<iframe src="https://example.com" width="560" height="315"></iframe>'
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-[#4C1D95] transition-colors font-mono text-sm"
                  style={{ borderColor: '#e5e7eb', minHeight: '120px' }}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleInsertEmbed}
                    disabled={!embedCode.trim()}
                    className="px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ backgroundColor: '#4C1D95' }}
                  >
                    Insert Embed
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmbedModal(false);
                      setEmbedCode('');
                      setEmbedViewMode('code');
                    }}
                    className="px-4 py-2 rounded-lg border-2 transition-colors"
                    style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
                  >
                    Cancel
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  Tip: The embed will use dimensions from the code, or default to 560x315px
                </div>
              </div>
            )}
            
            {/* Preview View */}
            {embedViewMode === 'preview' && embedCode && (
              <div className="space-y-4">
                <div 
                  className="border-2 rounded-lg p-4 bg-gray-50"
                  style={{ borderColor: '#4C1D95', minHeight: '200px' }}
                >
                  <iframe
                    srcDoc={embedCode}
                    style={{
                      width: '100%',
                      minHeight: '315px',
                      border: 'none',
                      borderRadius: '0.5rem'
                    }}
                    title="Embed Preview"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleInsertEmbed}
                    className="px-4 py-2 rounded-lg text-white transition-colors"
                    style={{ backgroundColor: '#4C1D95' }}
                  >
                    Insert Embed
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmbedViewMode('code')}
                    className="px-4 py-2 rounded-lg border-2 transition-colors"
                    style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
                  >
                    Edit Code
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmbedModal(false);
                      setEmbedCode('');
                      setEmbedViewMode('code');
                    }}
                    className="px-4 py-2 rounded-lg border-2 transition-colors"
                    style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Codeblock Modal */}
      {showCodeBlockModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowCodeBlockModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ border: '2px solid #4C1D95' }}
          >
            {/* Header */}
            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Code className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingCodeBlock ? 'Edit Code Block' : 'Insert Code Block'}
              </h3>
              <button 
                type="button" 
                onClick={() => setShowCodeBlockModal(false)} 
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex">
              {/* Sidebar - Themes */}
              <div className="w-64 bg-gray-50 border-r overflow-y-auto p-4">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Themes</h4>
                <div className="space-y-2">
                  {CODEBLOCK_THEMES.map(theme => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setCodeBlockTheme(theme.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        codeBlockTheme === theme.id 
                          ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-500' 
                          : 'hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: theme.style.backgroundColor }} 
                      />
                      {theme.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                  <div 
                    className="rounded-lg overflow-hidden shadow-lg transition-all duration-300" 
                    style={{ backgroundColor: CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.style.backgroundColor }}
                  >
                    {/* Mac Header */}
                    <div 
                      className="px-4 py-2 flex items-center justify-between"
                      style={{ backgroundColor: CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.headerStyle.backgroundColor }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                        <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                        <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
                      </div>
                      <div 
                        className="text-xs font-mono opacity-70"
                        style={{ color: CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.headerStyle.color }}
                      >
                        {codeBlockFileName || `script.${codeBlockLanguage === 'javascript' ? 'js' : codeBlockLanguage}`}
                      </div>
                      <button 
                        onClick={handleCopyCode}
                        className="opacity-50 hover:opacity-100 transition-opacity"
                        title="Copy code"
                      >
                        {copied ? (
                          <span 
                            className="text-xs font-medium"
                            style={{ color: CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.headerStyle.color }}
                          >
                            Copied!
                          </span>
                        ) : (
                          <Copy 
                            className="w-4 h-4"
                            style={{ color: CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.headerStyle.color }}
                          />
                        )}
                      </button>
                    </div>
                    
                    {/* Code Content */}
                    <div 
                      className="p-4 overflow-x-auto font-mono text-sm"
                      style={{ 
                        backgroundColor: CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.style.backgroundColor,
                        color: CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.style.color,
                        ...CODEBLOCK_THEMES.find(t => t.id === codeBlockTheme)?.syntax
                      }}
                    >
                      <pre className="m-0">
                        <code 
                          dangerouslySetInnerHTML={{ 
                            __html: highlightCode(codeBlockContent || '// Your code will appear here...', codeBlockLanguage) 
                          }}
                        />
                      </pre>
                    </div>
                  </div>
                </div>

                {/* Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">File Name (Optional)</label>
                    <input
                      type="text"
                      value={codeBlockFileName}
                      onChange={(e) => setCodeBlockFileName(e.target.value)}
                      placeholder="e.g. App.jsx"
                      className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4C1D95] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                    <select
                      value={codeBlockLanguage}
                      onChange={(e) => setCodeBlockLanguage(e.target.value)}
                      className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4C1D95] transition-colors"
                    >
                      <option value="no-color">No Color (Plain Text)</option>
                      <option value="javascript">JavaScript</option>
                      <option value="typescript">TypeScript</option>
                      <option value="python">Python</option>
                      <option value="html">HTML</option>
                      <option value="css">CSS</option>
                      <option value="json">JSON</option>
                      <option value="bash">Bash</option>
                      <option value="sql">SQL</option>
                      <option value="java">Java</option>
                      <option value="csharp">C#</option>
                      <option value="php">PHP</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCodeBlockWrapped(!codeBlockWrapped)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#4C1D95] focus:ring-offset-2 ${
                      codeBlockWrapped ? 'bg-[#4C1D95]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        codeBlockWrapped ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label 
                    className="text-sm font-medium text-gray-700 cursor-pointer select-none"
                    onClick={() => setCodeBlockWrapped(!codeBlockWrapped)}
                  >
                    Wrapped Code (Show first 10 lines initially)
                  </label>
                </div>

                <div className="flex-1 flex flex-col min-h-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <textarea
                    value={codeBlockContent}
                    onChange={(e) => setCodeBlockContent(e.target.value)}
                    placeholder="Paste your code here..."
                    className="flex-1 w-full p-4 border-2 rounded-lg font-mono text-sm focus:outline-none focus:border-[#4C1D95] transition-colors resize-none"
                    spellCheck="false"
                    style={{ minHeight: '250px' }}
                    autoFocus
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCodeBlockModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingCodeBlock && (
                <button
                  type="button"
                  onClick={() => {
                    if (editingCodeBlock) {
                      editingCodeBlock.remove();
                      setEditingCodeBlock(null);
                      setShowCodeBlockModal(false);
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    }
                  }}
                  className="px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <X className="w-4 h-4" />
                  Delete
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveCodeBlock}
                disabled={!codeBlockContent.trim()}
                className="px-6 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Check className="w-4 h-4" />
                {editingCodeBlock ? 'Update Code Block' : 'Insert Code Block'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tooltip Modal */}
      {showTooltipModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowTooltipModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <Info className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingTooltip ? 'Edit Tooltip' : 'Add Tooltip'}
              </h3>
              <p className="text-sm text-gray-600">
                Selected text: <span className="font-medium text-gray-800">"{tooltipSelection}"</span>
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tooltip Information
              </label>
              <textarea
                value={tooltipText}
                onChange={(e) => setTooltipText(e.target.value)}
                placeholder="Enter the information to show when hovering over the selected text..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95] resize-none"
                rows="3"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowTooltipModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingTooltip && (
                <button
                  type="button"
                  onClick={() => {
                    if (editingTooltip) {
                      // Remove the tooltip wrapper and replace with plain text
                      const plainText = editingTooltip.textContent;
                      const textNode = document.createTextNode(plainText);
                      editingTooltip.parentNode.replaceChild(textNode, editingTooltip);
                      
                      setEditingTooltip(null);
                      setShowTooltipModal(false);
                      
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    }
                  }}
                  className="px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <X className="w-4 h-4" />
                  Remove Tooltip
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveTooltip}
                disabled={!tooltipText.trim()}
                className="px-6 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Check className="w-4 h-4" />
                {editingTooltip ? 'Update Tooltip' : 'Add Tooltip'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Modal */}
      {showQuoteModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowQuoteModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <Quote className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingQuote ? 'Edit Quote Block' : 'Insert Quote Block'}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quote Text *
                </label>
                <textarea
                  value={quoteText}
                  onChange={(e) => setQuoteText(e.target.value)}
                  placeholder="Enter the quote text..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95] resize-none"
                  rows="4"
                  style={{ whiteSpace: 'pre-wrap' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author (Optional)
                </label>
                <input
                  type="text"
                  value={quoteAuthor}
                  onChange={(e) => setQuoteAuthor(e.target.value)}
                  placeholder="Quote author or source"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Theme
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {QUOTE_THEMES.map(theme => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setQuoteTheme(theme.id)}
                      className={`p-3 text-left border rounded-lg transition-colors ${
                        quoteTheme === theme.id 
                          ? 'border-[#4C1D95] bg-[#4C1D95]/10' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-sm">{theme.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {theme.id === 'modern' && 'Clean card with light background'}
                        {theme.id === 'classic' && 'Warm background with italic text'}
                        {theme.id === 'vibrant' && 'Gradient background with white text'}
                        {theme.id === 'minimal' && 'Simple left border design'}
                        {theme.id === 'code-like' && 'Dark theme with monospace font'}
                        {theme.id === 'radio' && 'Retro radio style with green glow'}
                        {theme.id === 'vintage' && 'Old-fashioned paper with ornate border'}
                        {theme.id === 'neon' && 'Cyberpunk neon glow effect'}
                        {theme.id === 'scifi' && 'Futuristic sci-fi interface style'}
                        {theme.id === 'paper' && 'Clean paper document style'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingQuote && (
                <button
                  type="button"
                  onClick={() => {
                    if (editingQuote) {
                      editingQuote.remove();
                      setEditingQuote(null);
                      setShowQuoteModal(false);
                      
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    }
                  }}
                  className="px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <X className="w-4 h-4" />
                  Remove Quote
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveQuote}
                disabled={!quoteText.trim()}
                className="px-6 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Check className="w-4 h-4" />
                {editingQuote ? 'Update Quote' : 'Insert Quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Button Modal */}
      {showButtonModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowButtonModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <MousePointer className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingButton ? 'Edit Button' : 'Insert Button'}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Text *
                </label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  placeholder="Enter button text..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button URL *
                </label>
                <input
                  type="url"
                  value={buttonUrl}
                  onChange={(e) => setButtonUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Alignment
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setButtonAlignment('left')}
                    className={`flex-1 px-3 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      buttonAlignment === 'left' 
                        ? 'border-[#4C1D95] bg-[#4C1D95]/10 text-[#4C1D95]' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <AlignLeft className="w-4 h-4" />
                    <span className="text-sm">Left</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setButtonAlignment('center')}
                    className={`flex-1 px-3 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      buttonAlignment === 'center' 
                        ? 'border-[#4C1D95] bg-[#4C1D95]/10 text-[#4C1D95]' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <AlignCenter className="w-4 h-4" />
                    <span className="text-sm">Center</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setButtonAlignment('right')}
                    className={`flex-1 px-3 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      buttonAlignment === 'right' 
                        ? 'border-[#4C1D95] bg-[#4C1D95]/10 text-[#4C1D95]' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <AlignRight className="w-4 h-4" />
                    <span className="text-sm">Right</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Style
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {BUTTON_THEMES.map(theme => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setButtonTheme(theme.id)}
                      className={`p-3 text-left border rounded-lg transition-colors ${
                        buttonTheme === theme.id 
                          ? 'border-[#4C1D95] bg-[#4C1D95]/10' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-sm">{theme.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {theme.id === 'primary' && 'Solid background button'}
                        {theme.id === 'outline' && 'Border only button'}
                        {theme.id === 'gradient' && 'Gradient background'}
                        {theme.id === 'minimal' && 'Simple border style'}
                        {theme.id === 'rounded' && 'Fully rounded button'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowButtonModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingButton && (
                <button
                  type="button"
                  onClick={() => {
                    if (editingButton) {
                      editingButton.remove();
                      setEditingButton(null);
                      setShowButtonModal(false);
                      
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    }
                  }}
                  className="px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <X className="w-4 h-4" />
                  Remove Button
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveButton}
                disabled={!buttonText.trim() || !buttonUrl.trim()}
                className="px-6 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Check className="w-4 h-4" />
                {editingButton ? 'Update Button' : 'Insert Button'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Divider Modal */}
      {showDividerModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowDividerModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <Minus className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingDivider ? 'Edit Divider' : 'Insert Divider'}
              </h3>
              <p className="text-sm text-gray-600">
                Choose a divider style to separate content sections.
              </p>
            </div>
            
            <div className="space-y-3">
              {DIVIDER_THEMES.map(theme => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => setDividerTheme(theme.id)}
                  className={`w-full p-3 text-left border rounded-lg transition-colors ${
                    dividerTheme === theme.id 
                      ? 'border-[#4C1D95] bg-[#4C1D95]/10' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-medium text-sm mb-2">{theme.name}</div>
                  <div className="preview-container" style={{ height: '20px', position: 'relative' }}>
                    {theme.content ? (
                      <div style={{ textAlign: 'center', color: '#105652', fontSize: '14px' }}>
                        {theme.content}
                      </div>
                    ) : (
                      <div style={{
                        ...theme.style,
                        margin: '8px 0',
                        width: '100%'
                      }} />
                    )}
                  </div>
                </button>
              ))}
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowDividerModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingDivider && (
                <button
                  type="button"
                  onClick={() => {
                    if (editingDivider) {
                      editingDivider.remove();
                      setEditingDivider(null);
                      setShowDividerModal(false);
                      
                      // Trigger input event to update preview
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                    }
                  }}
                  className="px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <X className="w-4 h-4" />
                  Remove Divider
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveDivider}
                className="px-6 py-2 rounded-lg text-white transition-colors flex items-center gap-2"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Check className="w-4 h-4" />
                {editingDivider ? 'Update Divider' : 'Insert Divider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowTableModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <Table className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingTable ? 'Edit Table Style' : 'Insert Table'}
              </h3>
            </div>
            
            <div className="space-y-4">
              {!editingTable && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rows
                      </label>
                      <input
                        type="number"
                        min="2"
                        max="10"
                        value={tableRows}
                        onChange={(e) => setTableRows(parseInt(e.target.value) || 3)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Columns
                      </label>
                      <input
                        type="number"
                        min="2"
                        max="6"
                        value={tableCols}
                        onChange={(e) => setTableCols(parseInt(e.target.value) || 3)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Table Style
                </label>
                <div className="flex gap-2 flex-wrap">
                  {TABLE_THEMES.map(theme => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setTableTheme(theme.id)}
                      className={`flex items-center justify-center w-12 h-12 border rounded-lg transition-colors ${
                        tableTheme === theme.id 
                          ? 'border-[#4C1D95] bg-[#4C1D95]/10 text-[#4C1D95]' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      title={theme.name}
                    >
                      <span className="text-xl">{theme.icon}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowTableModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              <button
                type="button"
                onClick={handleOpenTableEditor}
                className="px-6 py-2 rounded-lg text-white transition-colors flex items-center gap-2"
                style={{ backgroundColor: '#4C1D95' }}
              >
                <Table className="w-4 h-4" />
                Open Table Editor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Table Editor */}
      {showTableEditor && (
        <div className="fixed inset-0 bg-white z-[100001] flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowTableEditor(false);
                  setSelectedCells(new Set());
                  setSelectionStart(null);
                  setSelectionEnd(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold text-gray-800">Table Editor</h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Excel-like: Click & drag to select, Ctrl+Click for multi-select, Shift+Click to extend
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Theme Selection */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Theme:</span>
                <div className="flex gap-1">
                  {TABLE_THEMES.map(theme => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setTableTheme(theme.id)}
                      className={`flex items-center justify-center w-10 h-10 border rounded-lg transition-colors ${
                        tableTheme === theme.id 
                          ? 'border-[#4C1D95] bg-[#4C1D95]/10 text-[#4C1D95]' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      title={theme.name}
                    >
                      <span className="text-lg">{theme.icon}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Row/Column Controls */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Rows:</span>
                  <button
                    type="button"
                    onClick={() => updateTableDimensions(Math.max(2, tableRows - 1), tableCols)}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                    disabled={tableRows <= 2}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center text-sm font-medium">{tableRows}</span>
                  <button
                    type="button"
                    onClick={() => updateTableDimensions(Math.min(20, tableRows + 1), tableCols)}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                    disabled={tableRows >= 20}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Columns:</span>
                  <button
                    type="button"
                    onClick={() => updateTableDimensions(tableRows, Math.max(2, tableCols - 1))}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                    disabled={tableCols <= 2}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center text-sm font-medium">{tableCols}</span>
                  <button
                    type="button"
                    onClick={() => updateTableDimensions(tableRows, Math.min(10, tableCols + 1))}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                    disabled={tableCols >= 10}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Selection Info */}
              {selectedCells.size > 0 && (
                <span className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded">
                  {selectedCells.size} cell{selectedCells.size > 1 ? 's' : ''} selected
                </span>
              )}
              
              {/* Save Button */}
              <button
                type="button"
                onClick={handleSaveTable}
                className="px-6 py-2 bg-[#4C1D95] text-white rounded-lg hover:bg-[#3d1777] transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Table
              </button>
            </div>
          </div>
          
          {/* Table Preview and Editor */}
          <div 
            className="flex-1 p-8 overflow-auto bg-gray-50"
            onMouseUp={handleCellMouseUp}
            onPaste={handleTablePaste}
            onKeyDown={handleTableKeyDown}
            tabIndex={0}
          >
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Table Preview</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllCells}
                      className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                    >
                      Select All
                    </button>
                  </div>
                </div>
                
                {/* Live Table Preview */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  {tableData.length > 0 && (
                    <table style={{
                      ...TABLE_THEMES.find(t => t.id === tableTheme)?.tableStyle,
                      margin: 0,
                      userSelect: 'none'
                    }}>
                      <thead>
                        <tr>
                          <th 
                            style={{
                              width: '30px',
                              padding: '4px',
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #e5e7eb',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={selectAllCells}
                            title="Select all"
                          >
                            ⊞
                          </th>
                          {Array.from({ length: tableCols }, (_, colIndex) => (
                            <th
                              key={`col-header-${colIndex}`}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#f3f4f6',
                                border: '1px solid #e5e7eb',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                cursor: 'pointer'
                              }}
                              onClick={() => selectColumn(colIndex)}
                              title={`Select column ${colIndex + 1}`}
                            >
                              {String.fromCharCode(65 + colIndex)}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          <th 
                            style={{
                              width: '30px',
                              padding: '4px',
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #e5e7eb',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => selectRow(0)}
                            title="Select row 1"
                          >
                            1
                          </th>
                          {Array.from({ length: tableCols }, (_, colIndex) => (
                            <th
                              key={colIndex}
                              style={{
                                ...TABLE_THEMES.find(t => t.id === tableTheme)?.thStyle,
                                fontWeight: '700',
                                backgroundColor: isCellSelected(0, colIndex) ? '#dbeafe' : TABLE_THEMES.find(t => t.id === tableTheme)?.thStyle?.backgroundColor,
                                outline: isCellSelected(0, colIndex) ? '2px solid #3b82f6' : 'none',
                                outlineOffset: '-2px'
                              }}
                              onMouseDown={(e) => handleCellMouseDown(0, colIndex, e)}
                              onMouseEnter={(e) => handleCellMouseEnter(0, colIndex, e)}
                            >
                              <input
                                type="text"
                                value={tableData[0]?.[colIndex] || ''}
                                onChange={(e) => updateCellData(0, colIndex, e.target.value)}
                                className="w-full bg-transparent border-none outline-none font-bold"
                                placeholder={`Header ${colIndex + 1}`}
                                style={{ fontWeight: '700', pointerEvents: 'auto' }}
                                onFocus={(e) => e.stopPropagation()}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: tableRows - 1 }, (_, rowIndex) => {
                          const actualRowIndex = rowIndex + 1;
                          const selectedTheme = TABLE_THEMES.find(t => t.id === tableTheme);
                          const isEvenRow = rowIndex % 2 === 1;
                          
                          return (
                            <tr key={actualRowIndex}>
                              <td 
                                style={{
                                  width: '30px',
                                  padding: '4px',
                                  backgroundColor: '#f3f4f6',
                                  border: '1px solid #e5e7eb',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  cursor: 'pointer'
                                }}
                                onClick={() => selectRow(actualRowIndex)}
                                title={`Select row ${actualRowIndex + 1}`}
                              >
                                {actualRowIndex + 1}
                              </td>
                              {Array.from({ length: tableCols }, (_, colIndex) => (
                                <td
                                  key={colIndex}
                                  style={{
                                    ...selectedTheme?.tdStyle,
                                    ...(selectedTheme?.evenRowStyle && isEvenRow ? selectedTheme.evenRowStyle : {}),
                                    backgroundColor: isCellSelected(actualRowIndex, colIndex) 
                                      ? '#dbeafe' 
                                      : (selectedTheme?.evenRowStyle && isEvenRow ? selectedTheme.evenRowStyle.backgroundColor : selectedTheme?.tdStyle?.backgroundColor),
                                    outline: isCellSelected(actualRowIndex, colIndex) ? '2px solid #3b82f6' : 'none',
                                    outlineOffset: '-2px'
                                  }}
                                  onMouseDown={(e) => handleCellMouseDown(actualRowIndex, colIndex, e)}
                                  onMouseEnter={(e) => handleCellMouseEnter(actualRowIndex, colIndex, e)}
                                >
                                  <input
                                    type="text"
                                    value={tableData[actualRowIndex]?.[colIndex] || ''}
                                    onChange={(e) => updateCellData(actualRowIndex, colIndex, e.target.value)}
                                    className="w-full bg-transparent border-none outline-none"
                                    placeholder={`Cell ${actualRowIndex}-${colIndex + 1}`}
                                    style={{ pointerEvents: 'auto' }}
                                    onFocus={(e) => e.stopPropagation()}
                                  />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>💡 Tips:</strong> Copy data from Excel/Google Sheets and paste here (Ctrl+V). 
                    Click row/column headers to select entire row/column. 
                    Click & drag to select multiple cells. 
                    Hold Ctrl/Cmd to select non-adjacent cells.
                    Press Delete/Backspace to clear selected cells.
                    Press Ctrl+A to select all.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Modal */}
      {showFaqModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowFaqModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <HelpCircle className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingFaq ? 'Edit FAQ' : 'Insert FAQ/Accordion'}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question
                </label>
                <input
                  type="text"
                  value={faqQuestion}
                  onChange={(e) => setFaqQuestion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                  placeholder="Enter your question..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Answer
                </label>
                <textarea
                  value={faqAnswer}
                  onChange={(e) => setFaqAnswer(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                  placeholder="Enter the answer..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Theme
                </label>
                <div className="flex gap-2">
                  {FAQ_THEMES.map(theme => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setFaqTheme(theme.id)}
                      className={`px-4 py-2 border rounded-lg transition-colors ${
                        faqTheme === theme.id 
                          ? 'border-[#4C1D95] bg-[#4C1D95]/10 text-[#4C1D95]' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {theme.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowFaqModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingFaq && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this FAQ?')) {
                      editingFaq.remove();
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setShowFaqModal(false);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white transition-colors hover:bg-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveFaq}
                className="px-6 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: '#4C1D95' }}
              >
                {editingFaq ? 'Update FAQ' : 'Insert FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Links Modal */}
      {showSocialModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowSocialModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <Network className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingSocial ? 'Edit Social Links' : 'Insert Social Links'}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heading
                </label>
                <input
                  type="text"
                  value={socialHeading}
                  onChange={(e) => setSocialHeading(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                  placeholder="Connect"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Social Media
                  </label>
                  {loadingSocialMedias && (
                    <span className="text-xs text-gray-500">Loading...</span>
                  )}
                </div>
                
                {availableSocialMedias.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                    {availableSocialMedias.map((socialMedia) => (
                      <div 
                        key={socialMedia.$id} 
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedSocialIds.includes(socialMedia.$id)
                            ? 'border-[#4C1D95] bg-[#4C1D95]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleSocialSelection(socialMedia.$id)}
                      >
                        <div className="w-8 h-8 rounded overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                          {socialMedia.iconUrl ? (
                            <img 
                              src={socialMedia.iconUrl} 
                              alt={socialMedia.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs font-semibold text-gray-600">
                              {socialMedia.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">
                            {socialMedia.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {socialMedia.link}
                          </div>
                        </div>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          selectedSocialIds.includes(socialMedia.$id)
                            ? 'border-[#4C1D95] bg-[#4C1D95]'
                            : 'border-gray-300'
                        }`}>
                          {selectedSocialIds.includes(socialMedia.$id) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg">
                    {loadingSocialMedias ? (
                      'Loading social media...'
                    ) : (
                      <div>
                        <p className="mb-2">No social media configured yet.</p>
                        <p className="text-sm">Go to Settings → Social Media to add your social media links first.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedSocialIds.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Preview:</div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-900">{socialHeading}</span>
                    <div className="flex items-center gap-2">
                      {availableSocialMedias
                        .filter(sm => selectedSocialIds.includes(sm.$id))
                        .map(socialMedia => (
                          <div key={socialMedia.$id} className="w-6 h-6 rounded overflow-hidden bg-gray-200 flex items-center justify-center">
                            {socialMedia.iconUrl ? (
                              <img 
                                src={socialMedia.iconUrl} 
                                alt={socialMedia.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-xs font-semibold text-gray-600">
                                {socialMedia.name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowSocialModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingSocial && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this social media section?')) {
                      editingSocial.remove();
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setShowSocialModal(false);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white transition-colors hover:bg-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveSocial}
                className="px-6 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: '#4C1D95' }}
                disabled={selectedSocialIds.length === 0}
              >
                {editingSocial ? 'Update Links' : 'Insert Links'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Navigation Modal */}
      {showProjectNavModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowProjectNavModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1">
                  <ChevronLeft className="w-5 h-5" style={{ color: '#4C1D95' }} />
                  <ChevronRight className="w-5 h-5" style={{ color: '#4C1D95' }} />
                </div>
                Insert Navigation
              </h3>
              <p className="text-sm text-gray-600">
                This will add Previous and Next navigation buttons for content browsing.
              </p>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowProjectNavModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingProjectNav && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this navigation?')) {
                      editingProjectNav.remove();
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setShowProjectNavModal(false);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white transition-colors hover:bg-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveProjectNav}
                className="px-6 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: '#4C1D95' }}
              >
                Insert Navigation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Author Modal */}
      {showAuthorModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowAuthorModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <User className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingAuthor ? 'Edit Authors' : 'Insert Authors'}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Authors
                  </label>
                  {loadingAuthors && (
                    <span className="text-xs text-gray-500">Loading...</span>
                  )}
                </div>
                
                {availableAuthors.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto">
                    {availableAuthors.map((author) => (
                      <div 
                        key={author.$id} 
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedAuthorIds.includes(author.$id)
                            ? 'border-[#4C1D95] bg-[#4C1D95]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleAuthorSelection(author.$id)}
                      >
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                          {author.imageUrl ? (
                            <img 
                              src={author.imageUrl} 
                              alt={author.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">
                            {author.name}
                          </div>
                          {author.designation && (
                            <div className="text-sm text-gray-500">
                              {author.designation}
                            </div>
                          )}
                          {author.comment && (
                            <div className="text-xs text-gray-400 truncate mt-1">
                              {author.comment}
                            </div>
                          )}
                        </div>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          selectedAuthorIds.includes(author.$id)
                            ? 'border-[#4C1D95] bg-[#4C1D95]'
                            : 'border-gray-300'
                        }`}>
                          {selectedAuthorIds.includes(author.$id) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg">
                    {loadingAuthors ? (
                      'Loading authors...'
                    ) : (
                      <div>
                        <p className="mb-2">No authors configured yet.</p>
                        <p className="text-sm">Go to Settings → Authors to add author profiles first.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedAuthorIds.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Preview:</div>
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="font-semibold text-gray-900">Authors: </span>
                    {availableAuthors
                      .filter(author => selectedAuthorIds.includes(author.$id))
                      .map((author, index) => (
                        <div key={author.$id} className="flex items-center gap-1">
                          {index > 0 && <span className="text-gray-500">, </span>}
                          <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                            {author.imageUrl ? (
                              <img 
                                src={author.imageUrl} 
                                alt={author.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-xs font-semibold text-gray-600">
                                {author.name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <span className="font-medium text-sm">{author.name}</span>
                          {author.designation && (
                            <span className="text-xs text-gray-500">{author.designation}</span>
                          )}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowAuthorModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingAuthor && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this author section?')) {
                      editingAuthor.remove();
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setShowAuthorModal(false);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white transition-colors hover:bg-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveAuthor}
                className="px-6 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: '#4C1D95' }}
                disabled={selectedAuthorIds.length === 0}
              >
                {editingAuthor ? 'Update Authors' : 'Insert Authors'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Download Modal */}
      {showFileDownloadModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowFileDownloadModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: '2px solid #4C1D95'
            }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                <FolderDown className="w-5 h-5" style={{ color: '#4C1D95' }} />
                {editingFileDownload ? 'Edit File Download' : 'Insert File Download'}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Message
                </label>
                <textarea
                  value={fileDownloadMessage}
                  onChange={(e) => setFileDownloadMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                  placeholder="Enter a custom message for the file download..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File Updated on:
                </label>
                <input
                  type="date"
                  value={fileDownloadDate}
                  onChange={(e) => setFileDownloadDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4C1D95]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Files
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#4C1D95] transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                    className="hidden"
                    id="file-upload-input"
                    disabled={uploadingFiles}
                  />
                  <label
                    htmlFor="file-upload-input"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {uploadingFiles ? 'Uploading...' : 'Click to upload files or drag and drop'}
                    </span>
                    <span className="text-xs text-gray-500">
                      Multiple files supported
                    </span>
                  </label>
                </div>

                {fileDownloadFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h4>
                    <div className="space-y-2">
                      {fileDownloadFiles.map((file, index) => (
                        <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#4C1D95] rounded flex items-center justify-center">
                              <span className="text-white text-xs font-bold">
                                {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{file.name}</div>
                              <div className="text-xs text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFileDownloadFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowFileDownloadModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingFileDownload && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this file download?')) {
                      editingFileDownload.remove();
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setShowFileDownloadModal(false);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white transition-colors hover:bg-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveFileDownload}
                className="px-6 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: '#4C1D95' }}
                disabled={!fileDownloadMessage.trim() || !fileDownloadDate || fileDownloadFiles.length === 0 || uploadingFiles}
              >
                {editingFileDownload ? 'Update File Download' : 'Insert File Download'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowImageModal(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4" style={{ color: '#4C1D95' }}>
              {editingImage ? 'Edit Image' : 'Insert Image'}
            </h3>
            
            <div className="space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  {editingImage ? 'Change Image (optional)' : 'Select Image'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileSelect}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Image Preview */}
              {imagePreviewUrl && (
                <div className="border border-gray-300 rounded-lg p-4">
                  <img 
                    src={imagePreviewUrl} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Alt Text */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  Alt Text (for accessibility)
                </label>
                <input
                  type="text"
                  value={imageAltText}
                  onChange={(e) => setImageAltText(e.target.value)}
                  placeholder="Describe the image..."
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Alignment */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  Alignment
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'left', label: 'Left', icon: AlignLeft },
                    { value: 'center', label: 'Center', icon: AlignCenter },
                    { value: 'right', label: 'Right', icon: AlignRight }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setImageAlignment(value)}
                      className={`flex-1 p-2 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                        imageAlignment === value
                          ? 'border-[#4C1D95] bg-[#4C1D95] text-white'
                          : 'border-gray-300 hover:border-[#4C1D95]'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowImageModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingImage && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this image?')) {
                      const alignmentWrapper = editingImage.closest('.image-alignment-wrapper');
                      if (alignmentWrapper) {
                        alignmentWrapper.remove();
                      } else {
                        editingImage.remove();
                      }
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setEditingImage(null);
                      setShowImageModal(false);
                      showToast('Image removed successfully!', 'success');
                    }
                  }}
                  className="px-4 py-2 rounded-lg transition-colors text-white"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveImage}
                disabled={uploadingImage || (!selectedImageFile && !editingImage)}
                className="px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#4C1D95' }}
              >
                {uploadingImage ? 'Uploading...' : editingImage ? 'Update' : 'Insert'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Modal */}
      {showAudioModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowAudioModal(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4" style={{ color: '#4C1D95' }}>
              {editingAudio ? 'Edit Audio' : 'Insert Audio'}
            </h3>
            
            <div className="space-y-4">
              {/* Audio Upload */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  {editingAudio ? 'Change Audio (optional)' : 'Select Audio'}
                </label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioFileSelect}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Audio Preview */}
              {audioPreviewUrl && (
                <div className="border border-gray-300 rounded-lg p-4">
                  <audio 
                    src={audioPreviewUrl} 
                    controls 
                    className="w-full"
                  />
                </div>
              )}

              {/* Audio Title */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  Audio Title
                </label>
                <input
                  type="text"
                  value={audioTitle}
                  onChange={(e) => setAudioTitle(e.target.value)}
                  placeholder="Enter audio title..."
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Alignment */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  Alignment
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'left', label: 'Left', icon: AlignLeft },
                    { value: 'center', label: 'Center', icon: AlignCenter },
                    { value: 'right', label: 'Right', icon: AlignRight }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setAudioAlignment(value)}
                      className={`flex-1 p-2 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                        audioAlignment === value
                          ? 'border-[#4C1D95] bg-[#4C1D95] text-white'
                          : 'border-gray-300 hover:border-[#4C1D95]'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowAudioModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingAudio && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this audio?')) {
                      const alignmentWrapper = editingAudio.closest('.audio-alignment-wrapper');
                      if (alignmentWrapper) {
                        alignmentWrapper.remove();
                      } else {
                        editingAudio.remove();
                      }
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setEditingAudio(null);
                      setShowAudioModal(false);
                      showToast('Audio removed successfully!', 'success');
                    }
                  }}
                  className="px-4 py-2 rounded-lg transition-colors text-white"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveAudio}
                disabled={uploadingAudio || (!selectedAudioFile && !editingAudio)}
                className="px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#4C1D95' }}
              >
                {uploadingAudio ? 'Uploading...' : editingAudio ? 'Update' : 'Insert'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4" style={{ color: '#4C1D95' }}>
              {editingVideo ? 'Edit Video' : 'Insert Video'}
            </h3>
            
            <div className="space-y-4">
              {/* Video Upload */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  {editingVideo ? 'Change Video (optional)' : 'Select Video'}
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoFileSelect}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Video Preview */}
              {videoPreviewUrl && (
                <div className="border border-gray-300 rounded-lg p-4">
                  <video 
                    src={videoPreviewUrl} 
                    controls 
                    className="w-full rounded-lg"
                    style={{ maxHeight: '200px' }}
                  />
                </div>
              )}

              {/* Video Title */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  Video Title
                </label>
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Enter video title..."
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Alignment */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#4C1D95' }}>
                  Alignment
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'left', label: 'Left', icon: AlignLeft },
                    { value: 'center', label: 'Center', icon: AlignCenter },
                    { value: 'right', label: 'Right', icon: AlignRight }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setVideoAlignment(value)}
                      className={`flex-1 p-2 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                        videoAlignment === value
                          ? 'border-[#4C1D95] bg-[#4C1D95] text-white'
                          : 'border-gray-300 hover:border-[#4C1D95]'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-2 rounded-lg border-2 transition-colors"
                style={{ borderColor: '#4C1D95', color: '#4C1D95' }}
              >
                Cancel
              </button>
              
              {editingVideo && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to remove this video?')) {
                      const alignmentWrapper = editingVideo.closest('.video-alignment-wrapper');
                      if (alignmentWrapper) {
                        alignmentWrapper.remove();
                      } else {
                        editingVideo.remove();
                      }
                      const event = new Event('input', { bubbles: true });
                      editorRef.current?.dispatchEvent(event);
                      setEditingVideo(null);
                      setShowVideoModal(false);
                      showToast('Video removed successfully!', 'success');
                    }
                  }}
                  className="px-4 py-2 rounded-lg transition-colors text-white"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Remove
                </button>
              )}
              
              <button
                type="button"
                onClick={handleSaveVideo}
                disabled={uploadingVideo || (!selectedVideoFile && !editingVideo)}
                className="px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#4C1D95' }}
              >
                {uploadingVideo ? 'Uploading...' : editingVideo ? 'Update' : 'Insert'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Embed Resize Handles */}
      {selectedEmbed && selectedSection === 'full-description' && (
        <div
          className="fixed pointer-events-none"
          style={{
            top: selectedEmbed.getBoundingClientRect().top + window.scrollY + 'px',
            left: selectedEmbed.getBoundingClientRect().left + window.scrollX + 'px',
            width: selectedEmbed.offsetWidth + 'px',
            height: selectedEmbed.offsetHeight + 'px',
            border: '2px solid #4C1D95',
            boxShadow: '0 0 0 1px white',
            zIndex: 1000,
          }}
        >
          {/* Corner Handles */}
          {['nw', 'ne', 'sw', 'se'].map((handle) => (
            <div
              key={handle}
              className="resize-handle absolute pointer-events-auto cursor-pointer"
              onMouseDown={(e) => startEmbedResize(e, handle)}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#4C1D95',
                border: '2px solid white',
                borderRadius: '50%',
                ...(handle.includes('n') ? { top: '-6px' } : { bottom: '-6px' }),
                ...(handle.includes('w') ? { left: '-6px' } : { right: '-6px' }),
                cursor: `${handle}-resize`,
              }}
            />
          ))}
          
          {/* Edge Handles */}
          {['n', 'e', 's', 'w'].map((handle) => (
            <div
              key={handle}
              className="resize-handle absolute pointer-events-auto cursor-pointer"
              onMouseDown={(e) => startEmbedResize(e, handle)}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#4C1D95',
                border: '2px solid white',
                borderRadius: '50%',
                ...(handle === 'n' && { top: '-6px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 's' && { bottom: '-6px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 'e' && { right: '-6px', top: '50%', transform: 'translateY(-50%)' }),
                ...(handle === 'w' && { left: '-6px', top: '50%', transform: 'translateY(-50%)' }),
                cursor: `${handle}-resize`,
              }}
            />
          ))}
          
          {/* Action Buttons */}
          <div className="absolute pointer-events-auto flex gap-2" style={{ top: '-45px', right: '-10px' }}>
            {/* Edit Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const embedCode = selectedEmbed.getAttribute('data-embed-code');
                if (embedCode) {
                  setEmbedCode(embedCode);
                  setEmbedViewMode('code');
                  setShowEmbedModal(true);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Edit Embed Code"
            >
              <FileCode className="w-4 h-4" />
            </button>
            
            {/* Delete Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectedEmbed.remove();
                setSelectedEmbed(null);
                // Trigger input event to update preview
                const event = new Event('input', { bubbles: true });
                editorRef.current?.dispatchEvent(event);
              }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Delete Embed (or press Delete/Backspace)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Image Resize Handles */}
      {selectedImage && selectedSection === 'full-description' && (
        <div
          className="fixed pointer-events-none"
          style={{
            top: selectedImage.getBoundingClientRect().top + window.scrollY + 'px',
            left: selectedImage.getBoundingClientRect().left + window.scrollX + 'px',
            width: selectedImage.offsetWidth + 'px',
            height: selectedImage.offsetHeight + 'px',
            border: '2px solid #4C1D95',
            boxShadow: '0 0 0 1px white',
            zIndex: 1000,
          }}
        >
          {/* Corner Handles */}
          {['nw', 'ne', 'sw', 'se'].map((handle) => (
            <div
              key={handle}
              className="resize-handle absolute pointer-events-auto cursor-pointer"
              onMouseDown={(e) => startImageResize(e, handle)}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#4C1D95',
                border: '2px solid white',
                borderRadius: '50%',
                ...(handle.includes('n') ? { top: '-6px' } : { bottom: '-6px' }),
                ...(handle.includes('w') ? { left: '-6px' } : { right: '-6px' }),
                cursor: `${handle}-resize`,
              }}
            />
          ))}
          
          {/* Edge Handles */}
          {['n', 'e', 's', 'w'].map((handle) => (
            <div
              key={handle}
              className="resize-handle absolute pointer-events-auto cursor-pointer"
              onMouseDown={(e) => startImageResize(e, handle)}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#4C1D95',
                border: '2px solid white',
                borderRadius: '50%',
                ...(handle === 'n' && { top: '-6px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 's' && { bottom: '-6px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 'e' && { right: '-6px', top: '50%', transform: 'translateY(-50%)' }),
                ...(handle === 'w' && { left: '-6px', top: '50%', transform: 'translateY(-50%)' }),
                cursor: `${handle}-resize`,
              }}
            />
          ))}
          
          {/* Action Buttons */}
          <div className="absolute pointer-events-auto flex gap-2" style={{ top: '-45px', right: '-10px' }}>
            {/* Alignment Buttons */}
            <div className="flex gap-1 bg-white rounded-lg shadow-lg p-1">
              {[
                { value: 'left', icon: AlignLeft, title: 'Align Left' },
                { value: 'center', icon: AlignCenter, title: 'Align Center' },
                { value: 'right', icon: AlignRight, title: 'Align Right' }
              ].map(({ value, icon: Icon, title }) => (
                <button
                  key={value}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const alignmentWrapper = selectedImage.closest('.image-alignment-wrapper');
                    if (alignmentWrapper) {
                      alignmentWrapper.style.textAlign = value;
                    }
                    selectedImage.setAttribute('data-image-alignment', value);
                    // Trigger input event to update preview
                    const event = new Event('input', { bubbles: true });
                    editorRef.current?.dispatchEvent(event);
                  }}
                  className={`p-1.5 rounded transition-colors ${
                    selectedImage.getAttribute('data-image-alignment') === value
                      ? 'bg-[#4C1D95] text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  title={title}
                >
                  <Icon className="w-3 h-3" />
                </button>
              ))}
            </div>
            
            {/* Edit Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditImage(selectedImage);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Edit Image"
            >
              <ImageIcon className="w-4 h-4" />
            </button>
            
            {/* Delete Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const alignmentWrapper = selectedImage.closest('.image-alignment-wrapper');
                if (alignmentWrapper) {
                  alignmentWrapper.remove();
                } else {
                  selectedImage.remove();
                }
                setSelectedImage(null);
                // Trigger input event to update preview
                const event = new Event('input', { bubbles: true });
                editorRef.current?.dispatchEvent(event);
              }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Delete Image (or press Delete/Backspace)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Audio Selection Handles */}
      {selectedAudio && selectedSection === 'full-description' && (
        <div
          className="fixed pointer-events-none"
          style={{
            top: selectedAudio.getBoundingClientRect().top + window.scrollY + 'px',
            left: selectedAudio.getBoundingClientRect().left + window.scrollX + 'px',
            width: selectedAudio.offsetWidth + 'px',
            height: selectedAudio.offsetHeight + 'px',
            border: '2px solid #4C1D95',
            boxShadow: '0 0 0 1px white',
            zIndex: 1000,
          }}
        >
          {/* Action Buttons */}
          <div className="absolute pointer-events-auto flex gap-2" style={{ top: '-45px', right: '-10px' }}>
            {/* Alignment Buttons */}
            <div className="flex gap-1 bg-white rounded-lg shadow-lg p-1">
              {[
                { value: 'left', icon: AlignLeft, title: 'Align Left' },
                { value: 'center', icon: AlignCenter, title: 'Align Center' },
                { value: 'right', icon: AlignRight, title: 'Align Right' }
              ].map(({ value, icon: Icon, title }) => (
                <button
                  key={value}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const alignmentWrapper = selectedAudio.closest('.audio-alignment-wrapper');
                    if (alignmentWrapper) {
                      alignmentWrapper.style.textAlign = value;
                    }
                    selectedAudio.setAttribute('data-audio-alignment', value);
                    // Trigger input event to update preview
                    const event = new Event('input', { bubbles: true });
                    editorRef.current?.dispatchEvent(event);
                  }}
                  className={`p-1.5 rounded transition-colors ${
                    selectedAudio.getAttribute('data-audio-alignment') === value
                      ? 'bg-[#4C1D95] text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  title={title}
                >
                  <Icon className="w-3 h-3" />
                </button>
              ))}
            </div>
            
            {/* Edit Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditAudio(selectedAudio);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Edit Audio"
            >
              <Music className="w-4 h-4" />
            </button>
            
            {/* Delete Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const alignmentWrapper = selectedAudio.closest('.audio-alignment-wrapper');
                if (alignmentWrapper) {
                  alignmentWrapper.remove();
                } else {
                  selectedAudio.remove();
                }
                setSelectedAudio(null);
                // Trigger input event to update preview
                const event = new Event('input', { bubbles: true });
                editorRef.current?.dispatchEvent(event);
              }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Delete Audio (or press Delete/Backspace)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Video Resize Handles */}
      {selectedVideo && selectedSection === 'full-description' && (
        <div
          className="fixed pointer-events-none"
          style={{
            top: selectedVideo.getBoundingClientRect().top + window.scrollY + 'px',
            left: selectedVideo.getBoundingClientRect().left + window.scrollX + 'px',
            width: selectedVideo.offsetWidth + 'px',
            height: selectedVideo.offsetHeight + 'px',
            border: '2px solid #4C1D95',
            boxShadow: '0 0 0 1px white',
            zIndex: 1000,
          }}
        >
          {/* Corner Handles */}
          {['nw', 'ne', 'sw', 'se'].map((handle) => (
            <div
              key={handle}
              className="resize-handle absolute pointer-events-auto cursor-pointer"
              onMouseDown={(e) => startVideoResize(e, handle)}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#4C1D95',
                border: '2px solid white',
                borderRadius: '50%',
                ...(handle.includes('n') ? { top: '-6px' } : { bottom: '-6px' }),
                ...(handle.includes('w') ? { left: '-6px' } : { right: '-6px' }),
                cursor: `${handle}-resize`,
              }}
            />
          ))}
          
          {/* Edge Handles */}
          {['n', 'e', 's', 'w'].map((handle) => (
            <div
              key={handle}
              className="resize-handle absolute pointer-events-auto cursor-pointer"
              onMouseDown={(e) => startVideoResize(e, handle)}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#4C1D95',
                border: '2px solid white',
                borderRadius: '50%',
                ...(handle === 'n' && { top: '-6px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 's' && { bottom: '-6px', left: '50%', transform: 'translateX(-50%)' }),
                ...(handle === 'e' && { right: '-6px', top: '50%', transform: 'translateY(-50%)' }),
                ...(handle === 'w' && { left: '-6px', top: '50%', transform: 'translateY(-50%)' }),
                cursor: `${handle}-resize`,
              }}
            />
          ))}
          
          {/* Action Buttons */}
          <div className="absolute pointer-events-auto flex gap-2" style={{ top: '-45px', right: '-10px' }}>
            {/* Alignment Buttons */}
            <div className="flex gap-1 bg-white rounded-lg shadow-lg p-1">
              {[
                { value: 'left', icon: AlignLeft, title: 'Align Left' },
                { value: 'center', icon: AlignCenter, title: 'Align Center' },
                { value: 'right', icon: AlignRight, title: 'Align Right' }
              ].map(({ value, icon: Icon, title }) => (
                <button
                  key={value}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const alignmentWrapper = selectedVideo.closest('.video-alignment-wrapper');
                    if (alignmentWrapper) {
                      alignmentWrapper.style.textAlign = value;
                    }
                    selectedVideo.setAttribute('data-video-alignment', value);
                    // Trigger input event to update preview
                    const event = new Event('input', { bubbles: true });
                    editorRef.current?.dispatchEvent(event);
                  }}
                  className={`p-1.5 rounded transition-colors ${
                    selectedVideo.getAttribute('data-video-alignment') === value
                      ? 'bg-[#4C1D95] text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  title={title}
                >
                  <Icon className="w-3 h-3" />
                </button>
              ))}
            </div>
            
            {/* Edit Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditVideo(selectedVideo);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Edit Video"
            >
              <Video className="w-4 h-4" />
            </button>
            
            {/* Delete Button */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const alignmentWrapper = selectedVideo.closest('.video-alignment-wrapper');
                if (alignmentWrapper) {
                  alignmentWrapper.remove();
                } else {
                  selectedVideo.remove();
                }
                setSelectedVideo(null);
                // Trigger input event to update preview
                const event = new Event('input', { bubbles: true });
                editorRef.current?.dispatchEvent(event);
              }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors"
              title="Delete Video (or press Delete/Backspace)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

